
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Routes, Route, Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  BarChart3,
  FileText,
  Receipt,
  Handshake,
  Filter,
  Download,
  MoreVertical,
  Calendar,
  ChevronDown,
  ChevronRight,
  PieChart,
  Bolt,
  Send,
  Trash2,
  FileEdit,
  X,
  PlusCircle,
  Upload,
  Eye,
  AlertTriangle,
  CheckCircle2,
  Home,
  ChevronUp,
  Search,
  ArrowUpRight,
  Clock,
  List,
  Kanban as KanbanIcon,
  Table as TableIcon,
  SlidersHorizontal,
  ArrowUpDown,
  Banknote,
  RotateCcw,
  Layout,
  Calculator,
  Users,
  Copy,
  Files,
  XCircle,
  Zap,
  RefreshCw,
  DollarSign,
  Archive,
  Split,
  BadgeCheck,
  MailX,
  Building2,
  FlaskConical,
  Layers,
  BatteryMedium,
  Briefcase,
  Sun,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { api } from '../../src/services/api';
import { formatDate } from '../../src/lib/utils';
import { supabase } from '../../src/lib/supabase';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { fetchGestaoUcKeys, isGestaoFatura } from '../../src/lib/sicoobAccount';
import { buscarTudoPorChave } from '../../src/lib/paginacaoKeyset';
import { buscarDonosAnteriores, nomesDaUcNaFatura } from '../../src/lib/trocaDonoUc';
import type { DonoAnteriorSnapshot } from '../../src/lib/trocaDonoUc';
import { primeiraEmissao, emissoesDaFatura } from '../../src/lib/timelineCobranca';
import { derivarStatusBoleto, valorCobradoBoleto, valorDevido } from '../../src/lib/statusBoleto';
import { faltaPdfUnificado } from '../../src/lib/pdfUnificadoGran';
import {
  fraseNegociacao, rotuloVinculo, vinculosDeAcordo, type AcordoDoVinculo, type ParcelaDoVinculo,
} from '../../src/lib/vinculoAcordo';
import { divisaoBoletoParceira } from '../../src/lib/divisaoBoletoParceira';
import { faturaEntraNaDuplicata, marcarDuplicatas } from '../../src/lib/duplicataFatura';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { revisarMesesSalvos } from '../../src/lib/filtroMesSalvo';
import { estiloDaCor, passaNoFiltroDeEtiqueta, FILTRO_QUALQUER_ETIQUETA } from '../../src/lib/etiquetasUc';
import { mesDoDia, mesesPorPlanilha, rotuloRateioDaJanela } from '../../src/lib/planilhaRateioVigente';
import { useEtiquetasUc, podeUsarEtiquetas } from './lib/useEtiquetasUc';
import { EtiquetasDaUc } from './components/EtiquetaUcPill';
import EtiquetasUcModal from './components/EtiquetasUcModal';
import FiltroFaturasModal from './components/FiltroFaturasModal';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import FaturaModal from './FaturaModal';
import FaturaFAB from './FaturaFAB';
import BoletoFAB from './BoletoFAB';
import BoletoModal from './BoletoModal';
import UnidadesList from './UnidadesList';
import PendentesCalculoModal from './PendentesCalculoModal';
import { useFaturasRealtime } from './lib/useFaturasRealtime';
import {
  COLUNA_DOS_MALOTES, SEM_MALOTES, colunaDoQuadro, malotesDaLista, montarMalotesEntregues,
  type MaloteNoQuadro, type MalotesEntregues,
} from './lib/colunaDoQuadro';
import CardMaloteSecretaria from './components/CardMaloteSecretaria';
import { aplicarPatchFaturas, emBlocos, idsDasFaturasDaUc, type AlvoAtualizacao } from './lib/realtimeFaturas';
import FaturaValidationModal from './FaturaValidationModal';
import FaturaCalculationModal from './FaturaCalculationModal';
import BoletoEmissionModal from './BoletoEmissionModal';
import PlanilhaGranModal from './PlanilhaGran/PlanilhaGranModal';
import EscolhaVisaoGranModal from './PlanilhaGran/EscolhaVisaoGranModal';
import FilaEmissaoGranBar from './PlanilhaGran/FilaEmissaoGranBar';
import * as filaGranRegra from './PlanilhaGran/filaEmissaoGran';
import type { FilaGran, ItemFilaGran } from './PlanilhaGran/filaEmissaoGran';
import BoletoEmitidoModal from './BoletoEmitidoModal';
import CobrancaEnviadaModal from './CobrancaEnviadaModal';
import ExportModal, { ExportColumn } from '../../src/components/ExportModal';
import { ExportZipModal } from '../../src/components/ExportZipModal';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import ConfirmationModal from '../../src/components/ConfirmationModal';
import DuplicateFaturaModal from './DuplicateFaturaModal';
import FolhaPagamentoView from './FolhaPagamento/FolhaPagamentoView';
import FinanceiroKPIDashboard from './FinanceiroKPIDashboard';
import ParcelamentosView from './ParcelamentosView';
import CriarParcelamentoModal from './CriarParcelamentoModal';
import SincronizarStatusModal from './SincronizarStatusModal';
import QuitarFaturasModal from './QuitarFaturasModal';
import LembretesCobrancaView from './LembretesCobrancaView';
import EsteiraCobrancaView from './EsteiraCobranca/EsteiraCobrancaView';
import SincronizacaoStatusView from './SincronizacaoStatusView';
import CancelamentosView from './CancelamentosView';
import RoleGuard from '../../src/components/RoleGuard';
import AuditoriaPagamentosView from './AuditoriaPagamentosView';
import PainelAdesaoBoletoUnicoView from './PainelAdesaoBoletoUnicoView';
import CarteiraFaturamentoView, { ehDoFaturamento } from './CarteiraFaturamentoView';
import { normalizarDocumento } from '../../src/lib/faturamento/carteiraFaturamento';
import { ucCodigoKeys, ucCodigosDaFatura } from '../../src/lib/ucCodigo';
import { useIndiceUsinas } from '../../src/hooks/useIndiceUsinas';
import { usinaDaFatura, totaisPorUsina, ROTULO_ORIGEM_USINA, NOME_SEM_USINA } from '../../src/lib/financeiro/usinaDaFatura';
import { boletoCobrouEquatorial } from '../../src/lib/repasseEquatorial';
import RepasseEquatorialManualView from './RepasseEquatorialManualView';
import RotaCobrancaView from './RotaCobrancaView';
import PainelExecutivo from './PainelExecutivo';
import SecaoFaturamento from '../Dashboard/DashboardFaturamento';
import PrevisaoRecebiveisView from './PrevisaoRecebiveisView';
import ConferenciaFaturamentoView from './ConferenciaFaturamentoView';
import RecebimentoFaturasView from './RecebimentoFaturasView';
import AgrupamentosFaturamentoView from './AgrupamentosFaturamentoView';

// Converte um valor de data do PostgREST em Date para exportação Excel SEM
// deslocamento de fuso. Colunas do tipo `date` chegam como 'YYYY-MM-DD'; se
// passadas por `new Date(str)` são lidas como meia-noite UTC e, no fuso do Brasil
// (UTC-3), o Excel exibe o dia anterior. Montamos ao MEIO-DIA local: a conversão
// Date→serial do SheetJS enviesa a meia-noite para o dia anterior, mas o meio-dia
// cai sempre no dia correto em qualquer fuso. Valores com hora/fuso (timestamptz)
// seguem via `new Date` (já carregam o instante correto).
export const parseLocalDate = (v: any): Date | null => {
  if (!v) return null;
  const s = String(v).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  return new Date(s);
};

// Implementação em src/lib/mesReferencia.ts. Reexportado aqui porque meia dúzia
// de telas já importa `normalizeMesReferencia` deste módulo — e importado no
// escopo local porque este arquivo também o usa (`export ... from` não traz o
// símbolo para cá).
import { normalizeMesReferencia } from '../../src/lib/mesReferencia';
export { normalizeMesReferencia };

export const getNextMonthReferencia = (m: string | null | undefined): string => {
  const normalized = normalizeMesReferencia(m);
  if (!normalized) return '';
  const [mes, ano] = normalized.split('/');
  const mapMes = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
  const mesIndex = mapMes.indexOf(mes);
  if (mesIndex === -1) return normalized;

  let nextMesIndex = (mesIndex + 1) % 12;
  let nextAno = parseInt(ano, 10);
  if (nextMesIndex === 0) {
    nextAno += 1;
  }
  return `${mapMes[nextMesIndex]}/${nextAno}`;
};

// Filtro de mês do quadro de faturas: o mesmo rótulo "SET/2026" pode vir do mês
// de referência da fatura, do vencimento do boleto ou da data de emissão do boleto.
export type TipoMesFiltro = 'referencia' | 'vencimento' | 'emissao';

export const TIPOS_MES_FILTRO: { key: TipoMesFiltro; label: string; curto: string }[] = [
  { key: 'referencia', label: 'Referência', curto: 'Mês Ref' },
  { key: 'vencimento', label: 'Vencimento', curto: 'Mês Venc' },
  { key: 'emissao', label: 'Emissão', curto: 'Mês Emis' },
];

// Painéis suspensos da barra de faturas. No desktop são dropdowns ancorados no
// botão; no celular viram "bottom sheet" de largura total — ancorados à direita
// do botão eles vazavam pela esquerda da tela. O z-index passa o botão da Vick
// (z-[152]) e o "Nova ação", que cobriam o "Aplicar Filtro".
const SHEET_BACKDROP = 'fixed inset-0 z-[165] bg-black/40 md:bg-transparent md:z-40';
const SHEET_PANEL = 'fixed inset-x-0 bottom-0 z-[170] rounded-t-2xl md:absolute md:inset-x-auto md:bottom-auto md:top-full md:right-0 md:mt-2 md:rounded-xl md:z-50';

// Colunas date ('YYYY-MM-DD') usam o mês literal; timestamptz usa o mês no fuso
// local — new Date() em UTC jogaria emissões da noite do dia 30/31 para o mês seguinte.
const mesAnoDeData = (value: any): string => {
  if (!value) return '';
  const s = String(value).trim();
  const mapMes = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
  const literal = s.match(/^(\d{4})-(\d{2})-\d{2}$/);
  if (literal) return `${mapMes[Number(literal[2]) - 1] || ''}/${literal[1]}`;
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return `${mapMes[d.getMonth()]}/${d.getFullYear()}`;
};

// Sem a data correspondente (ex.: boleto ainda não emitido) a fatura não tem mês
// nesse critério e fica fora de qualquer seleção.
export const mesDaFaturaPorTipo = (fatura: any, tipo: TipoMesFiltro): string => {
  if (tipo === 'vencimento') return mesAnoDeData(fatura?.vencimento_boleto);
  if (tipo === 'emissao') return mesAnoDeData(fatura?.emitido_em);
  return fatura?.mes_referencia || '';
};

// Seleção de meses que vem no link da lista: `?meses=todos` (nenhum mês) ou
// `?mes=NOV/2026` (só esse mês de referência). Sem nenhum dos dois, null.
const mesesDoLink = (params: URLSearchParams): string[] | null => {
  if (params.get('meses') === 'todos') return [];
  const mes = normalizeMesReferencia(params.get('mes'));
  return mes ? [mes] : null;
};

export const removeAccents = (str: string | null | undefined): string => {
  if (!str) return '';
  return String(str)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
};

export const cleanNumericString = (val: string) => {
  const cleaned = val.replace(/[.\-\/]/g, '').replace(/\s/g, '');
  const withoutLeadingZeros = cleaned.replace(/^0+/, '');
  return withoutLeadingZeros || cleaned;
};

export const normalizeCpfCnpj = (v: string) => v.replace(/[.\-\/]/g, '').replace(/\s/g, '');


const FinanceiroLayout: React.FC = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  return (
    <div className="w-full max-w-[1600px] mx-auto">
      <Routes>
        <Route index element={<FinanceiroDashboard />} />
        <Route path="ucs" element={<UnidadesList />} />
        <Route path="faturas" element={<FaturasList />} />
        <Route path="parcelamentos" element={<ParcelamentosView />} />
        <Route path="boletos" element={<BoletosList />} />
        <Route path="folha-pagamento" element={<FolhaPagamentoView />} />
        <Route path="lembretes-cobranca" element={<LembretesCobrancaView />} />
        {/* Esteira de cobrança e documentos — expõe cliente, valor e documento
            jurídico, então segue o mesmo bloqueio das outras telas de cobrança. */}
        <Route path="esteira" element={
          <RoleGuard blocked={['vendedor', 'parceiro', 'gerente_parcerias', 'vendedor_parceiro', 'motoboy', 'empresa_parceira_admin', 'empresa_parceira_operador', 'consumidor', 'geradoras_externo']}>
            <EsteiraCobrancaView />
          </RoleGuard>
        } />
        {/* Só adm/gerente/analista, por design (spec: "Permissões") — inclui geradoras_externo
            no bloqueio, diferente do array copiado de Cancelamentos, porque a tela expõe
            motivo/valor/cliente de faturas via RLS de leitura ampla (authenticated_select). */}
        <Route path="sincronizacao-status" element={
          <RoleGuard blocked={['vendedor', 'parceiro', 'gerente_parcerias', 'vendedor_parceiro', 'motoboy', 'empresa_parceira_admin', 'empresa_parceira_operador', 'consumidor', 'geradoras_externo']}>
            <SincronizacaoStatusView />
          </RoleGuard>
        } />
        <Route path="cancelamentos" element={
          <RoleGuard blocked={['vendedor', 'parceiro', 'gerente_parcerias', 'vendedor_parceiro', 'motoboy', 'empresa_parceira_admin', 'empresa_parceira_operador', 'consumidor']}>
            <CancelamentosView />
          </RoleGuard>
        } />
        {/* Tela de campo da rota de cobranca — mesma restricao das outras telas de
            cobranca, e o link dela chega por WhatsApp para abrir no celular. */}
        <Route path="rota-cobranca" element={
          <RoleGuard blocked={['vendedor', 'parceiro', 'gerente_parcerias', 'vendedor_parceiro', 'motoboy', 'empresa_parceira_admin', 'empresa_parceira_operador', 'consumidor', 'geradoras_externo']}>
            <RotaCobrancaView />
          </RoleGuard>
        } />
        {/* Painel de adesão ao boleto único (#5579) — fila de trabalho da operação.
            Expõe cliente, UC e status de cobrança, então segue o mesmo bloqueio das outras
            telas de cobrança: adm, gerente e analista entram, o resto fica fora. */}
        <Route path="adesao-boleto-unico" element={
          <RoleGuard blocked={['vendedor', 'parceiro', 'gerente_parcerias', 'vendedor_parceiro', 'motoboy', 'empresa_parceira_admin', 'empresa_parceira_operador', 'consumidor', 'geradoras_externo']}>
            <PainelAdesaoBoletoUnicoView />
          </RoleGuard>
        } />
        {/* Carteira de faturamento (#6242): mesmo público das telas de cobrança; só adm e
            gerente distribuem (a tela e a RLS conferem). */}
        <Route path="carteira-faturamento" element={
          <RoleGuard blocked={['vendedor', 'parceiro', 'gerente_parcerias', 'vendedor_parceiro', 'motoboy', 'empresa_parceira_admin', 'empresa_parceira_operador', 'consumidor', 'geradoras_externo']}>
            <CarteiraFaturamentoView />
          </RoleGuard>
        } />
        {/* Conferência do Faturamento (projeto 12, #5836) — adm, gerente e analista, decisão
            de 15/09/2026. Expõe cliente, UC e motivo de todas as faturas do mês. */}
        <Route path="conferencia-faturamento" element={
          <RoleGuard blocked={['vendedor', 'parceiro', 'gerente_parcerias', 'vendedor_parceiro', 'motoboy', 'empresa_parceira_admin', 'empresa_parceira_operador', 'consumidor', 'geradoras_externo']}>
            <ConferenciaFaturamentoView />
          </RoleGuard>
        } />
        {/* Recebimento de Faturas (projeto 12, #5835) — cada PDF do e-mail assinatura@ e onde
            parou. Mesmo acesso da Conferência; a RLS das tabelas libera só adm, gerente e analista. */}
        <Route path="recebimento-faturas" element={
          <RoleGuard blocked={['vendedor', 'parceiro', 'gerente_parcerias', 'vendedor_parceiro', 'motoboy', 'empresa_parceira_admin', 'empresa_parceira_operador', 'consumidor', 'geradoras_externo']}>
            <RecebimentoFaturasView />
          </RoleGuard>
        } />
        {/* Agrupamentos de Faturamento (projeto 9, #5781) — grupo de UCs que fecha e cobra junto.
            Mesmo acesso da Conferência: quem entra é quem pode gravar (adm, gerente, analista). */}
        <Route path="agrupamentos-faturamento" element={
          <RoleGuard blocked={['vendedor', 'parceiro', 'gerente_parcerias', 'vendedor_parceiro', 'motoboy', 'empresa_parceira_admin', 'empresa_parceira_operador', 'consumidor', 'geradoras_externo']}>
            <AgrupamentosFaturamentoView />
          </RoleGuard>
        } />
        <Route path="auditoria-pagamentos" element={<AuditoriaPagamentosView />} />
        <Route path="repasse-equatorial-manual" element={<RepasseEquatorialManualView />} />
        {/* Só adm: a tela mostra a carteira inteira (clientes, valores, devedores)
            e vai ganhar exportação para Excel e acesso ao modal da UC com ações. */}
        <Route path="painel-executivo" element={
          <RoleGuard allowed={['adm']}>
            <PainelExecutivo />
          </RoleGuard>
        } />
        <Route path="previsao-recebiveis" element={
          <RoleGuard blocked={['vendedor', 'parceiro', 'gerente_parcerias', 'vendedor_parceiro', 'motoboy', 'empresa_parceira_admin', 'empresa_parceira_operador', 'consumidor']}>
            <PrevisaoRecebiveisView />
          </RoleGuard>
        } />
        {/* Sem curinga, caminho inexistente sob /financeiro renderiza tela
            branca sem erro nenhum — foi o que aconteceu com as notificações de
            repasse à Equatorial, que apontavam para o antigo
            /financeiro/auditoria-boleto-unico depois que essa tela virou a
            Auditoria de Pagamentos. */}
        <Route path="*" element={
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-14 h-14 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
              <AlertTriangle size={26} className="text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="text-xl font-poppins font-bold dark:text-white">Página não encontrada</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-md">
              O endereço <code className="font-mono text-xs">{currentPath}</code> não existe no Financeiro.
              Se você chegou por um link de notificação, avise o time — o link está desatualizado.
            </p>
            <Link
              to="/financeiro"
              className="mt-6 px-4 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors text-sm"
            >
              Voltar ao Financeiro
            </Link>
          </div>
        } />
      </Routes>
    </div>
  );
};

// ----------------------------------------------------------------------
// DASHBOARD
// ----------------------------------------------------------------------

const FinanceiroDashboard = () => {
  const { profile } = useAuth();
  const [faturas, setFaturas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const canViewSummary = profile?.role === 'adm' || profile?.role === 'gerente' || profile?.role === 'analista';

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMes, setSelectedMes] = useState<string>('');
  const [isMesFilterOpen, setIsMesFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    fetchData();
  }, []);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setIsMesFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      let allData: any[] = [];
      let from = 0;
      const step = 1000;
      while (true) {
        let q = supabase
          .from('faturas')
          .select('id,status_fatura,mes_referencia,created_at,emitido_em,emitido_por_nome,valor_boleto_vigor,valor_pagamento,energia_injetada_gd1_kwh,duplicata_confirmada')
          // Cobrança de teste (sandbox) nunca entra em indicador financeiro.
          .eq('ambiente', 'producao')
          .range(from, from + step - 1);
        if (profile?.empresa_parceira_id) q = q.eq('empresa_parceira_id', profile.empresa_parceira_id);
        const { data, error } = await q;
        if (error) throw error;
        if (!data || data.length === 0) break;
        allData = allData.concat(data);
        if (data.length < step) break;
        from += step;
      }

      const fNormalized = allData.map((fat: any) => ({
        ...fat,
        mes_referencia: normalizeMesReferencia(fat.mes_referencia)
      }));
      setFaturas(fNormalized);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCurrentMonth = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);

    const mapMes = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
    setSelectedMes(`${mapMes[now.getMonth()]}/${now.getFullYear()}`);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedMes('');
  };

  // Dropdown options
  const mesesDisponiveis = useMemo(() => {
    const unique = Array.from(new Set(faturas.map(f => f.mes_referencia).filter(Boolean)));
    const monthOrder: Record<string, number> = {
      'JAN': 1, 'FEV': 2, 'MAR': 3, 'ABR': 4, 'MAI': 5, 'JUN': 6,
      'JUL': 7, 'AGO': 8, 'SET': 9, 'OUT': 10, 'NOV': 11, 'DEZ': 12
    };

    return unique.sort((a, b) => {
      const [mesA, anoA] = a.split('/');
      const [mesB, anoB] = b.split('/');
      if (anoA !== anoB) return Number(anoB) - Number(anoA);
      return (monthOrder[mesB] || 0) - (monthOrder[mesA] || 0);
    });
  }, [faturas]);

  const toLocalDateStr = (isoStr: string) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return isoStr.split('T')[0];
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Filter Data Logic
  const filterData = () => {
    let filtered = faturas;

    if (startDate || endDate) {
      filtered = filtered.filter(item => {
        const dateStr = item.emitido_em;
        if (!dateStr) return false;
        const localDate = toLocalDateStr(dateStr);
        const start = startDate || '1970-01-01';
        const end = endDate || '2100-01-01';
        return localDate >= start && localDate <= end;
      });
    }

    if (selectedMes) {
      filtered = filtered.filter(item => item.mes_referencia === selectedMes);
    }

    return filtered;
  };

  const filteredFaturasBase = filterData();
  const faturasDuplicadasExcluidas = filteredFaturasBase.filter(f => f.status_fatura === 'Excluído').length;
  const filteredFaturas = filteredFaturasBase.filter(f => f.status_fatura !== 'Excluído');

  // Emitted faturas
  const faturasEmitidas = filteredFaturas.filter(f => f.emitido_em || ['Com boleto emitido / A enviar', 'Cobrança enviada'].includes(f.status_fatura));

  // Metrics
  const totalFaturas = filteredFaturas.length;
  const qtdBoletosEmitidos = faturasEmitidas.length;
  const totalBoletosEmitidos = faturasEmitidas.reduce((acc, f) => acc + (Number(f.valor_boleto_vigor) || 0), 0);
  const energiaInjetada = filteredFaturas.reduce((acc, f) => acc + (Number(f.energia_injetada_gd1_kwh) || 0), 0);

  // User Emissions
  const emissionsByUser = useMemo(() => {
    const acc: Record<string, { name: string; count: number; total: number }> = {};
    faturasEmitidas.forEach(f => {
      const userName = f.emitido_por_nome || 'Desconhecido / Sistema';
      if (!acc[userName]) acc[userName] = { name: userName, count: 0, total: 0 };
      acc[userName].count += 1;
      acc[userName].total += (Number(f.valor_boleto_vigor) || 0);
    });
    return Object.values(acc).sort((a, b) => b.count - a.count);
  }, [faturasEmitidas]);

  // Chart Data Preparation
  const getChartData = () => {
    const dataMap = new Map();

    faturas.forEach(f => {
      const createdDate = toLocalDateStr(f.created_at);
      const isExcluded = f.status_fatura === 'Excluído';
      if (isExcluded) return;

      if (startDate || endDate) {
        const start = startDate || '1970-01-01';
        const end = endDate || '2100-01-01';
        if (createdDate >= start && createdDate <= end) {
          if (!dataMap.has(createdDate)) dataMap.set(createdDate, { date: createdDate, faturas: 0, boletos: 0 });
          dataMap.get(createdDate).faturas += 1;
        }
      } else {
        if (!dataMap.has(createdDate)) dataMap.set(createdDate, { date: createdDate, faturas: 0, boletos: 0 });
        dataMap.get(createdDate).faturas += 1;
      }
    });

    const faturasEmitidasAll = faturas.filter(f => f.status_fatura !== 'Excluído' && (f.emitido_em || ['Com boleto emitido / A enviar', 'Cobrança enviada'].includes(f.status_fatura)));

    faturasEmitidasAll.forEach(f => {
      const emitDate = f.emitido_em ? toLocalDateStr(f.emitido_em) : toLocalDateStr(f.created_at);

      if (startDate || endDate) {
        const start = startDate || '1970-01-01';
        const end = endDate || '2100-01-01';
        if (emitDate >= start && emitDate <= end) {
          if (!dataMap.has(emitDate)) dataMap.set(emitDate, { date: emitDate, faturas: 0, boletos: 0 });
          dataMap.get(emitDate).boletos += 1;
        }
      } else {
        if (!dataMap.has(emitDate)) dataMap.set(emitDate, { date: emitDate, faturas: 0, boletos: 0 });
        dataMap.get(emitDate).boletos += 1;
      }
    });

    return Array.from(dataMap.values()).sort((a, b) => a.date.localeCompare(b.date)).map(item => ({
      ...item,
      date: formatDate(item.date).slice(0, 5) // Display as DD/MM
    }));
  };

  const chartData = getChartData();

  if (loading) return <LoadingSpinner label="Carregando resumo..." />;

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        <h2 className="text-2xl font-poppins font-bold dark:text-white">Resumo Financeiro</h2>

        <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-gray-800 p-2 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          {/* Mês de Referência Filter */}
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setIsMesFilterOpen(!isMesFilterOpen)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border transition-colors ${selectedMes
                ? 'bg-primary/10 border-primary/30 text-primary dark:bg-primary/20 dark:border-primary/50'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800/50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
            >
              <Calendar size={14} />
              <span className="font-medium whitespace-nowrap">
                {selectedMes ? `Mês: ${selectedMes}` : 'Filtrar Mês Ref.'}
              </span>
              <ChevronDown size={14} className={`transition-transform ${isMesFilterOpen ? 'rotate-180' : ''}`} />
            </button>

            {isMesFilterOpen && (
              <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 py-1 max-h-64 overflow-y-auto">
                <button
                  onClick={() => { setSelectedMes(''); setIsMesFilterOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${!selectedMes ? 'font-bold text-primary bg-primary/5 dark:bg-primary/10' : 'text-gray-700 dark:text-gray-300'}`}
                >
                  Todos os meses
                </button>
                {mesesDisponiveis.map(m => (
                  <button
                    key={m}
                    onClick={() => { setSelectedMes(m); setIsMesFilterOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedMes === m ? 'font-bold text-primary bg-primary/5 dark:bg-primary/10' : 'text-gray-700 dark:text-gray-300'}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="hidden md:block w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1"></div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase hidden sm:inline">Período da emissão:</span>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 text-gray-900 dark:text-white rounded px-2 py-1 outline-none focus:border-primary"
            />
            <span className="text-gray-400">-</span>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 text-gray-900 dark:text-white rounded px-2 py-1 outline-none focus:border-primary"
            />
          </div>

          <div className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1"></div>

          <button
            onClick={handleCurrentMonth}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 dark:bg-primary/20 text-primary text-sm font-semibold rounded-lg hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors"
          >
            <Calendar size={14} />
            Mês Atual
          </button>

          {(startDate || endDate || selectedMes) && (
            <button
              onClick={clearFilters}
              className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors ml-auto md:ml-0"
              title="Limpar Filtros"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Cards */}
      {canViewSummary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm transition-shadow hover:shadow-md">
            <div className="flex justify-between items-start">
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Total Faturas</p>
              <FileText size={20} className="text-gray-400" />
            </div>
            <p className="text-2xl font-montserrat font-bold mt-2 text-gray-900 dark:text-white">{totalFaturas}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm transition-shadow hover:shadow-md">
            <div className="flex justify-between items-start">
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Faturas duplicadas/excluídas</p>
              <AlertTriangle size={20} className="text-red-400" />
            </div>
            <p className="text-2xl font-montserrat font-bold mt-2 text-gray-900 dark:text-white">{faturasDuplicadasExcluidas}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm transition-shadow hover:shadow-md">
            <div className="flex justify-between items-start">
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Boletos Emitidos</p>
              <Receipt size={20} className="text-primary" />
            </div>
            <p className="text-2xl font-montserrat font-bold mt-2 text-gray-900 dark:text-white">
              {qtdBoletosEmitidos}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm transition-shadow hover:shadow-md">
            <div className="flex justify-between items-start">
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Total Emitido</p>
              <Banknote size={20} className="text-primary" />
            </div>
            <p className="text-2xl font-montserrat font-bold mt-2 text-primary">
              {totalBoletosEmitidos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm transition-shadow hover:shadow-md">
            <div className="flex justify-between items-start">
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Energia Injetada</p>
              <Bolt size={20} className="text-yellow-500" />
            </div>
            <p className="text-2xl font-montserrat font-bold mt-2 text-gray-700 dark:text-gray-200">
              {energiaInjetada.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} <span className="text-sm font-normal text-gray-400 dark:text-gray-500">kWh</span>
            </p>
          </div>
        </div>
      )}

      {/* Charts & Emissions per User */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <BarChart3 size={20} className="text-primary" />
              Desempenho Diário
            </h3>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" strokeOpacity={0.3} />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 12 }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'rgb(31 41 55)', color: '#fff', borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  itemStyle={{ color: '#fff', fontSize: '12px', fontWeight: 600 }}
                />
                <Legend />
                <Bar dataKey="faturas" name="Faturas" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={20} />
                <Bar dataKey="boletos" name="Boletos Emitidos" fill="#10b981" radius={[4, 4, 0, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm flex flex-col h-full min-h-[400px]">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Users size={20} className="text-primary" />
              Emissões por Usuário
            </h3>
          </div>

          <div className="flex-1 overflow-y-auto pr-2 space-y-3 custom-scrollbar">
            {emissionsByUser.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 mt-10">
                <Receipt size={32} className="mb-2 opacity-50" />
                <p className="text-sm">Nenhum boleto emitido no período.</p>
              </div>
            ) : (
              emissionsByUser.map((em, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">
                      {em.name.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{em.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{em.count} boletos emitidos</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-primary">R$ {em.total.toFixed(2)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Ritmo de emissao do time: tempo medio, boletos fora do prazo padrao e
          motivos dos atrasos. Fica aqui, no fim do Resumo Financeiro, ao lado do
          "Emissoes por Usuario" que ja conta quanto cada um emitiu — este responde
          o quao no prazo isso foi feito. */}
      <SecaoFaturamento />
    </div>
  );
};

// ----------------------------------------------------------------------
// FATURAS LIST
// ----------------------------------------------------------------------


/**
 * Etiqueta de fatura de teste. Só chega a renderizar para adm: a policy
 * sandbox_restrict_faturas não devolve fatura sandbox para nenhum outro papel.
 */
const EtiquetaSandbox: React.FC<{ onRemover?: () => void }> = ({ onRemover }) => (
  <span
    onClick={onRemover ? (e) => { e.stopPropagation(); onRemover(); } : undefined}
    className={`flex items-center gap-1 text-[9px] font-bold text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 px-1.5 py-0.5 rounded-full ${onRemover ? 'cursor-pointer hover:bg-amber-200 dark:hover:bg-amber-900/60' : ''}`}
    title={onRemover
      ? 'Fatura de teste: fora de somatórios, inadimplência e exportação, e invisível para quem não é adm. Clique para tirar a marca.'
      : 'Fatura de teste: fora de somatórios, inadimplência e exportação.'}
  >
    <FlaskConical size={10} /> TESTE
  </span>
);

// Colunas da UC que o Financeiro (Janela de Cobrança) usa — carga cheia e recarga por id.
// `situacao` e `empresa_parceira_id` entram pela Planilha Gran: sem eles toda UC da lista
// parecia "não vinculada à Gran" e as extras não apareciam para incluir. `desconto_tarifa`
// entra pela conferência com a Gran (#6116): a planilha aponta desconto diferente do conferido.
// `rateio_unidade_geradora_id` é a usina do cadastro, para a fatura sem usina impressa (#6402).
const COLUNAS_UC_FINANCEIRO = 'id, numero_uc, outro_numero_uc, nome_cliente, cpf_cnpj_cliente, titular_fatura, cpf_cnpj_titular, data_nascimento_titular, num_medidor, unidade_geradora, percent_rateio, hist_manual_mes_referencia, hist_manual_leitura_anterior, hist_manual_leitura_atual, hist_manual_proxima_leitura, hist_manual_data_apresentacao, hist_manual_data_vencimento, situacao, empresa_parceira_id, desconto_tarifa, rateio_unidade_geradora_id';

/** Valor do filtro "Usina" para as faturas que não vêm de usina da Vigor. */
const FILTRO_SEM_USINA = '__sem_usina__';

const FaturasList = () => {
  const { user, profile } = useAuth();
  const { addToast } = useToast();
  // `?busca=` semeia a busca da lista. É o que faz o alerta "Fatura sem
  // cobrança vencendo em breve — UC 10010198499" cair na fatura, e não numa
  // lista de 6 mil linhas onde a pessoa tem que redigitar a UC que acabou de
  // ler. Só o valor inicial: depois quem manda é o campo.
  const [searchParams] = useSearchParams();
  // `?meses=todos` (link do nome do cliente na rota de cobrança): a busca vale
  // para todos os meses — a dívida é de vários — sem apagar a escolha de meses
  // que a pessoa deixou gravada. A gravação volta quando ela escolhe um mês.
  // `?mes=NOV/2026` (clique na parcela do acordo em Parcelamentos): só aquele mês
  // de referência, também sem gravar. O ref guarda a seleção que o link impôs.
  const mesesDoLinkRef = useRef(mesesDoLink(searchParams));
  // O mês do link é de referência; o critério gravado (vencimento, emissão) fica intacto.
  const tipoDoLinkRef = useRef(!!mesesDoLinkRef.current?.length);
  const [faturas, setFaturas] = useState<any[]>([]);
  const [rateios, setRateios] = useState<any[]>([]);
  const [rateioItens, setRateioItens] = useState<any[]>([]);
  const [unidadesConsumidoras, setUnidadesConsumidoras] = useState<any[]>([]);

  // Carga cheia em andamento (contador: pode haver duas). Enquanto houver, os
  // patches esperam — a foto mais velha que ela devolve apagaria o que aplicaram.
  const cargasEmVooRef = useRef(0);
  const [cargaCheiaEmVoo, setCargaCheiaEmVoo] = useState(false);
  const ucsPendentesRef = useRef<Set<number>>(new Set());
  const faturasRef = useRef<any[]>([]);
  faturasRef.current = faturas;

  // UC cobrada por agrupamento (#5785): não existe boleto individual. O card leva um selo com o
  // nome da Secretaria nas colunas em que ainda aparece (calculado e a enviar não mostram essas
  // faturas; com o malote entregue, viram um card da Secretaria — ver lib/colunaDoQuadro.ts).
  const [agrupamentoPorUc, setAgrupamentoPorUc] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data, error } = await supabase
        .from('vw_agrupamento_faturamento_uc')
        .select('numero_uc, agrupamento_nome')
        .eq('agrupamento_ativo', true);
      if (cancelado) return;
      if (error) {
        // Falhar aqui so tira o selo: o quadro continua funcionando como antes.
        console.error('Agrupamentos das UCs (selo do card):', error);
        return;
      }
      const mapa = new Map<string, string>();
      (data ?? []).forEach((l: any) => {
        const n = String(l.numero_uc ?? '').replace(/\D/g, '').replace(/^0+/, '');
        if (n && l.agrupamento_nome) mapa.set(n, l.agrupamento_nome);
      });
      setAgrupamentoPorUc(mapa);
    })();
    return () => { cancelado = true; };
  }, []);

  // Troca de dono: a fatura carimbada é do dono anterior, e a busca casa o nome dele
  // (guardado na troca), não o de quem está hoje no cadastro da UC.
  const [donosAnteriores, setDonosAnteriores] = useState<Map<string, DonoAnteriorSnapshot>>(new Map());
  useEffect(() => {
    let cancelado = false;
    buscarDonosAnteriores(supabase)
      .then(mapa => { if (!cancelado) setDonosAnteriores(mapa); })
      // Falhar aqui só deixa a fatura carimbada sem o nome do dono anterior na busca.
      .catch(err => console.error('Trocas de dono (busca de faturas):', err));
    return () => { cancelado = true; };
  }, []);

  const ucsMap = useMemo(() => {
    const map = new Map<string, any>();
    unidadesConsumidoras.forEach(uc => {
      if (uc.numero_uc) {
        const norm1 = uc.numero_uc.replace(/[.\-\s]/g, '').replace(/^0+/, '');
        if (norm1) map.set(norm1, uc);
      }
      if (uc.outro_numero_uc) {
        const norm2 = uc.outro_numero_uc.replace(/[.\-\s]/g, '').replace(/^0+/, '');
        if (norm2) map.set(norm2, uc);
      }
    });
    return map;
  }, [unidadesConsumidoras]);

  // Etiquetas de UC (docs/superpowers/specs/2026-10-13-etiquetas-uc-design.md). A fatura acha
  // a UC pelo número — `ucsMap` cobre numero_uc e outro_numero_uc — e a etiqueta é da UC.
  const podeEtiquetar = podeUsarEtiquetas(profile?.role);
  const etiquetasApi = useEtiquetasUc(podeEtiquetar);
  const [ucEtiquetando, setUcEtiquetando] = useState<{ id: number; rotulo: string } | null>(null);
  const ucDaFatura = (fatura: any) => {
    const norm = fatura.uc_codigo ? String(fatura.uc_codigo).replace(/[.\-\s]/g, '').replace(/^0+/, '') : '';
    return norm ? ucsMap.get(norm) ?? null : null;
  };
  const etiquetasDaFatura = (fatura: any) => {
    const uc = ucDaFatura(fatura);
    return uc ? etiquetasApi.porUc.get(uc.id) : undefined;
  };
  /** Pílulas + botão 🏷 da fatura. O botão só aparece para quem pode mexer. */
  const renderEtiquetas = (fatura: any) => {
    const uc = ucDaFatura(fatura);
    return (
      <EtiquetasDaUc
        etiquetas={uc ? etiquetasApi.porUc.get(uc.id) : undefined}
        ucCadastrada={!!uc}
        onAbrir={podeEtiquetar ? () => uc && setUcEtiquetando({ id: uc.id, rotulo: `UC ${getDisplayUC(fatura.uc_codigo)} · ${fatura.cliente_nome || uc.nome_cliente || ''}` }) : undefined}
      />
    );
  };
  /** Classe do destaque (fundo ou contorno) na cor da primeira etiqueta da UC. */
  const destaqueEtiqueta = (fatura: any, tipo: 'fundo' | 'contorno') => {
    const primeira = etiquetasDaFatura(fatura)?.[0];
    return primeira ? estiloDaCor(primeira.cor)[tipo] : '';
  };

  // Usina de cada fatura (#6402): a impressa na conta da distribuidora e, sem ela,
  // a do cadastro da UC. Regra em src/lib/financeiro/usinaDaFatura.ts. Calculada uma
  // vez por carga: o filtro, o card e a exportação só consultam o mapa.
  const indiceUsinas = useIndiceUsinas();
  const usinaPorFatura = useMemo(() => {
    const mapa = new Map<number, ReturnType<typeof usinaDaFatura>>();
    if (indiceUsinas.usinas.length === 0) return mapa;
    for (const f of faturas) mapa.set(f.id, usinaDaFatura(f, ucDaFatura(f), indiceUsinas));
    return mapa;
  }, [faturas, ucsMap, indiceUsinas]);
  const usinaDe = (fatura: any) => usinaPorFatura.get(fatura.id) ?? null;
  const renderUsina = (fatura: any) => {
    const r = usinaDe(fatura);
    if (!r) return null;
    return (
      <span
        className="inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-medium leading-none text-gray-500 dark:text-gray-400 w-fit"
        title={r.origem === 'conta'
          ? `Usina ${r.usina.nome} — impressa na conta da distribuidora`
          : `Usina ${r.usina.nome} — do cadastro da UC (a conta não traz usina)`}
      >
        <Sun size={10} className="shrink-0 text-amber-500" />
        {r.usina.nome}
      </span>
    );
  };

  const canViewSummary = profile?.role === 'adm' || profile?.role === 'gerente' || profile?.role === 'analista';
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'data_vencimento', direction: 'asc' });
  const [cancellationModalData, setCancellationModalData] = useState<any[] | null>(null);
  // Faturas de teste ficam FORA da lista por padrão, mesmo para o adm, e só aparecem
  // quando ele abre o card de teste. Misturadas com as reais, eram confundidas e
  // emitidas de verdade. Ninguém além de adm as recebe (RLS).
  const [sandboxFilter, setSandboxFilter] = useState<'ocultar' | 'somente'>('ocultar');
  const [faturaParaDesmarcar, setFaturaParaDesmarcar] = useState<any | null>(null);
  const [sandboxCount, setSandboxCount] = useState(0);

  const formatDateSafely = (dateVal: any, includeTime = true) => {
    if (!dateVal) return 'Data não disponível';
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) {
        const str = String(dateVal);
        if (str.includes('/') && str.includes(':')) {
          return str;
        }
        return 'Data não disponível';
      }
      if (includeTime) {
        return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
      }
      return d.toLocaleDateString('pt-BR');
    } catch (e) {
      return 'Data não disponível';
    }
  };

  // Filters
  const [activeTab, setActiveTab] = useState<'todos' | 'pendente' | 'gerado'>('todos');
  const [viewMode, setViewMode] = useState<'list' | 'kanban' | 'table'>('kanban');
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('busca') ?? '');
  // Enhanced Filters
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showOnlyOverdue, setShowOnlyOverdue] = useState(false);
  const [showOnlyPaid, setShowOnlyPaid] = useState(false);
  const [showOnlyParcelados, setShowOnlyParcelados] = useState(false);
  const [showOnlyGestao, setShowOnlyGestao] = useState(false);
  const [gestaoUcKeys, setGestaoUcKeys] = useState<Set<string>>(new Set());

  // Carrega uma vez as chaves de UC marcadas para Vigor Gestão (identifica faturas da Gestão pelo uc_codigo)
  useEffect(() => { fetchGestaoUcKeys().then(setGestaoUcKeys).catch(() => {}); }, []);
  const [showOnlyQuitados, setShowOnlyQuitados] = useState(false);
  const [duplicadasFilter, setDuplicadasFilter] = useState<'none' | 'duplicadas' | 'mantidas'>('none');
  const [espelhadasFilter, setEspelhadasFilter] = useState<'none' | 'espelhadas' | 'nao_espelhadas'>('none');
  const [cancelamentoFilter, setCancelamentoFilter] = useState<'none' | 'com' | 'sem'>('none');
  const [avulsasFilter, setAvulsasFilter] = useState<'none' | 'com' | 'sem'>('none');
  // Avulsas de saldo remanescente (coluna faturas.saldo_remanescente). Antes o motivo
  // ia escrito no campo do mês e criava anos falsos no filtro de mês.
  const [showOnlySaldoRemanescente, setShowOnlySaldoRemanescente] = useState(false);
  // UCs da VIGOR que aderiram ao boleto único (unidades_consumidoras.boleto_unico), com a
  // data do aceite. A Gran fica de fora: tem fluxo próprio no Asaas e já tem o card
  // "Empresas Parceiras". Carregadas uma vez, como as da Gestão: a fatura não carrega a adesão.
  const [showOnlyBoletoUnico, setShowOnlyBoletoUnico] = useState(false);
  const [aceiteBoletoUnicoPorUc, setAceiteBoletoUnicoPorUc] = useState<Map<string, string | null>>(new Map());
  const [ucsBoletoUnicoCount, setUcsBoletoUnicoCount] = useState(0);
  useEffect(() => {
    supabase.from('unidades_consumidoras').select('numero_uc, outro_numero_uc, boleto_unico_aceite_em')
      .eq('boleto_unico', true).is('empresa_parceira_id', null)
      .then(({ data, error }) => {
        if (error) { console.error('Erro ao carregar UCs do boleto único:', error); return; }
        const aceites = new Map<string, string | null>();
        (data || []).forEach(uc => ucCodigoKeys(uc.numero_uc, uc.outro_numero_uc)
          .forEach(k => aceites.set(k, uc.boleto_unico_aceite_em ?? null)));
        setAceiteBoletoUnicoPorUc(aceites);
        setUcsBoletoUnicoCount((data || []).length);
      });
  }, []);
  // Fatura de quem aderiu, emitida DEPOIS da adesão: os meses anteriores da mesma UC são
  // boletos comuns (o card chegou a contar 501 faturas pagas desde sempre). Boleto emitido
  // a partir do aceite; sem boleto ainda, fatura que chegou depois do aceite. Fatura de
  // parceira nunca entra.
  const isFaturaDepoisDaAdesao = (f: {
    uc_codigo?: string | null; empresa_parceira_id?: string | null; emitido_em?: string | null; created_at?: string | null;
  }): boolean => {
    if (f.empresa_parceira_id) return false;
    const chaves = ucCodigosDaFatura(f.uc_codigo).filter(k => aceiteBoletoUnicoPorUc.has(k));
    if (chaves.length === 0) return false;
    const aceite = chaves.map(k => aceiteBoletoUnicoPorUc.get(k)).find(Boolean);
    const base = f.emitido_em || f.created_at;
    return !!aceite && !!base && new Date(base).getTime() >= new Date(aceite).getTime();
  };
  // Boleto único de fato: o boleto cobra a conta de luz, ou ainda vai ser emitido (sai
  // somado). É o mesmo critério do filtro "Boleto único" da Auditoria de Pagamentos — os
  // emitidos antes da correção de 14/10/2026 cobram só a Vigor e não entram.
  const isFaturaBoletoUnico = (f: Parameters<typeof isFaturaDepoisDaAdesao>[0] & {
    valor_equatorial_no_boleto?: number | null; linha_digitavel_boleto?: string | null; valor_boleto_vigor?: number | null;
  }): boolean => isFaturaDepoisDaAdesao(f) && boletoCobrouEquatorial(f) !== false;
  // Faturas de empresa parceira (hoje só a Gran) já vêm na listagem — a RLS libera
  // para todos os papéis internos — mas são 5 no meio de 6k+ da Vigor, então na
  // prática ficavam impossíveis de achar. Este filtro isola um lado ou o outro.
  const [parceiraFilter, setParceiraFilter] = useState<'none' | 'parceira' | 'vigor'>('none');
  const [empresasParceiras, setEmpresasParceiras] = useState<{ id: string; nome: string }[]>([]);
  // Planilha Gran (25/09/2026): o card da parceira passa a perguntar entre o quadro
  // filtrado de sempre e a planilha dos condomínios da Gran.
  const [showEscolhaGran, setShowEscolhaGran] = useState(false);
  const [showPlanilhaGran, setShowPlanilhaGran] = useState(false);
  const [filaGran, setFilaGran] = useState<FilaGran | null>(null);
  const [descadastroFilter, setDescadastroFilter] = useState(false);
  // "Minha carteira" (#6242): só os clientes de quem está logado na carteira de faturamento.
  const [minhaCarteira, setMinhaCarteira] = useState(false);
  const [cpfsMinhaCarteira, setCpfsMinhaCarteira] = useState<Set<string> | null>(null);
  useEffect(() => {
    if (!minhaCarteira || !profile?.id || cpfsMinhaCarteira) return;
    supabase.from('carteira_faturamento').select('cpf_cnpj_cliente').eq('responsavel_id', profile.id)
      .then(({ data }) => setCpfsMinhaCarteira(new Set((data || []).map(d => d.cpf_cnpj_cliente))));
  }, [minhaCarteira, profile?.id, cpfsMinhaCarteira]);
  const [recalculadaFilter, setRecalculadaFilter] = useState<'none' | 'com' | 'sem_cancel_ou_recalc' | 'sem'>('none');
  const [filters, setFilters] = useState({
    nome_cliente: '',
    created_at_start: '',
    created_at_end: '',
    tipo_ligacao: '',
    mes_referencia: '',
    data_vencimento_start: '',
    data_vencimento_end: '',
    valor_min: '',
    valor_max: '',
    consumo_min: '',
    consumo_max: '',
    saldo_min: '',
    saldo_max: '',
    classificacao: '',
    status_fatura: '',
    vencimento_boleto_start: '',
    vencimento_boleto_end: '',
    emitido_em_start: '',
    emitido_em_end: '',
    enviado_em_start: '',
    enviado_em_end: '',
    data_pgto_boleto_start: '',
    data_pgto_boleto_end: '',
    // '' = todas; FILTRO_QUALQUER_ETIQUETA = com alguma; senão, id da etiqueta.
    etiqueta: '',
    // '' = todas; FILTRO_SEM_USINA = sem usina da Vigor; senão, id da usina (#6402).
    usina: ''
  });
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  // Pendentes de Cálculo State
  const [showPendentesModal, setShowPendentesModal] = useState(false);

  // Date filter dropdown state
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [dateFilterType, setDateFilterType] = useState<'created_at' | 'emitido_em' | 'enviado_em' | 'vencimento_boleto' | 'data_vencimento' | 'data_pgto_boleto'>('created_at');
  const dateDropdownRef = useRef<HTMLDivElement>(null);

  // Export dropdown state
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showKPIDashboard, setShowKPIDashboard] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  // Atualização manual da lista — evita o F5. Recarrega faturas e parcelamentos
  // sem passar por `loading`, para o quadro/tabela não sumir da tela enquanto
  // busca: a lista atual continua visível e é trocada quando o dado novo chega.
  const [refreshing, setRefreshing] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date>(() => new Date());

  // Malotes das Secretarias (Prefeitura) já entregues: as faturas deles saem do quadro e viram
  // um card por Secretaria em "Cobrança enviada" (ver lib/colunaDoQuadro.ts). Relido a cada
  // carga cheia da lista.
  const [malotesEntregues, setMalotesEntregues] = useState<MalotesEntregues>(SEM_MALOTES);
  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const { data: fechamentos, error } = await supabase
          .from('agrupamento_fechamentos')
          .select('id, agrupamento_id, competencia, fatura_boleto_id, ucs_no_grupo, valor_cobrado, total_vigor, malote_entregue_em, situacao_pagamento')
          .not('malote_entregue_em', 'is', null);
        if (error) throw error;
        const idsFechamentos = (fechamentos ?? []).map((f: any) => f.id);
        if (!idsFechamentos.length) {
          if (!cancelado) setMalotesEntregues(SEM_MALOTES);
          return;
        }
        const [itens, agrupamentos] = await Promise.all([
          // ~30 UCs por Secretaria por mês: passa de 1.000 linhas em poucos meses. fatura_id é único.
          buscarTudoPorChave<any>(
            (apos, tamanho) => {
              let q = supabase
                .from('agrupamento_fechamento_faturas')
                .select('fechamento_id, fatura_id, valor_vigor')
                .in('fechamento_id', idsFechamentos)
                .order('fatura_id', { ascending: true })
                .limit(tamanho);
              if (apos != null) q = q.gt('fatura_id', apos);
              return q as any;
            },
            (i) => i.fatura_id,
          ),
          supabase.from('agrupamentos_faturamento').select('id, nome, pagador_nome'),
        ]);
        if (agrupamentos.error) throw agrupamentos.error;
        if (!cancelado) setMalotesEntregues(montarMalotesEntregues(fechamentos as any[], itens, agrupamentos.data ?? []));
      } catch (err) {
        // Falhar aqui deixa o quadro como se nenhum malote tivesse sido entregue: as faturas da
        // Prefeitura continuam fora de calculado/a enviar, só não aparece o card da Secretaria.
        console.error('Malotes entregues (quadro de faturas):', err);
      }
    })();
    return () => { cancelado = true; };
  }, [ultimaAtualizacao]);
  // Boleto agrupado de cada Secretaria, para o card mostrar vencimento e situação do pagamento.
  const boletosAgrupados = useMemo(
    () => new Map(faturas.filter(f => f.cobranca_agrupada === 'boleto').map(f => [Number(f.id), f])),
    [faturas],
  );

  // Kanban Pagination State
  const [globalKanbanPageSize, setGlobalKanbanPageSize] = useState<number | 'all'>(25);
  const [showPageSizeDropdown, setShowPageSizeDropdown] = useState(false);

  const handleGlobalPageSizeChange = (val: number | 'all') => {
    setGlobalKanbanPageSize(val);
    setShowPageSizeDropdown(false);
  };

  // Cards Configuration State
  const [showCardsModal, setShowCardsModal] = useState(false);
  const ALL_CARDS_LIST = [
    { id: 'total_faturas', label: 'Total de Faturas', icon: <FileText size={18} /> },
    { id: 'saldo_remanescente', label: 'Saldo Remanescente', icon: <BatteryMedium size={18} /> },
    { id: 'boleto_unico', label: 'Boleto Único', icon: <Layers size={18} /> },
    { id: 'pendente_calculo', label: 'Pendente de Cálculo', icon: <Calculator size={18} /> },
    { id: 'boletos_emitidos', label: 'Boletos Emitidos', icon: <Receipt size={18} /> },
    { id: 'total_emitido', label: 'Total Emitido', icon: <DollarSign size={18} /> },
    { id: 'total_areceber', label: 'Total a Receber', icon: <Banknote size={18} /> },
    { id: 'total_injetado', label: 'Total Injetado', icon: <Bolt size={18} /> },
    { id: 'vencidos', label: 'Boletos Vencidos', icon: <AlertTriangle size={18} /> },
    { id: 'pagos', label: 'Boletos Pagos', icon: <CheckCircle2 size={18} /> },
    { id: 'espelhadas', label: 'Faturas Espelhadas', icon: <Copy size={18} /> },
    { id: 'duplicadas', label: 'Faturas Duplicadas', icon: <Files size={18} /> },
    { id: 'cancelamento_solicitado', label: 'Cancel. Solicitado', icon: <XCircle size={18} /> },
    { id: 'avulsas', label: 'Cobranças Avulsas', icon: <Zap size={18} /> },
    { id: 'recalculada', label: 'Cobrança Recalculada', icon: <RefreshCw size={18} /> },
    { id: 'parcelados', label: 'Parcelados', icon: <Split size={18} /> },
    { id: 'quitados', label: 'Quitados', icon: <BadgeCheck size={18} /> },
    { id: 'descadastro_email', label: 'Descadastrar E-mail', icon: <MailX size={18} /> },
    { id: 'gestao', label: 'Vigor Gestão', icon: <Building2 size={18} /> },
    { id: 'parceiras', label: 'Empresas Parceiras', icon: <Handshake size={18} /> },
    { id: 'sandbox', label: 'Sandbox (teste)', icon: <FlaskConical size={18} /> }
  ];

  const ALL_CARDS = useMemo(() => {
    // Sandbox é ferramenta de teste do adm: ninguém mais precisa saber que existe.
    const base = profile?.role === 'adm'
      ? ALL_CARDS_LIST
      : ALL_CARDS_LIST.filter(c => c.id !== 'sandbox');
    // Only adm and gerente can see Vencidos, Pagos and Cancelamento Solicitado
    if (profile?.role === 'analista') {
      return base.filter(c => !['total_emitido', 'total_areceber', 'total_injetado'].includes(c.id));
    }
    return base;
  }, [profile?.role]);

  // Preferências salvas antes de um card existir não o incluem; injeta uma única vez
  // (flag no localStorage) para o card aparecer sem sobrescrever quem o ocultar depois.
  const seedCard = (cards: string[], userId: string, cardId: string): string[] => {
    const seedKey = `vigorhub_financeiro_card_${cardId}_seeded_${userId}`;
    if (localStorage.getItem(seedKey)) return cards;
    localStorage.setItem(seedKey, '1');
    return cards.includes(cardId) ? cards : [...cards, cardId];
  };

  const [visibleCards, setVisibleCards] = useState<string[]>(() => {
    if (typeof window !== 'undefined' && user?.id) {
      const saved = localStorage.getItem(`vigorhub_financeiro_visible_cards_${user.id}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            return seedCard(seedCard(seedCard(seedCard(parsed, user.id, 'duplicadas'), user.id, 'parceiras'), user.id, 'saldo_remanescente'), user.id, 'boleto_unico');
          }
        } catch (e) {
          console.error(e);
        }
      }
    }
    // Default: show all cards
    return ALL_CARDS.map(c => c.id);
  });

  // Tracks whether visibleCards was loaded from localStorage for the current user.
  // Prevents the save effect from overwriting stored preferences before the load completes
  // (user?.id may be undefined on first render when auth is still loading).
  const [cardsInitialized, setCardsInitialized] = useState(() => typeof window !== 'undefined' && !!user?.id);

  useEffect(() => {
    if (user?.id && !cardsInitialized) {
      const saved = localStorage.getItem(`vigorhub_financeiro_visible_cards_${user.id}`);
      setCardsInitialized(true);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            setVisibleCards(seedCard(seedCard(seedCard(seedCard(parsed, user.id, 'duplicadas'), user.id, 'parceiras'), user.id, 'saldo_remanescente'), user.id, 'boleto_unico'));
          }
        } catch (e) {
          console.error(e);
        }
      }
    }
  }, [user?.id, cardsInitialized]);

  useEffect(() => {
    if (user?.id && cardsInitialized) {
      localStorage.setItem(`vigorhub_financeiro_visible_cards_${user.id}`, JSON.stringify(visibleCards));
    }
  }, [visibleCards, user?.id, cardsInitialized]);

  // Function to count active filters
  useEffect(() => {
    let count = 0;
    if (filters.nome_cliente) count++;
    if (filters.created_at_start || filters.created_at_end) count++;
    if (filters.tipo_ligacao) count++;
    if (filters.mes_referencia) count++;
    if (filters.data_vencimento_start || filters.data_vencimento_end) count++;
    if (filters.valor_min || filters.valor_max) count++;
    if (filters.consumo_min || filters.consumo_max) count++;
    if (filters.saldo_min || filters.saldo_max) count++;
    if (filters.classificacao) count++;
    if (filters.status_fatura) count++;
    if (filters.etiqueta) count++;
    if (filters.usina) count++;
    if (filters.vencimento_boleto_start || filters.vencimento_boleto_end) count++;
    if (filters.emitido_em_start || filters.emitido_em_end) count++;
    if (filters.enviado_em_start || filters.enviado_em_end) count++;
    if (filters.data_pgto_boleto_start || filters.data_pgto_boleto_end) count++;
    // Os toggles dos cards filtram o quadro inteiro igual aos filtros do modal, mas
    // ficavam fora desta conta: com o card fora da vista no carrossel, o quadro
    // aparecia vazio sem nada na tela indicando filtro ativo (caso do "Descadastrar
    // E-mail", 11/09/2026). Entram aqui para o botão Filtros denunciar todos.
    if (showOnlyOverdue) count++;
    if (showOnlyPaid) count++;
    if (showOnlyParcelados) count++;
    if (showOnlyQuitados) count++;
    if (showOnlyGestao) count++;
    if (duplicadasFilter !== 'none') count++;
    if (descadastroFilter) count++;
    if (espelhadasFilter !== 'none') count++;
    if (sandboxFilter === 'somente') count++;
    if (recalculadaFilter !== 'none') count++;
    if (cancelamentoFilter !== 'none') count++;
    if (avulsasFilter !== 'none') count++;
    if (showOnlySaldoRemanescente) count++;
    if (showOnlyBoletoUnico) count++;
    if (parceiraFilter !== 'none') count++;
    setActiveFiltersCount(count);
  }, [filters, showOnlyOverdue, showOnlyPaid, showOnlyParcelados, showOnlyQuitados,
      showOnlyGestao, duplicadasFilter, descadastroFilter, espelhadasFilter, sandboxFilter,
      recalculadaFilter, cancelamentoFilter, avulsasFilter, showOnlySaldoRemanescente, showOnlyBoletoUnico, parceiraFilter]);

  // Clear Filters
  const clearFilters = () => {
    setFilters({
      nome_cliente: '',
      created_at_start: '',
      created_at_end: '',
      tipo_ligacao: '',
      mes_referencia: '',
      data_vencimento_start: '',
      data_vencimento_end: '',
      valor_min: '',
      valor_max: '',
      consumo_min: '',
      consumo_max: '',
      saldo_min: '',
      saldo_max: '',
      classificacao: '',
      status_fatura: '',
      vencimento_boleto_start: '',
      vencimento_boleto_end: '',
      emitido_em_start: '',
      emitido_em_end: '',
      enviado_em_start: '',
      enviado_em_end: '',
      data_pgto_boleto_start: '',
      data_pgto_boleto_end: '',
      etiqueta: '',
      usina: ''
    });
    setSearchTerm('');
    setSelectedMeses([]);
    setShowOnlyOverdue(false);
    setShowOnlyPaid(false);
    setShowOnlyParcelados(false);
    setShowOnlyQuitados(false);
    setShowOnlyGestao(false);
    setDuplicadasFilter('none');
    // Faltavam aqui: "limpar filtros" devolvia o quadro ainda filtrado por estes.
    setDescadastroFilter(false);
    setEspelhadasFilter('none');
    setSandboxFilter('ocultar');
    setRecalculadaFilter('none');
    setCancelamentoFilter('none');
    setAvulsasFilter('none');
    setShowOnlySaldoRemanescente(false);
    setShowOnlyBoletoUnico(false);
    setParceiraFilter('none');
  };

  // Helper for Status Colors
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Fatura disponível':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      case 'Fatura validada':
        return 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'; // Azul claro
      case 'Com boleto calculado':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'; // Azul escuro
      case 'Com boleto emitido / A enviar':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'; // Verde claro
      case 'Cobrança enviada':
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'; // Verde escuro
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusBorderColor = (status: string) => {
    switch (status) {
      case 'Fatura disponível': return 'border-purple-200 dark:border-purple-800';
      case 'Fatura validada': return 'border-sky-200 dark:border-sky-800';
      case 'Com boleto calculado': return 'border-blue-200 dark:border-blue-800';
      case 'Com boleto emitido / A enviar': return 'border-green-200 dark:border-green-800';
      case 'Cobrança enviada': return 'border-emerald-200 dark:border-emerald-800';
      default: return 'border-gray-200 dark:border-gray-700';
    }
  };

  const isBoletoCalculado = (status: string) => {
    return ['Com boleto calculado', 'Com boleto emitido / A enviar', 'Cobrança enviada'].includes(status);
  };

  const getBoletoBadge = (fatura: any) => {
    if (!isBoletoCalculado(fatura.status_fatura)) return null;

    const currentStatus = (fatura.status_boleto_vigor || '').trim().toLowerCase();

    if (currentStatus === 'pago') {
      return (
        <span className="flex items-center gap-1 text-[10px] font-bold text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-400 px-1.5 py-0.5 rounded">
          <Banknote size={12} /> Pago
        </span>
      );
    }

    if (currentStatus === 'cancelado') {
      return (
        <span className="flex items-center gap-1 text-[10px] font-bold text-gray-700 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-400 px-1.5 py-0.5 rounded">
          <X size={12} /> Cancelado
        </span>
      );
    }

    if (currentStatus === 'parcelado') {
      return (
        <span className="flex items-center gap-1 text-[10px] font-bold text-indigo-700 bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400 px-1.5 py-0.5 rounded">
          <Split size={12} /> Parcelado
        </span>
      );
    }

    if (currentStatus === 'quitado') {
      return (
        <span className="flex items-center gap-1 text-[10px] font-bold text-teal-700 bg-teal-100 dark:bg-teal-900/30 dark:text-teal-400 px-1.5 py-0.5 rounded">
          <BadgeCheck size={12} /> Quitado
        </span>
      );
    }

    // UC de agrupamento (Prefeitura): a data desta fatura é a da Equatorial. Quem
    // cobra é o boleto agrupado da Secretaria, com vencimento próprio (#6284).
    if (fatura.cobranca_agrupada === 'membro') {
      if (Number(fatura.valor_boleto_vigor || 0) <= 0) return null;
      return (
        <span
          title="Cobrada no boleto agrupado da Secretaria, que tem vencimento próprio"
          className="flex items-center gap-1 text-[10px] font-bold text-violet-700 bg-violet-100 dark:bg-violet-900/30 dark:text-violet-400 px-1.5 py-0.5 rounded"
        >
          <Layers size={12} /> Cobrança agrupada
        </span>
      );
    }

    if (fatura.vencimento_boleto) {
      const vencimento = new Date(fatura.vencimento_boleto);
      const hoje = new Date();
      vencimento.setHours(0, 0, 0, 0);
      hoje.setHours(0, 0, 0, 0);

      // We consider the original time but normalize to local midnight to compare correctly.
      // E.g., Date parses 'YYYY-MM-DD' as UTC midnight.
      // So to be safe with timezone, we can parse manually or use the simple local date check:
      const [year, month, day] = fatura.vencimento_boleto.split('T')[0].split('-');
      const vencimentoLocal = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      vencimentoLocal.setHours(0, 0, 0, 0);

      if (vencimentoLocal < hoje && currentStatus !== 'pago' && currentStatus !== 'cancelado' && Number(fatura.valor_boleto_vigor || 0) > 0) {
        return (
          <span className="flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-400 px-1.5 py-0.5 rounded">
            <AlertTriangle size={12} /> Vencido
          </span>
        );
      }
    }

    if (currentStatus === 'em aberto' || !fatura.status_boleto_vigor) {
      if (Number(fatura.valor_boleto_vigor || 0) <= 0) return null;
      return (
        <span className="flex items-center gap-1 text-[10px] font-bold text-yellow-700 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400 px-1.5 py-0.5 rounded">
          <Clock size={12} /> Em aberto
        </span>
      );
    }

    return null;
  };

  // Card de uma Secretaria (Prefeitura) com o malote do mês entregue (components/CardMaloteSecretaria).
  const renderCardMalote = (m: MaloteNoQuadro) => {
    const boleto = m.faturaBoletoId != null ? boletosAgrupados.get(m.faturaBoletoId) : undefined;
    return (
      <CardMaloteSecretaria
        key={`malote-${m.fechamentoId}`}
        malote={m}
        vencimentoBoleto={boleto?.vencimento_boleto ?? null}
        seloBoleto={boleto ? getBoletoBadge(boleto) : null}
      />
    );
  };

  // Mês antigo salvo no filtro volta para o atual ao abrir (ver filtroMesSalvo.ts);
  // o aviso sai num effect porque o toast não roda dentro do inicializador.
  const mesesDescartadosRef = useRef<string[]>([]);
  const lerMesesSalvos = (saved: string): string[] => {
    const { meses, descartados } = revisarMesesSalvos(JSON.parse(saved), new Date());
    if (descartados.length) mesesDescartadosRef.current = descartados;
    return meses;
  };
  const [selectedMeses, setSelectedMeses] = useState<string[]>(() => {
    if (mesesDoLinkRef.current) return mesesDoLinkRef.current;
    if (typeof window !== 'undefined' && user?.id) {
      const saved = localStorage.getItem(`vigorhub_faturas_selectedMeses_${user.id}`);
      if (saved) {
        try {
          return lerMesesSalvos(saved);
        } catch (e) {
          console.error(e);
        }
      }
    }
    return [];
  });
  const [mesesInitialized, setMesesInitialized] = useState(() => typeof window !== 'undefined' && !!user?.id);
  const [tipoMesFiltro, setTipoMesFiltro] = useState<TipoMesFiltro>(() => {
    if (tipoDoLinkRef.current) return 'referencia';
    if (typeof window !== 'undefined' && user?.id) {
      const saved = localStorage.getItem(`vigorhub_faturas_tipoMesFiltro_${user.id}`);
      if (saved && TIPOS_MES_FILTRO.some(t => t.key === saved)) return saved as TipoMesFiltro;
    }
    return 'referencia';
  });
  const [isMesFilterOpen, setIsMesFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user?.id && !mesesInitialized) {
      const saved = localStorage.getItem(`vigorhub_faturas_selectedMeses_${user.id}`);
      const savedTipo = localStorage.getItem(`vigorhub_faturas_tipoMesFiltro_${user.id}`);
      if (savedTipo && !tipoDoLinkRef.current && TIPOS_MES_FILTRO.some(t => t.key === savedTipo)) setTipoMesFiltro(savedTipo as TipoMesFiltro);
      setMesesInitialized(true);
      if (saved && !mesesDoLinkRef.current) {
        try {
          setSelectedMeses(lerMesesSalvos(saved));
        } catch (e) {
          console.error(e);
        }
      }
    }
  }, [user?.id, mesesInitialized]);

  useEffect(() => {
    const descartados = mesesDescartadosRef.current;
    if (!mesesInitialized || descartados.length === 0) return;
    mesesDescartadosRef.current = [];
    addToast(`Seu filtro estava em ${descartados.join(', ')}. Voltamos para o mês atual.`, 'info', 8000);
  }, [mesesInitialized, selectedMeses]);

  useEffect(() => {
    if (!user?.id || !mesesInitialized) return;
    // Aberta por link (todos os meses ou um mês só): a seleção do link não
    // sobrescreve a escolha gravada. Mexeu nos meses na tela, volta a gravar.
    if (mesesDoLinkRef.current) {
      if (selectedMeses.join() === mesesDoLinkRef.current.join()) return;
      mesesDoLinkRef.current = null;
    }
    localStorage.setItem(`vigorhub_faturas_selectedMeses_${user.id}`, JSON.stringify(selectedMeses));
  }, [selectedMeses, user?.id, mesesInitialized]);

  useEffect(() => {
    if (!user?.id || !mesesInitialized) return;
    if (tipoDoLinkRef.current) {
      if (tipoMesFiltro === 'referencia') return;
      tipoDoLinkRef.current = false;
    }
    localStorage.setItem(`vigorhub_faturas_tipoMesFiltro_${user.id}`, tipoMesFiltro);
  }, [tipoMesFiltro, user?.id, mesesInitialized]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setIsMesFilterOpen(false);
      }
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target as Node)) {
        setShowDateDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const mesesDisponiveis = useMemo(() => {
    const unique = Array.from(new Set(faturas.map(f => mesDaFaturaPorTipo(f, tipoMesFiltro)).filter(Boolean)));
    const monthOrder: Record<string, number> = {
      'JAN': 1, 'FEV': 2, 'MAR': 3, 'ABR': 4, 'MAI': 5, 'JUN': 6,
      'JUL': 7, 'AGO': 8, 'SET': 9, 'OUT': 10, 'NOV': 11, 'DEZ': 12
    };

    const grouped = unique.reduce((acc: Record<string, any[]>, mesAno: string) => {
      const parts = mesAno.split('/');
      if (parts.length === 2) {
        const [mes, ano] = parts;
        if (!acc[ano]) acc[ano] = [];
        acc[ano].push({ label: mesAno, mes, ano, mesNum: monthOrder[mes.toUpperCase()] || 0 });
      }
      return acc;
    }, {});

    const sortedYears = Object.keys(grouped).sort((a, b) => Number(a) - Number(b));

    return sortedYears.map(ano => {
      grouped[ano].sort((a, b) => a.mesNum - b.mesNum);
      return { ano, meses: grouped[ano] };
    });
  }, [faturas, tipoMesFiltro]);

  const toggleMes = (mesAno: string) => {
    setSelectedMeses(prev =>
      prev.includes(mesAno) ? prev.filter(m => m !== mesAno) : [...prev, mesAno]
    );
  };

  const handleMarcarTodos = () => {
    const todos = mesesDisponiveis.flatMap(g => g.meses.map(m => m.label));
    setSelectedMeses(todos);
  };

  const handleLimparMeses = () => {
    setSelectedMeses([]);
  };

  // Dropdown Portal State
  const [dropdownState, setDropdownState] = useState<{
    fatura: any;
    position: { top: number; left: number };
  } | null>(null);

  // Edit State
  const [editingFatura, setEditingFatura] = useState<any | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Delete State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [faturaToDelete, setFaturaToDelete] = useState<any | null>(null);
  const [motivoExclusao, setMotivoExclusao] = useState('');

  // Parcelamento Modal State
  const [showParcelamentoModal, setShowParcelamentoModal] = useState(false);
  const [parcelarFatura, setParcelarFatura] = useState<any | null>(null);

  // Sincronizar Status Modal State
  const [showSincronizarStatusModal, setShowSincronizarStatusModal] = useState(false);
  const [sincronizarFatura, setSincronizarFatura] = useState<any | null>(null);

  // Quitar (pago via avulsa) Modal State
  const [showQuitarModal, setShowQuitarModal] = useState(false);
  const [quitarFatura, setQuitarFatura] = useState<any | null>(null);
  const [activeParcelamentos, setActiveParcelamentos] = useState<{ count: number; total: number }>({ count: 0, total: 0 });
  const [parcelaFaturaIds, setParcelaFaturaIds] = useState<Set<number>>(new Set());
  // Acordos e parcelas: dizem, na planilha e no card, que a entrada/parcela é
  // negociação e de que meses é a dívida que ela cobra (#6260).
  const [dadosAcordos, setDadosAcordos] = useState<{ acordos: AcordoDoVinculo[]; parcelas: ParcelaDoVinculo[] }>({ acordos: [], parcelas: [] });

  // Validation Modal State
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validatingFaturaId, setValidatingFaturaId] = useState<string | null>(null); // Changed to ID
  const [showGenericModal, setShowGenericModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showKanbanExportModal, setShowKanbanExportModal] = useState(false);
  const [showZipModal, setShowZipModal] = useState(false);

  // Year Expansion State for Filters
  const [expandedYears, setExpandedYears] = useState<Record<string, boolean>>({});

  const toggleYearExpand = (ano: string) => {
    setExpandedYears(prev => ({
      ...prev,
      [ano]: !(prev[ano] ?? false)
    }));
  };

  const isYearExpanded = (ano: string, meses: { label: string }[]) => {
    if (expandedYears[ano] !== undefined) return expandedYears[ano];
    // Auto expand current year or any year containing selected months for superior UX
    return meses.some(m => selectedMeses.includes(m.label));
  };

  const KANBAN_STATUSES = ['Fatura disponível', 'Fatura validada', 'Com boleto calculado', 'Com boleto emitido / A enviar', 'Cobrança enviada'];
  const KANBAN_STATUS_COLORS: Record<string, string> = {
    'Fatura disponível': 'bg-purple-500',
    'Fatura validada': 'bg-sky-500',
    'Com boleto calculado': 'bg-blue-500',
    'Com boleto emitido / A enviar': 'bg-green-500',
    'Cobrança enviada': 'bg-emerald-600',
  };
  const [exportKanbanFilter, setExportKanbanFilter] = useState<string[]>(KANBAN_STATUSES);
  const [ucSemFormatacao, setUcSemFormatacao] = useState(false);

  const formatUCExport = (uc: string): string => {
    if (!uc) return uc;
    const cleaned = uc.replace(/[.\-]/g, '');
    return cleaned.replace(/^0+/, '') || '0';
  };

  // Real-time lock tracking: fatura_id -> { locked_by_user_id, locked_by_name }
  const [activeLocks, setActiveLocks] = useState<Record<number, { locked_by_user_id: string; locked_by_name: string }>>({})

  // In-memory profile name cache to avoid N+1 API calls on each lock realtime event
  const profileCacheRef = useRef<Record<string, string>>({});

  const getProfileName = async (userId: string): Promise<string> => {
    if (profileCacheRef.current[userId]) return profileCacheRef.current[userId];
    const { data } = await supabase.from('profiles').select('id,full_name').eq('id', userId).single();
    if (data?.full_name) profileCacheRef.current[data.id] = data.full_name;
    return data?.full_name || 'Outro usuário';
  };

  const prefetchProfileNames = async (userIds: string[]): Promise<void> => {
    const missing = userIds.filter(id => !profileCacheRef.current[id]);
    if (missing.length === 0) return;
    const { data } = await supabase.from('profiles').select('id,full_name').in('id', missing);
    (data || []).forEach((p: any) => { profileCacheRef.current[p.id] = p.full_name; });
  };;

  // Duplicate Modal State
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicatedFaturasGroup, setDuplicatedFaturasGroup] = useState<any[]>([]);

  // Parceria (#6388): o boleto da parceira separado em bruto, conta de luz, comissão da
  // parceira e o que fica com a Vigor. É a mesma divisão do card "Ficou com a Gran"
  // (divisaoBoletoParceira) e fecha no boleto. Boleto cancelado, parcelado ou quitado
  // fica em branco: o dinheiro anda na cobrança que tomou o lugar dele.
  const divisaoParceria = (f: any) =>
    ['cancelado', 'parcelado', 'quitado'].includes((f.status_boleto_vigor || '').trim().toLowerCase())
      ? null
      : divisaoBoletoParceira(f);
  const moedaParceria = (v: number | undefined) => v != null ? { v, t: 'n', z: '"R$" #,##0.00' } : '';
  const COR_PARCERIA = 'B45309';

  const faturasExportColumns: ExportColumn[] = [
    { key: 'uc_codigo', label: 'Número da UC' },
    { key: 'numero_medidor', label: 'Número do Medidor' },
    { key: 'pdf_fatura', label: 'Link da fatura da distribuidora' },
    { key: 'status_fatura', label: 'Status da fatura (distribuidora)', format: (v: any) => v || 'Fatura disponível' },
    { key: 'classificacao', label: 'Classificação' },
    { key: 'cliente_cpf_cnpj', label: 'CPF/CNPJ do cliente' },
    { key: 'cliente_nome', label: 'Nome do cliente' },
    { key: 'desconto_tarifa', label: 'Desconto tarifa', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '0%' }) },
    { key: 'desconto_bandeira', label: 'Desconto bandeira', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '0%' }) },
    { key: 'data_apresentacao', label: 'Data de apresentação', format: (v: any) => v ? { v: parseLocalDate(v), t: 'd', z: 'dd/mm/yyyy' } : '' },
    { key: 'data_vencimento', label: 'Data de vencimento da fatura (distribuidora)', format: (v: any) => v ? { v: parseLocalDate(v), t: 'd', z: 'dd/mm/yyyy' } : '' },
    { key: 'cadastro_rateio', label: '% Rateio', format: (v: any) => v != null ? { v: Number(v), t: 'n' } : '' },
    { key: 'leitura_anterior', label: 'Leitura anterior' },
    { key: 'leitura_atual', label: 'Leitura atual' },
    { key: 'proxima_leitura', label: 'Próxima leitura', format: (v: any) => v ? { v: parseLocalDate(v), t: 'd', z: 'dd/mm/yyyy' } : '' },
    { key: 'mes_referencia', label: 'Mês Referência' },
    // Entrada e parcela de acordo vencem num mês e pagam a dívida de outros (#6260).
    { key: '_meses_negociados', label: 'Meses negociados (acordo)' },
    { key: 'geracao_ciclo', label: 'Ciclo/Mês' },
    // Nome da usina (#6402): o número da UC geradora sozinho ninguém lê.
    { key: '_usina', label: 'Usina' },
    { key: '_usina_origem', label: 'Usina — de onde veio' },
    { key: 'unidade_geradora_1', label: 'Unidade geradora 1' },
    { key: 'unidade_geradora_2', label: 'Unidade geradora 2' },
    { key: 'unidade_geradora_3', label: 'Unidade geradora 3' },
    { key: 'unidade_geradora_4', label: 'Unidade geradora 4' },
    { key: 'unidade_geradora_5', label: 'Unidade geradora 5' },
    { key: 'geracao_unidade_geradora_1', label: 'Geração UG 1' },
    { key: 'geracao_unidade_geradora_2', label: 'Geração UG 2' },
    { key: 'geracao_unidade_geradora_3', label: 'Geração UG 3' },
    { key: 'geracao_unidade_geradora_4', label: 'Geração UG 4' },
    { key: 'geracao_unidade_geradora_5', label: 'Geração UG 5' },
    { key: 'url_boleto', label: 'Link da fatura da Vigor' },
    { key: 'valor_boleto_vigor', label: 'Valor do boleto Vigor', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '"R$" #,##0.00' }) },
    { key: 'valor_pagamento', label: 'Valor Pago (recebido)', format: (v: any) => v != null && v !== '' ? { v: Number(v), t: 'n', z: '"R$" #,##0.00' } : '' },
    {
      key: '_parceria', label: 'Parceria', headerColor: COR_PARCERIA,
      format: (_: any, item: any) => !item.empresa_parceira_id ? '' :
        (empresasParceiras.find(e => e.id === item.empresa_parceira_id)?.nome || 'Parceira')
        + (item.fora_parceria_vigor ? ' (cliente próprio)' : ''),
    },
    { key: '_parceria_bruto', label: 'Parceria: bruto (R$)', headerColor: COR_PARCERIA, format: (_: any, item: any) => moedaParceria(divisaoParceria(item)?.boleto) },
    { key: '_parceria_equatorial', label: 'Parceria: repasse Equatorial (R$)', headerColor: COR_PARCERIA, format: (_: any, item: any) => moedaParceria(divisaoParceria(item)?.equatorial) },
    { key: '_parceria_comissao', label: 'Parceria: comissão da parceira (R$)', headerColor: COR_PARCERIA, format: (_: any, item: any) => moedaParceria(divisaoParceria(item)?.parceira) },
    { key: '_parceria_liquido', label: 'Parceria: líquido Vigor (R$)', headerColor: COR_PARCERIA, format: (_: any, item: any) => moedaParceria(divisaoParceria(item)?.vigor) },
    { key: 'status_boleto_vigor', label: 'Status do boleto Vigor' },
    { key: '_vinculo_parcelamento', label: 'Vínculo parcelamento' },
    { key: '_negociado_por', label: 'Acordo feito por' },
    { key: 'economia', label: 'Economia', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '"R$" #,##0.00' }) },
    { key: 'vencimento_boleto', label: 'Vencimento do boleto Vigor', format: (v: any) => v ? { v: parseLocalDate(v), t: 'd', z: 'dd/mm/yyyy' } : '' },
    { key: 'data_pgto_boleto', label: 'Data de Pagamento', format: (v: any) => v ? { v: parseLocalDate(v), t: 'd', z: 'dd/mm/yyyy' } : '' },
    { key: 'valor_consumo_scee_boleto', label: 'Valor do consumo SCEE', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '"R$" #,##0.00' }) },
    { key: 'valor_adc_bandeira_boleto', label: 'Valor adicional bandeira', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '"R$" #,##0.00' }) },
    { key: 'qtd_adc_bandeira_boleto', label: 'Qtd adicional bandeira', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '#,##0.00' }) },
    { key: 'valor_restituicao_imposto', label: 'Valor restituição de imposto (R$)', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '"R$" #,##0.00' }) },
    { key: 'energia_nao_isenta', label: 'Valor energia não isenta (R$)', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '"R$" #,##0.00' }) },
    { key: 'outros_multas_boleto', label: 'Valor multas/juros/outros (R$)', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '"R$" #,##0.00' }) },
    { key: 'outros_multas_descricao', label: 'Descrição multas/juros/outros' },
    { key: 'valor_parcelamentos_boleto', label: 'Valor parcelamento (R$)', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '"R$" #,##0.00' }) },
    { key: 'parcelamento_descricao', label: 'Descrição parcelamento' },
    {
      key: 'valor_tarifa_consumo_com_desconto',
      label: 'Valor da tarifa de consumo c/ desconto',
      format: (_, item) => ({
        v: (item.valor_tarifa_distribuidora || 0) * (1 - (item.desconto_tarifa || 0)),
        t: 'n',
        z: '0.000000'
      })
    },
    {
      key: 'valor_tarifa_bandeira_com_desconto',
      label: 'Valor da tarifa da bandeira c/ desconto',
      format: (_, item) => ({
        v: (item.tarifa_bandeira || 0) * (1 - (item.desconto_bandeira || 0)),
        t: 'n',
        z: '0.000000'
      })
    },
    { key: 'excedente_recebido_unidade_geradora_1', label: 'Excedente UG 1' },
    { key: 'excedente_recebido_unidade_geradora_2', label: 'Excedente UG 2' },
    { key: 'excedente_recebido_unidade_geradora_3', label: 'Excedente UG 3' },
    { key: 'excedente_recebido_unidade_geradora_4', label: 'Excedente UG 4' },
    { key: 'excedente_recebido_unidade_geradora_5', label: 'Excedente UG 5' },
    { key: 'descricao_ug_1', label: 'Descrição Geradora 1' },
    { key: 'descricao_ug_2', label: 'Descrição Geradora 2' },
    { key: 'descricao_ug_3', label: 'Descrição Geradora 3' },
    { key: 'descricao_ug_4', label: 'Descrição Geradora 4' },
    { key: 'descricao_ug_5', label: 'Descrição Geradora 5' },
    { key: 'injecao_kwh_ug_1', label: 'Injeção Geradora 1', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '#,##0.00' }) },
    { key: 'injecao_kwh_ug_2', label: 'Injeção Geradora 2', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '#,##0.00' }) },
    { key: 'injecao_kwh_ug_3', label: 'Injeção Geradora 3', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '#,##0.00' }) },
    { key: 'injecao_kwh_ug_4', label: 'Injeção Geradora 4', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '#,##0.00' }) },
    { key: 'injecao_kwh_ug_5', label: 'Injeção Geradora 5', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '#,##0.00' }) },
    { key: 'credito_recebido', label: 'Crédito recebido', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '"R$" #,##0.00' }) },
    { key: 'saldo_acumulado_kwh', label: 'Saldo acumulado', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '#,##0.00' }) },
    { key: 'valor_tarifa_distribuidora', label: 'Valor tarifa consumo (distribuidora)', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '0.000000' }) },
    { key: 'tarifa_bandeira', label: 'Valor tarifa bandeira (distribuidora)', format: (v: any) => ({ v: Number(v || 0), t: 'n', z: '0.000000' }) },

    // "Emitido por/em" descrevem o boleto que está VIVO — a reemissão sobrescreve os
    // dois de propósito (19/08/2026). As três colunas abaixo saem de
    // `historico_emissoes` e recuperam quem fez o trabalho antes do cancelamento, que
    // era o que sumia do export nas 590 faturas já reemitidas (chamado do Ítalo).
    { key: 'emitido_por_nome', label: 'Emitido por' },
    { key: 'emitido_em', label: 'Emitido em', format: (v: any, item: any) => { const val = item.boleto_emissao_data || v; return val ? { v: new Date(val), t: 'd', z: 'dd/mm/yyyy hh:mm' } : ''; } },
    { key: 'primeira_emissao_por', label: '1ª emissão por', format: (_: any, item: any) => primeiraEmissao(item)?.emitido_por_nome || '' },
    { key: 'primeira_emissao_em', label: '1ª emissão em', format: (_: any, item: any) => { const val = primeiraEmissao(item)?.emitido_em; return val ? { v: new Date(val), t: 'd', z: 'dd/mm/yyyy hh:mm' } : ''; } },
    { key: 'qtd_emissoes', label: 'Nº de emissões', format: (_: any, item: any) => ({ v: emissoesDaFatura(item).length, t: 'n', z: '0' }) },
    { key: 'enviado_por_nome', label: 'Enviado por' },
    { key: 'enviado_em', label: 'Enviado em', format: (v: any) => v ? { v: new Date(v), t: 'd', z: 'dd/mm/yyyy hh:mm' } : '' },
    { key: 'cancelado_por_nome', label: 'Cancelado por' },
    { key: 'cancelado_em', label: 'Cancelado em', format: (v: any) => v ? { v: new Date(v), t: 'd', z: 'dd/mm/yyyy hh:mm' } : '' },
    { key: 'motivo_cancelamento_boleto', label: 'Motivo Cancelamento' },
  ];

  // Derived state for the modal
  const validatingFatura = useMemo(() => {
    return faturas.find(f => f.id === validatingFaturaId) || null;
  }, [faturas, validatingFaturaId]);

  const handleCurrentMonth = () => {
    const now = new Date();
    const mapMes = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
    const currentMesReferencia = `${mapMes[now.getMonth()]}/${now.getFullYear()}`;
    setSelectedMeses([currentMesReferencia]);
  };

  const getDisplayUC = (uc_codigo: string) => {
    if (!uc_codigo) return 'SEM UC';
    const normCode = uc_codigo.replace(/[.\-\s]/g, '').replace(/^0+/, '');
    if (!normCode) return uc_codigo;

    const matched = unidadesConsumidoras.find(uc => {
      const n1 = uc.numero_uc ? uc.numero_uc.replace(/[.\-\s]/g, '').replace(/^0+/, '') : '';
      const n2 = uc.outro_numero_uc ? uc.outro_numero_uc.replace(/[.\-\s]/g, '').replace(/^0+/, '') : '';
      return n1 === normCode || n2 === normCode;
    });

    if (matched && matched.numero_uc && matched.outro_numero_uc) {
      const n1 = matched.numero_uc.replace(/[.\-\s]/g, '').replace(/^0+/, '');
      const n2 = matched.outro_numero_uc.replace(/[.\-\s]/g, '').replace(/^0+/, '');
      if (n1 !== n2) {
        return `${matched.numero_uc} / ${matched.outro_numero_uc}`;
      }
    }
    return uc_codigo;
  };

  const fetchAllUcs = async () => {
    // Define the query columns
    const fullCols = COLUNAS_UC_FINANCEIRO;
    const fallbackCols = 'id, numero_uc, outro_numero_uc, nome_cliente, cpf_cnpj_cliente, titular_fatura, cpf_cnpj_titular, data_nascimento_titular, unidade_geradora, percent_rateio';

    const paginar = (cols: string) =>
      buscarTudoPorChave<any>(
        (apos, tamanho) => {
          let q = supabase
            .from('unidades_consumidoras')
            .select(cols)
            .order('id', { ascending: true })
            .limit(tamanho);
          if (apos != null) q = q.gt('id', apos);
          if (profile?.empresa_parceira_id) q = q.eq('empresa_parceira_id', profile.empresa_parceira_id);
          return q as any;
        },
        (u) => u.id,
      );

    // O fallback continua de pé para o caso de alguma coluna do `fullCols` sumir
    // do schema, mas sem o `limit(1)` de teste que antes gastava uma ida extra ao
    // banco em toda carga — só refaz a consulta se a completa realmente falhar.
    try {
      return await paginar(fullCols);
    } catch (err: any) {
      console.warn('Falling back to simplified UC query due to:', err?.message);
      try {
        return await paginar(fallbackCols);
      } catch (err2) {
        console.error('Error fetching UCs:', err2);
        return [];
      }
    }
  };

  const fetchFaturas = async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    cargasEmVooRef.current += 1;
    setCargaCheiaEmVoo(true);
    try {
      const [data, rData, riData, ucsData] = await Promise.all([
        api.faturas.list(),
        api.rateios.list(),
        api.rateios.getAllItens(),
        fetchAllUcs()
      ]);

      // Carimbo só depois que os quatro fetches voltaram: é o que o botão de
      // atualizar mostra como "atualizado às HH:MM". Em erro o catch abaixo
      // assume e o carimbo antigo continua valendo — não mente sobre frescor.
      setUltimaAtualizacao(new Date());
      setRateios((rData || []).map((r: any) => ({ ...r, mes_referencia: normalizeMesReferencia(r.mes_referencia) })));
      setRateioItens(riData || []);
      if (ucsData) setUnidadesConsumidoras(ucsData);

      const dataNormalized = data.map((fat: any) => ({ ...fat, mes_referencia: normalizeMesReferencia(fat.mes_referencia) }));

      // Auto-update status_boleto_vigor for overdue/open bills
      const updates = dataNormalized.map((f: any) => {
        // Fatura sem valor (Sem cobrança/Sem injeção) não é "Vencido" nem "Em
        // aberto" — sem este guard, esta rotina grava 'Vencido' direto no banco
        // pra fatura fantasma toda vez que a lista carrega. Mesmo guard de
        // src/lib/statusBoleto.ts (derivarStatusBoleto) e do badge abaixo.
        if (!isBoletoCalculado(f.status_fatura) || !(Number(f.valor_boleto_vigor) > 0)) return null;
        // Fatura de UC de agrupamento não vence pela própria data (#6284) — e, depois
        // que o fechamento do mês é emitido, a trava do banco recusa mudar o status
        // dela: num lote, uma recusa derrubaria a atualização das outras faturas.
        if (f.cobranca_agrupada === 'membro') return null;

        // Normalize status for check
        const currentBoletoStatus = (f.status_boleto_vigor || '').trim().toLowerCase();
        if (currentBoletoStatus === 'pago' || currentBoletoStatus === 'cancelado' || currentBoletoStatus === 'parcelado' || currentBoletoStatus === 'quitado') return null;

        // Default to current status to avoid unintended overwrites
        let correctStatus = f.status_boleto_vigor || 'Em aberto';

        if (f.vencimento_boleto) {
          const [year, month, day] = f.vencimento_boleto.split('T')[0].split('-');
          const vencimentoLocal = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          vencimentoLocal.setHours(0, 0, 0, 0);
          const hoje = new Date();
          hoje.setHours(0, 0, 0, 0);
          if (vencimentoLocal < hoje) {
            correctStatus = 'Vencido';
          } else if (!f.status_boleto_vigor || f.status_boleto_vigor === 'Vencido') {
            // If it was overdue but now it's not (e.g. date changed), or if it's empty
            correctStatus = 'Em aberto';
          }
        }

        if (f.status_boleto_vigor !== correctStatus) {
          return { id: f.id, status_boleto_vigor: correctStatus };
        }
        return null;
      }).filter(Boolean);

      if (updates.length > 0) {
        // Sync DB in background
        const vencidosIds = updates.filter(u => u!.status_boleto_vigor === 'Vencido').map(u => u!.id);
        const abertosIds = updates.filter(u => u!.status_boleto_vigor === 'Em aberto').map(u => u!.id);

        if (vencidosIds.length > 0) {
          supabase.from('faturas').update({ status_boleto_vigor: 'Vencido' }).in('id', vencidosIds).then();
        }
        if (abertosIds.length > 0) {
          supabase.from('faturas').update({ status_boleto_vigor: 'Em aberto' }).in('id', abertosIds).then();
        }

        // Apply to local state immediately
        const updatedData = dataNormalized.map((f: any) => {
          const update = updates.find(u => u!.id === f.id);
          if (update) return { ...f, status_boleto_vigor: update!.status_boleto_vigor };
          return f;
        });
        setFaturas(updatedData);
        return updatedData;
      }

      setFaturas(dataNormalized);
      return dataNormalized;
    } catch (err) {
      console.error(err);
      return [];
    } finally {
      if (!isRefresh) setLoading(false);
      cargasEmVooRef.current = Math.max(0, cargasEmVooRef.current - 1);
      if (cargasEmVooRef.current === 0) {
        setCargaCheiaEmVoo(false);
        const ucsPendentes = Array.from(ucsPendentesRef.current);
        ucsPendentesRef.current.clear();
        if (ucsPendentes.length) recarregarUcsPorId(ucsPendentes);
      }
    }
  }

  const normalizarFatura = (f: any) => ({ ...f, mes_referencia: normalizeMesReferencia(f.mes_referencia) });

  // Recarga só das faturas apontadas (evento do tempo real ou ação da Janela de
  // Cobrança). A carga cheia leva ~10s e deixava a tela parada até o F5.
  const recarregarFaturasPorId = async (ids: number[]) => {
    const respostas = await Promise.all(
      emBlocos(ids, 200).map((bloco) => supabase.from('faturas').select('*').in('id', bloco)),
    );
    const falha = respostas.find((r) => r.error)?.error;
    if (falha) throw falha;
    const encontradas = respostas.flatMap((r) => r.data || []).map(normalizarFatura);
    setFaturas((prev) => aplicarPatchFaturas(prev, encontradas, ids));
  };

  const { enfileirar: recarregarFaturas } = useFaturasRealtime({
    usuarioId: user?.id,
    pausado: cargaCheiaEmVoo,
    pausadoAgora: () => cargasEmVooRef.current > 0,
    aoAplicar: recarregarFaturasPorId,
    aoRecarregarTudo: () => { fetchFaturas(true); },
  });

  // Upload no n8n e robô criam a fatura por fora, sem id conhecido: acha pela UC.
  const recarregarFaturasDaUc = async (ucCodigos: string[]) => {
    const normalizados = Array.from(new Set(
      ucCodigos.map((u) => String(u ?? '').replace(/[.\-\s]/g, '').replace(/^0+/, '')).filter(Boolean),
    ));
    if (!normalizados.length) return;
    const { data, error } = await supabase.from('faturas').select('id, uc_codigo, status_fatura').in('uc_codigo_norm', normalizados);
    if (error) {
      console.error('[Financeiro] recarga de faturas por UC:', error);
      return;
    }
    recarregarFaturas(idsDasFaturasDaUc(faturasRef.current, data || [], normalizados));
  };

  // `unidades_consumidoras` não está no tempo real: quem altera o cadastro avisa por aqui.
  const recarregarUcsPorId = async (ids: number[]) => {
    if (cargasEmVooRef.current > 0) {
      ids.forEach((id) => ucsPendentesRef.current.add(Number(id)));
      return;
    }
    const { data, error } = await supabase.from('unidades_consumidoras').select(COLUNAS_UC_FINANCEIRO).in('id', ids);
    if (error) {
      console.error('[Financeiro] recarga de UC:', error);
      fetchFaturas(true);
      return;
    }
    const porId = new Map((data || []).map((uc: any) => [Number(uc.id), uc]));
    setUnidadesConsumidoras((prev) => prev.map((uc) => porId.get(Number(uc.id)) ?? uc));
  };

  const aplicarAlvoAtualizacao = (alvo?: AlvoAtualizacao) => {
    if (!alvo) {
      fetchFaturas(true);
      return;
    }
    if (alvo.faturaIds?.length) recarregarFaturas(alvo.faturaIds);
    if (alvo.ucCodigos?.length) recarregarFaturasDaUc(alvo.ucCodigos);
    if (alvo.ucIds?.length) recarregarUcsPorId(alvo.ucIds);
  };

  // Modais do fluxo (validar/calcular/emitir): a fatura aberta atualiza na hora —
  // inclusive na Janela de Cobrança, que fica aberta por trás — e a carga cheia
  // segue como rede de segurança para o que o modal tiver mexido além dela.
  //
  // Entre um passo e outro atualiza SÓ a fatura aberta, e na hora (solicitação 5973).
  // Antes cada passo disparava também a carga cheia (~10 mil faturas + rateios + UCs,
  // ~60 MB), e o patch da fatura ia para a fila do tempo real — que fica PAUSADA
  // durante a carga cheia. O próximo modal só abre quando `validatingFatura` muda de
  // status, então o Faturamento esperava o download inteiro a cada passo.
  //
  // A carga cheia não some: fica marcada e roda uma vez quando o fluxo fecha (efeito
  // de `validatingFaturaId` abaixo), para a lista de trás pegar o que o modal tiver
  // mexido além da fatura.
  const cargaCheiaPendenteRef = useRef(false);
  const atualizarAposFluxoDaFatura = () => {
    cargaCheiaPendenteRef.current = true;
    if (!validatingFaturaId) return;
    const id = Number(validatingFaturaId);
    recarregarFaturasPorId([id]).catch((err) => console.error('[Financeiro] recarga da fatura do fluxo:', err));
    // Carga cheia já em voo (a da abertura da tela, p. ex.) traz a foto de ANTES do
    // passo e sobrescreveria o patch; a fila reaplica quando ela terminar.
    if (cargasEmVooRef.current > 0) recarregarFaturas([id]);
  };

  React.useEffect(() => {
    if (validatingFaturaId || !cargaCheiaPendenteRef.current) return;
    cargaCheiaPendenteRef.current = false;
    fetchFaturas(true);
  }, [validatingFaturaId]);

  React.useEffect(() => {
    const handleClickOutside = () => setDropdownState(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  React.useEffect(() => {
    fetchFaturas();
  }, []);

  // Contador do card de sandbox — sai da própria lista já carregada, sem ida extra
  // ao banco: as de teste vêm junto das reais para o adm.
  React.useEffect(() => {
    if (profile?.role !== 'adm') return;
    setSandboxCount(faturas.filter(f => f.ambiente === 'sandbox').length);
  }, [profile?.role, faturas]);

  /** Carimba/descarimba a fatura como teste. Marcar é na hora; tirar a marca pede
   *  confirmação, porque devolve a fatura aos analistas e libera cobrança real. */
  const alternarSandbox = (fatura: any) => {
    setDropdownState(null);
    if (fatura.ambiente === 'sandbox') {
      setFaturaParaDesmarcar(fatura);
      return;
    }
    void gravarAmbiente(fatura, 'sandbox');
  };

  /** Update direto: a policy restritiva já só deixa adm gravar 'sandbox', e a lista
   *  se atualiza localmente para o clique responder na hora (sem esperar o refetch
   *  da base inteira). */
  const gravarAmbiente = async (fatura: any, novo: 'sandbox' | 'producao') => {
    const { error } = await supabase.from('faturas').update({ ambiente: novo }).eq('id', fatura.id);
    if (error) {
      addToast('Não foi possível alterar a marca de teste: ' + error.message, 'error');
      return;
    }
    setFaturas(prev => prev.map(f => (f.id === fatura.id ? { ...f, ambiente: novo } : f)));
    await logAction(user?.id, novo === 'sandbox' ? 'MARCAR_FATURA_TESTE' : 'DESMARCAR_FATURA_TESTE', {
      fatura_id: fatura.id,
    });
    logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'marcar_fatura_teste', origem: 'acao' });
    addToast(
      novo === 'sandbox'
        ? 'Fatura marcada como teste — saiu da lista e dos números. Ela fica no card de teste.'
        : 'Marca de teste removida: a fatura voltou para a operação real.',
      'success',
    );
  };

  // Só para rotular o card com o nome da parceira; a filtragem em si usa
  // empresa_parceira_id, que já vem em cada fatura.
  React.useEffect(() => {
    supabase
      .from('empresas_parceiras')
      .select('id, nome')
      .eq('status', 'ativo')
      .then(({ data, error }) => {
        if (error) {
          console.error('Erro ao carregar empresas parceiras:', error.message);
          return;
        }
        setEmpresasParceiras(data || []);
      });
  }, []);

  const fetchParcelamentosData = async () => {
    // Paginado: a Data API corta em 1.000 linhas e a lista de parcelas só cresce.
    try {
      const [acordos, parcelas] = await Promise.all([
        buscarTudoPorChave<any>(
          (apos, tamanho) => {
            let q = supabase
              .from('parcelamentos')
              .select('id, status, valor_total, criado_por, entrada_fatura_id, faturas_origem_snapshot')
              .order('id', { ascending: true })
              .limit(tamanho);
            if (apos != null) q = q.gt('id', apos);
            return q as any;
          },
          (a) => a.id,
        ),
        buscarTudoPorChave<any>(
          (apos, tamanho) => {
            let q = supabase
              .from('parcelamento_parcelas')
              .select('id, parcelamento_id, numero_parcela, tipo, fatura_id')
              .order('id', { ascending: true })
              .limit(tamanho);
            if (apos != null) q = q.gt('id', apos);
            return q as any;
          },
          (p) => p.id,
        ),
      ]);
      const ativos = acordos.filter((a: any) => a.status === 'ativo');
      setActiveParcelamentos({
        count: ativos.length,
        total: ativos.reduce((sum: number, p: any) => sum + Number(p.valor_total || 0), 0)
      });
      setParcelaFaturaIds(new Set(parcelas.filter((p: any) => p.fatura_id != null).map((p: any) => Number(p.fatura_id))));
      // Nome de quem fez o acordo, para a coluna "Acordo feito por" da planilha.
      await prefetchProfileNames(Array.from(new Set(acordos.map((a: any) => a.criado_por).filter(Boolean))))
        .catch(() => {});
      setDadosAcordos({ acordos, parcelas });
    } catch (err: any) {
      console.error('Erro ao carregar acordos de parcelamento:', err?.message || err);
    }
  };

  React.useEffect(() => {
    fetchParcelamentosData();
  }, []);

  /** Recarrega a lista sob demanda, sem recarregar a página.
   *
   *  Puxa faturas + parcelamentos (os cards de "Parcelados"/"Quitados" saem
   *  daí) mantendo intactos filtros, busca, ordenação, aba e paginação — o
   *  estado da tela não é tocado, só o dado por baixo. `fetchFaturas(true)`
   *  não liga o `loading`, então nada pisca: o `refreshing` local só gira o
   *  ícone e trava cliques repetidos.
   *
   *  `fetchFaturas` engole a exceção e devolve `[]` em falha de rede. Lista
   *  vazia onde antes havia faturas é, na prática, esse erro — avisa em vez de
   *  deixar o usuário achar que o banco esvaziou. */
  const handleRefreshLista = async () => {
    if (refreshing) return;
    setRefreshing(true);
    const antes = faturas.length;
    try {
      const [novas] = await Promise.all([fetchFaturas(true), fetchParcelamentosData()]);
      if (antes > 0 && (novas?.length ?? 0) === 0) {
        addToast('Não foi possível atualizar a lista. Verifique a conexão e tente de novo.', 'error');
        return;
      }
      const diff = (novas?.length ?? 0) - antes;
      addToast(
        diff > 0
          ? `Lista atualizada — ${diff} fatura${diff !== 1 ? 's' : ''} nova${diff !== 1 ? 's' : ''}.`
          : 'Lista atualizada.',
        'success',
      );
      logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'atualizar_lista', origem: 'acao' });
    } finally {
      setRefreshing(false);
    }
  };

  // Real-time lock subscription
  React.useEffect(() => {
    // Fetch current locks on mount with a single batched profile lookup
    const fetchLocks = async () => {
      const { data: locks } = await supabase
        .from('fatura_locks')
        .select('fatura_id, locked_by');
      if (!locks || locks.length === 0) return;

      // Batch-fetch all profile names at once, populating the cache
      const userIds = [...new Set(locks.map((l: any) => l.locked_by.toString()))];
      await prefetchProfileNames(userIds);

      const map: Record<number, { locked_by_user_id: string; locked_by_name: string }> = {};
      locks.forEach((l: any) => {
        map[l.fatura_id] = {
          locked_by_user_id: l.locked_by,
          locked_by_name: profileCacheRef.current[l.locked_by] || 'Outro usuário'
        };
      });
      setActiveLocks(map);
    };
    fetchLocks();

    // Subscribe to realtime changes — uses cache to avoid per-event API calls
    const channel = supabase
      .channel('fatura_locks_realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'fatura_locks' }, async (payload) => {
        const { fatura_id, locked_by } = payload.new;
        const name = await getProfileName(locked_by);
        setActiveLocks(prev => ({
          ...prev,
          [fatura_id]: { locked_by_user_id: locked_by, locked_by_name: name }
        }));
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'fatura_locks' }, async (payload) => {
        const { fatura_id, locked_by } = payload.new;
        const name = await getProfileName(locked_by);
        setActiveLocks(prev => ({
          ...prev,
          [fatura_id]: { locked_by_user_id: locked_by, locked_by_name: name }
        }));
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'fatura_locks' }, (payload) => {
        const { fatura_id } = payload.old;
        setActiveLocks(prev => {
          const next = { ...prev };
          delete next[fatura_id];
          return next;
        });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  /** Recarrega a fatura e as outras da mesma UC, aplica na lista e devolve o grupo.
   *  Falhou? Devolve null e o fluxo segue sem a checagem, como já fazia o `catch`
   *  da carga cheia (que devolvia lista vazia). */
  const buscarFaturasDaMesmaUc = async (faturaId: string): Promise<any[] | null> => {
    try {
      const { data: atual, error } = await supabase.from('faturas').select('*').eq('id', Number(faturaId)).maybeSingle();
      if (error || !atual) throw error || new Error('fatura não encontrada');
      const ucNorm = String(atual.uc_codigo ?? '').replace(/[.\-\s]/g, '').replace(/^0+/, '');
      let grupo: any[] = [atual];
      if (ucNorm) {
        const { data: daUc, error: errUc } = await supabase.from('faturas').select('*').eq('uc_codigo_norm', ucNorm);
        if (errUc) throw errUc;
        grupo = daUc?.length ? daUc : grupo;
      }
      const normalizadas = grupo.map(normalizarFatura);
      const ids = normalizadas.map((f: any) => Number(f.id));
      setFaturas((prev) => aplicarPatchFaturas(prev, normalizadas, ids));
      return normalizadas;
    } catch (err) {
      console.error('[Financeiro] recarga da UC no fluxo:', err);
      recarregarFaturas([Number(faturaId)]);
      return null;
    }
  };

  const releaseFaturaLock = async (faturaId: string | null) => {
    if (!faturaId || !user?.id) return;
    await supabase.rpc('release_fatura_lock_v2', {
      p_fatura_id: Number(faturaId),
      p_user_id: user.id
    });
  };

  // Heartbeat: keep lock alive every 90s while any modal is open
  React.useEffect(() => {
    if (!validatingFaturaId || !user?.id) return;

    const interval = setInterval(async () => {
      await supabase.rpc('heartbeat_fatura_lock_v2', {
        p_fatura_id: Number(validatingFaturaId),
        p_user_id: user.id
      });
    }, 90_000);

    // Release on tab close while modal is open
    const handleUnload = () => {
      if (!validatingFaturaId || !user?.id) return;
      const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/release_fatura_lock_v2`;
      let token = '';
      try {
        const key = Object.keys(localStorage).find(k => k.includes('auth-token'));
        if (key) token = JSON.parse(localStorage.getItem(key) || '{}')?.access_token || '';
      } catch { /* ignore */ }
      fetch(url, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', 'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY, 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ p_fatura_id: Number(validatingFaturaId), p_user_id: user.id })
      }).catch(() => { });
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [validatingFaturaId, user?.id]);

  /** Abre a fatura no passo em que ela está. Devolve se abriu de fato — a emissão em
   *  sequência da Planilha Gran usa isso para pausar em vez de esperar para sempre
   *  por uma fatura que ficou com outro usuário. */
  const handleCardClick = async (fatura: any): Promise<boolean> => {
    if (fatura.isDuplicated) {
      const duplicates = faturas.filter(f =>
        normalizeUc(f.uc_codigo || '') === normalizeUc(fatura.uc_codigo || '') &&
        normalizeMesReferencia(f.mes_referencia) === normalizeMesReferencia(fatura.mes_referencia) &&
        normalizeDate(f.data_vencimento) === normalizeDate(fatura.data_vencimento)
      );
      setDuplicatedFaturasGroup(duplicates);
      setShowDuplicateModal(true);
      return false;
    }

    if (!user?.id) {
      addToast('Usuário não autenticado.', 'error');
      return false;
    }

    // Se já tinha outra fatura aberta, libera o lock dela antes de abrir a nova
    if (validatingFaturaId && validatingFaturaId !== String(fatura.id)) {
      await releaseFaturaLock(validatingFaturaId);
      setShowValidationModal(false);
      setShowGenericModal(false);
      setValidatingFaturaId(null);
    }

    const { data, error } = await supabase.rpc('acquire_fatura_lock_v2', {
      p_fatura_id: Number(fatura.id),
      p_user_id: user.id
    });

    if (error) {
      // RPC error (network, auth, etc.) — log and proceed (fail-open)
      console.error('[FaturaLock] RPC error acquiring lock:', error);
      addToast('Erro ao verificar disponibilidade da fatura. Tente novamente.', 'error');
      return false;
    }

    const lockResult = data?.[0];
    console.log('[FaturaLock] acquire result:', lockResult);

    if (lockResult && !lockResult.success) {
      // Genuine lock conflict — another user is editing
      addToast(
        `Esta fatura está sendo editada por ${lockResult.locked_by_name || 'outro usuário'}.`,
        'warning'
      );
      return false;
    }

    setValidatingFaturaId(fatura.id);
    if (!fatura.status_fatura || fatura.status_fatura === 'Fatura disponível') {
      setShowValidationModal(true);
    } else {
      setShowGenericModal(true);
    }
    return true;
  };

  // ─── Planilha Gran: emissão em sequência ─────────────────────────────────────
  // A fila só abre a fatura da vez no fluxo de sempre (BoletoEmissionModal, com todas
  // as travas dele) e anda sozinha quando o boleto sai. A regra (quando abrir, andar,
  // pausar) está em PlanilhaGran/filaEmissaoGran.ts, com teste; aqui é só a ligação.
  const abrirDaFilaGran = async (fila: FilaGran) => {
    const atual = filaGranRegra.atualDaFila(fila);
    if (!atual) return;
    const f = faturasRef.current.find((x: any) => Number(x.id) === atual.faturaId);
    const passo = filaGranRegra.antesDeAbrir(fila, f?.status_fatura);
    if (!passo.abrir) {
      if (passo.aviso) addToast(passo.aviso, 'info');
      setFilaGran(passo.fila);
      return;
    }
    const abriu = await handleCardClick(f);
    if (!abriu) setFilaGran(q => q && filaGranRegra.naoAbriu(q));
  };

  const iniciarFilaGran = (itens: ItemFilaGran[]) => {
    if (itens.length) setFilaGran(filaGranRegra.novaFila(itens));
  };

  const pularNaFilaGran = async () => {
    const atual = filaGran && filaGranRegra.atualDaFila(filaGran);
    if (!atual) return;
    if (validatingFaturaId && String(validatingFaturaId) === String(atual.faturaId)) {
      await releaseFaturaLock(validatingFaturaId);
      setShowValidationModal(false);
      setShowGenericModal(false);
      setValidatingFaturaId(null);
    }
    setFilaGran(q => q && filaGranRegra.pular(q));
  };

  // Abre a fatura da vez. Um respiro entre uma e outra para o operador ver que o
  // boleto anterior saiu antes de a janela trocar.
  React.useEffect(() => {
    if (!filaGran) return;
    const id = filaGranRegra.faturaParaAbrir(filaGran, validatingFaturaId);
    if (id === null) return;
    const fila = filaGran;
    const t = setTimeout(() => { abrirDaFilaGran(fila); }, filaGran.pos === 0 ? 0 : 900);
    return () => clearTimeout(t);
  }, [filaGran?.pos, filaGran?.pausada, filaGran?.aberta]);

  // Acompanha a janela da fatura da vez: marca aberta, anda quando o boleto sai, pausa
  // quando o operador fecha sem emitir.
  React.useEffect(() => {
    if (!filaGran) return;
    const proxima = filaGranRegra.observar(filaGran, validatingFaturaId, validatingFatura?.status_fatura);
    if (proxima !== filaGran) setFilaGran(proxima);
  }, [filaGran, validatingFaturaId, validatingFatura?.status_fatura]);

  const empresaGran = useMemo(
    () => empresasParceiras.find(e => e.nome.trim().toLowerCase() === 'gran') ?? (empresasParceiras.length === 1 ? empresasParceiras[0] : null),
    [empresasParceiras],
  );
  const podeGerirPlanilhaGran = ['adm', 'gerente', 'analista'].includes(profile?.role || '');

  // Normaliza UC code removendo formatação e zeros à esquerda para comparação de duplicatas.
  // "161.388.012-17" e "000016138801217" viram "16138801217"
  const normalizeUc = (uc: any) => {
    if (typeof uc !== 'string' && typeof uc !== 'number') return '';
    return String(uc).replace(/[.\-\s]/g, '').replace(/^0+/, '');
  };

  // Normaliza strings e objetos de data para a porção YYYY-MM-DD
  const normalizeDate = (dateStr: any) => {
    if (!dateStr) return '';
    const str = String(dateStr).trim();
    if (str.includes('T')) {
      return str.split('T')[0];
    }
    if (str.includes(' ')) {
      return str.split(' ')[0];
    }
    return str;
  };

  // Data-calendário para comparação com inputs type=date ('YYYY-MM-DD').
  // Colunas date passam direto; timestamptz vira o dia no fuso local do navegador —
  // parsear com new Date() compara em UTC e desloca o dia em fusos negativos (Brasil).
  const toLocalDateStr = (value: any): string => {
    if (!value) return '';
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const d = new Date(s);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Range inclusivo [start, end] por dia local; sem valor na fatura = fora do range
  const inDateRange = (value: any, start: string, end: string): boolean => {
    if (!start && !end) return true;
    const d = toLocalDateStr(value);
    if (!d) return false;
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  };

  // Normaliza valores numéricos decimais arredondando para duas casas decimais
  const normalizeValue = (value: any) => {
    if (value === null || value === undefined) return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100;
  };

  // --- Filtering Logic ---
  // Duplicata é UC + mês + vencimento repetidos. A regra de quem entra na conta mora em
  // `duplicataFatura.ts` (a mesma do gatilho do banco): avulsa, excluída, cancelada e
  // "Aguardando Upload" ficam de fora. Contá-las marcava 521 grupos como duplicata em
  // 23/09/2026, dos quais 9 eram duplicata de verdade.
  const processedFaturas = useMemo(
    () => marcarDuplicatas(faturas, (f: any) =>
      `${normalizeUc(f.uc_codigo || '')}|${normalizeMesReferencia(f.mes_referencia)}|${normalizeDate(f.data_vencimento)}`
    ),
    [faturas]
  );

  // Vírgula separa termos que valem juntos — "qualquer um destes". É como o link
  // do card da rota de cobrança manda as UCs de uma parada que junta mais de uma.
  // Um termo só segue exatamente como antes (inclusive sem aparar espaços).
  const termosBusca = useMemo(() => {
    const partes = searchTerm.split(',').map(t => t.trim()).filter(Boolean);
    return (partes.length > 1 ? partes : [searchTerm]).map(t => ({
      bruto: t,
      semAcento: removeAccents(t),
      documento: normalizeCpfCnpj(t),
      digitos: cleanNumericString(t),
    }));
  }, [searchTerm]);

  const getFilteredFaturas = () => {
    return processedFaturas.filter(fatura => {
      // 1. Search Filter (nome, UC, valor e CPF/CNPJ)
      const normUc = fatura.uc_codigo ? fatura.uc_codigo.replace(/[.\-\s]/g, '').replace(/^0+/, '') : '';
      const matchedUc = normUc ? ucsMap.get(normUc) : null;

      const casaTermo = ({ bruto, semAcento, documento, digitos }: typeof termosBusca[number]) =>
        removeAccents(fatura.cliente_nome).includes(semAcento) ||
        (fatura.uc_codigo || '').includes(bruto) ||
        cleanNumericString(fatura.uc_codigo || '').includes(digitos) ||
        removeAccents(fatura.nome_cliente_boleto).includes(semAcento) ||
        nomesDaUcNaFatura(fatura, matchedUc, donosAnteriores).some(n => removeAccents(n).includes(semAcento)) ||
        (matchedUc && (
          (matchedUc.numero_uc || '').includes(bruto) ||
          cleanNumericString(matchedUc.numero_uc || '').includes(digitos) ||
          (matchedUc.outro_numero_uc || '').includes(bruto) ||
          cleanNumericString(matchedUc.outro_numero_uc || '').includes(digitos)
        )) ||
        String(fatura.valor_fatura_distribuidora).includes(bruto) ||
        (documento.length > 0 && normalizeCpfCnpj(fatura.cliente_cpf_cnpj || '').includes(documento)) ||
        (digitos.length > 0 && cleanNumericString(fatura.cliente_cpf_cnpj || '').includes(digitos));

      if (!termosBusca.some(casaTermo)) return false;


      // 2. Advanced Filters

      // Nome do Cliente
      if (filters.nome_cliente && !(fatura.cliente_nome || '').toLowerCase().includes(filters.nome_cliente.toLowerCase())) return false;

      // Status Fatura
      if (filters.status_fatura && fatura.status_fatura !== filters.status_fatura) return false;
      if (filters.etiqueta && !passaNoFiltroDeEtiqueta(etiquetasDaFatura(fatura), filters.etiqueta)) return false;
      if (filters.usina && (usinaDe(fatura)?.usina.id ?? FILTRO_SEM_USINA) !== filters.usina) return false;

      // Classificação
      if (filters.classificacao && !(fatura.classificacao || '').toLowerCase().includes(filters.classificacao.toLowerCase())) return false;

      // Tipo Ligação
      if (filters.tipo_ligacao && !(fatura.tipo_ligacao || '').toLowerCase().includes(filters.tipo_ligacao.toLowerCase())) return false;

      // Mês Referência
      if (filters.mes_referencia && !(fatura.mes_referencia || '').includes(filters.mes_referencia)) return false;

      // 3. Date Filter (Top right fallback and specific advanced filter logic)
      if (selectedMeses.length > 0) {
        const mesFatura = mesDaFaturaPorTipo(fatura, tipoMesFiltro);
        if (!mesFatura || !selectedMeses.includes(mesFatura)) {
          return false;
        }
      }

      // Created At (Importação)
      if (!inDateRange(fatura.created_at, filters.created_at_start, filters.created_at_end)) return false;

      // Vencimento (data_vencimento)
      if (!inDateRange(fatura.data_vencimento, filters.data_vencimento_start, filters.data_vencimento_end)) return false;

      // Vencimento Boleto
      if (!inDateRange(fatura.vencimento_boleto, filters.vencimento_boleto_start, filters.vencimento_boleto_end)) return false;

      // Emitido Em
      if (!inDateRange(fatura.emitido_em, filters.emitido_em_start, filters.emitido_em_end)) return false;

      // Enviado Em
      if (!inDateRange(fatura.enviado_em, filters.enviado_em_start, filters.enviado_em_end)) return false;

      // Data de Pagamento
      if (!inDateRange(fatura.data_pgto_boleto, filters.data_pgto_boleto_start, filters.data_pgto_boleto_end)) return false;

      // Valor
      if (filters.valor_min && Number(fatura.valor_fatura_distribuidora) < Number(filters.valor_min)) return false;
      if (filters.valor_max && Number(fatura.valor_fatura_distribuidora) > Number(filters.valor_max)) return false;

      // Consumo
      if (filters.consumo_min && Number(fatura.consumo_ativo_kwh) < Number(filters.consumo_min)) return false;
      if (filters.consumo_max && Number(fatura.consumo_ativo_kwh) > Number(filters.consumo_max)) return false;

      // Saldo
      if (filters.saldo_min && Number(fatura.saldo_acumulado_kwh) < Number(filters.saldo_min)) return false;
      if (filters.saldo_max && Number(fatura.saldo_acumulado_kwh) > Number(filters.saldo_max)) return false;

      // Overdue filter — mesma derivação do card e do badge (ver overdueFaturas).
      if (showOnlyOverdue) {
        if (derivarStatusBoleto(fatura) !== 'Vencido') return false;
      }

      // Paid filter
      if (showOnlyPaid) {
        if ((fatura.status_boleto_vigor || '').trim().toLowerCase() !== 'pago') return false;
      }

      // Parcelados filter
      if (showOnlyParcelados) {
        const isParcelado = fatura.parcelamento_origem_id != null ||
          (fatura.status_boleto_vigor || '').trim().toLowerCase() === 'parcelado' ||
          parcelaFaturaIds.has(Number(fatura.id));
        if (!isParcelado) return false;
      }

      // Quitados filter
      if (showOnlyQuitados) {
        if ((fatura.status_boleto_vigor || '').trim().toLowerCase() !== 'quitado') return false;
      }

      // Vigor Gestão filter
      if (showOnlyGestao) {
        if (!isGestaoFatura(fatura.uc_codigo, gestaoUcKeys)) return false;
      }

      // Espelhadas filter — usa tipo_pdf_fatura definido pelo robô de processamento
      if (espelhadasFilter !== 'none') {
        const isEspelhada = fatura.tipo_pdf_fatura === 'espelho';
        if (espelhadasFilter === 'espelhadas' && !isEspelhada) return false;
        if (espelhadasFilter === 'nao_espelhadas' && isEspelhada) return false;
      }

      // Cobrança recalculada filter — usa a mesma lógica do display (fallback para cancelado_por_nome)
      if (recalculadaFilter !== 'none') {
        const cancHist = (Array.isArray(fatura.historico_cancelamentos) && fatura.historico_cancelamentos.length > 0)
          ? fatura.historico_cancelamentos
          : (fatura.cancelado_por_nome ? [true] : []);
        const isCanceled = (fatura.status_boleto_vigor || '').trim().toLowerCase() === 'cancelado';
        const isRecalculada = cancHist.length > 0 && !isCanceled;
        const semCancelamento = !fatura.cancelado_por_nome;
        if (recalculadaFilter === 'com' && !isRecalculada) return false;
        if (recalculadaFilter === 'sem' && isRecalculada) return false;
        if (recalculadaFilter === 'sem_cancel_ou_recalc' && !semCancelamento && !isRecalculada) return false;
      }

      // Cancelamento solicitado filter
      if (cancelamentoFilter === 'com' && !fatura.cancelado_por_nome) return false;
      if (cancelamentoFilter === 'sem' && !!fatura.cancelado_por_nome) return false;

      // Avulsas filter
      if (avulsasFilter !== 'none') {
        const isAvulsa = (fatura.classificacao || '').toUpperCase() === 'AVULSO' && fatura.status_fatura !== 'Aguardando processamento';
        if (avulsasFilter === 'com' && !isAvulsa) return false;
        if (avulsasFilter === 'sem' && isAvulsa) return false;
      }

      if (showOnlySaldoRemanescente && !fatura.saldo_remanescente) return false;
      if (showOnlyBoletoUnico && !isFaturaBoletoUnico(fatura)) return false;

      // Empresa parceira filter (empresa_parceira_id null = fatura da própria Vigor)
      if (parceiraFilter === 'parceira' && !fatura.empresa_parceira_id) return false;
      if (parceiraFilter === 'vigor' && !!fatura.empresa_parceira_id) return false;

      // Duplicadas filter
      if (duplicadasFilter === 'duplicadas' && !fatura.isDuplicated) return false;
      if (duplicadasFilter === 'mantidas' && !fatura.duplicata_confirmada) return false;

      return true;
    });
  };

  // Real e teste nunca aparecem juntas: por padrão a tela mostra só as reais; com o
  // card de teste ligado, só as de teste (canceladas inclusive — cancelar não tira
  // a marca). Teste nunca entra em número nenhum.
  //   filteredFaturas   → o que a tela mostra
  //   faturasParaNumeros → o que alimenta cards, somatórios e exportação
  // A RLS já garante que quem não é adm nunca recebe uma fatura sandbox.
  let filteredFaturas = getFilteredFaturas()
    .filter(f => (sandboxFilter === 'somente') === (f.ambiente === 'sandbox'));
  const faturasParaNumeros = filteredFaturas.filter(f => f.ambiente !== 'sandbox');
  const exportData = exportKanbanFilter.length === KANBAN_STATUSES.length
    ? faturasParaNumeros
    : faturasParaNumeros.filter(f => exportKanbanFilter.includes(f.status_fatura || ''));

  const vinculoAcordoPorFatura = useMemo(
    () => vinculosDeAcordo(dadosAcordos.acordos, dadosAcordos.parcelas, faturas),
    [dadosAcordos, faturas],
  );

  // A entrada e a parcela do acordo nascem no mês em que vencem; esta linha diz
  // de que meses é a dívida que elas cobram (#6260).
  const renderNegociacao = (fatura: any) => {
    const vinculo = vinculoAcordoPorFatura.get(String(fatura.id));
    const frase = fraseNegociacao(vinculo);
    if (!frase) return null;
    return (
      <div
        className="flex items-center gap-1 mt-0.5 text-[10px] font-semibold text-indigo-700 dark:text-indigo-300"
        title={vinculo?.meses.length ? `Meses negociados: ${vinculo.meses.join(', ')}` : undefined}
      >
        <Split size={10} className="shrink-0" />
        <span className="truncate">{frase}</span>
      </div>
    );
  };

  // Sinaliza o vínculo com parcelamento para o financeiro não somar em dobro:
  // a fatura original vira "Parcelado" e cada parcela paga entra como "Pago".
  // A entrada e a parcela também dizem de que meses é a dívida que cobram: no
  // relatório do recebido elas pareciam boleto normal do mês do vencimento (#6260).
  const finalExportData = exportData.map(f => {
    const vinculo = vinculoAcordoPorFatura.get(String(f.id));
    const usina = usinaDe(f);
    return {
      ...f,
      _usina: usina?.usina.nome ?? '',
      _usina_origem: usina ? ROTULO_ORIGEM_USINA[usina.origem] : '',
      ...(ucSemFormatacao ? { uc_codigo: formatUCExport(f.uc_codigo || '') } : {}),
      _vinculo_parcelamento: rotuloVinculo(vinculo),
      _meses_negociados: vinculo ? vinculo.meses.join(', ') : '',
      _negociado_por: vinculo?.criadoPor ? profileCacheRef.current[vinculo.criadoPor] || '' : '',
    };
  });

  const sortedFaturas = [...filteredFaturas].sort((a, b) => {
    if (!sortConfig.key) return 0;

    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue === bValue) return 0;
    if (aValue === undefined || aValue === null) return 1;
    if (bValue === undefined || bValue === null) return -1;

    const comparison = aValue < bValue ? -1 : 1;
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  // --- Summary Calculations ---
  // Daqui para baixo tudo usa faturasParaNumeros: cobrança de teste não conta.
  const totalFaturasCount = faturasParaNumeros.filter(f =>
    f.status_fatura !== 'Excluído' &&
    f.status_fatura !== 'Aguardando Upload' &&
    f.status_fatura !== 'Aguardando processamento'
  ).length;
  const totalInjetado = faturasParaNumeros.reduce((acc, f) => acc + (Number(f.energia_injetada_gd1_kwh) || 0) + (Number(f.energia_injetada_gd2_kwh) || 0), 0);

  const boletosEmitidosFaturas = faturasParaNumeros.filter(f => {
    const status = (f.status_boleto_vigor || '').trim().toLowerCase();
    // Parcelado/Quitado tiveram o boleto baixado no Sicoob — tratam-se como cancelado para fins de soma
    return (f.status_fatura === 'Com boleto emitido / A enviar' || f.status_fatura === 'Cobrança enviada')
      && status !== 'cancelado' && status !== 'parcelado' && status !== 'quitado';
  });
  const totalBoletosEmitidosCount = boletosEmitidosFaturas.length;
  const totalEmitidoValue = boletosEmitidosFaturas.reduce((acc, f) => acc + (Number(f.valor_boleto_vigor) || 0), 0);

  // Total a Receber: Apenas boletos emitidos, em aberto (não pagos, vencidos, parcelados nem quitados)
  const aReceberFaturas = boletosEmitidosFaturas.filter(f => {
    const status = (f.status_boleto_vigor || '').trim().toLowerCase();
    if (status === 'pago' || status === 'parcelado' || status === 'quitado') return false;

    if (!f.vencimento_boleto) return true;
    const [year, month, day] = String(f.vencimento_boleto).split('T')[0].split('-');
    const vencimentoLocal = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    vencimentoLocal.setHours(0, 0, 0, 0);

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return vencimentoLocal >= hoje;
  });
  const totalAReceberValue = aReceberFaturas.reduce((acc, f) => acc + (Number(f.valor_boleto_vigor) || 0), 0);

  // Status Counts for Export Summary
  const countImportados = faturasParaNumeros.filter(f => f.status_fatura === 'Fatura disponível').length;
  const countValidadas = faturasParaNumeros.filter(f => f.status_fatura === 'Fatura validada').length;
  const countCalculados = faturasParaNumeros.filter(f => f.status_fatura === 'Com boleto calculado').length;
  const countEmitidos = faturasParaNumeros.filter(f => f.status_fatura === 'Com boleto emitido / A enviar').length;
  const countCobrancaEnviada = faturasParaNumeros.filter(f => f.status_fatura === 'Cobrança enviada').length;

  const exportSummary = [
    { label: 'Total de Faturas (Filtro Atual)', value: faturasParaNumeros.length },
    { label: 'Faturas Importadas (Disponíveis)', value: countImportados },
    { label: 'Faturas Validadas', value: countValidadas },
    { label: 'Boletos Calculados', value: countCalculados },
    { label: 'Boletos Emitidos', value: countEmitidos },
    { label: 'Cobranças Enviadas', value: countCobrancaEnviada },
    // Faturamento por usina (#6402): os mesmos boletos do card "Total emitido".
    ...(indiceUsinas.usinas.length > 0 ? [
      { label: '', value: '' },
      { label: 'FATURAMENTO POR USINA (boletos emitidos, R$)', value: '' },
      ...totaisPorUsina(boletosEmitidosFaturas, f => usinaDe(f)?.usina ?? null, f => Number(f.valor_boleto_vigor) || 0, indiceUsinas)
        .map(l => ({ label: `${l.nome} — ${l.quantidade} boleto${l.quantidade === 1 ? '' : 's'}`, value: Math.round(l.valor * 100) / 100 })),
      { label: 'Total', value: Math.round(totalEmitidoValue * 100) / 100 },
    ] : []),
  ];

  // Meses de faturamento que cada planilha de rateio alimenta. Mês cuja planilha
  // (a do mês anterior) ainda não subiu repete a última que existe — a Janela não
  // fica mais esperando o upload (ver src/lib/planilhaRateioVigente.ts).
  const mesesDaPlanilha = useMemo(() => mesesPorPlanilha(rateios, mesDoDia(new Date())), [rateios]);

  const pendentesCalculo = useMemo(() => {
    const pendentes: any[] = [];

    // Cache normalized months present in faturas to check if the month has "arrived" in the system
    const monthsWithFaturas = new Set(faturas.map(f => normalizeMesReferencia(f.mes_referencia)));

    // Helper to clean UC strings (removes dots, dashes, spaces, and leading zeros)
    const cleanUC = (uc?: any) => {
      if (typeof uc !== 'string' && typeof uc !== 'number') return '';
      return String(uc).replace(/[.\-\s]/g, '').replace(/^0+/, '');
    };

    // PRÉ-INDEXAÇÃO: Mapa de Faturas por UC Limpa + Mês Referência
    // Complexidade: O(Faturas)
    const faturasMap = new Map<string, any[]>();
    faturas.forEach(f => {
      if (f.classificacao === 'AVULSO') return; // ignoramos faturas avulsas

      const ucL = cleanUC(f.uc_codigo);
      if (!ucL) return;

      const mes = normalizeMesReferencia(f.mes_referencia);
      const key = `${ucL}|${mes}`;

      if (!faturasMap.has(key)) {
        faturasMap.set(key, []);
      }
      faturasMap.get(key)!.push(f);
    });

    // Iterar sobre rateioItens e buscar no mapa em O(1)
    // Complexidade: O(Rateios + RateioItens)
    const rateioNomeMap = new Map<number, string>();
    rateios.forEach(r => {
      if (r.id) rateioNomeMap.set(r.id, r.nome || r.mes_referencia);
    });

    // Um item pode alimentar mais de um mês: o seguinte ao da planilha e, enquanto a
    // planilha nova não sobe, os meses em que ela é repetida.
    const itensPorMes = rateioItens.flatMap(item =>
      (mesesDaPlanilha.get(item.rateio_id) ?? []).map(m => ({ item, expectedMonth: m.mesFatura, repetida: m.repetida, planilhaMes: m.planilhaMes }))
    );

    itensPorMes.forEach(({ item, expectedMonth, repetida, planilhaMes }) => {
      // Condition: ONLY show pending items if there's at least one fatura for that expected month already in the system
      if (!monthsWithFaturas.has(expectedMonth)) return;

      const itemUcClean = cleanUC(item.uc);
      const itemOutroUcClean = cleanUC(item.outro_numero_uc);

      // Lookup UC in ucsMap
      const ucRegistry = ucsMap.get(itemUcClean) || (itemOutroUcClean ? ucsMap.get(itemOutroUcClean) : undefined);

      const searchUc1 = cleanUC(ucRegistry?.numero_uc);
      const searchUc2 = cleanUC(ucRegistry?.outro_numero_uc);

      // Buscar faturas candidatas no Map (O(1)) por TODAS as variantes de UC do rateio.
      // Importante: uma UC pode ter faturas registradas sob mais de um código (numero_uc
      // vs outro_numero_uc) no mesmo mês — ex.: um placeholder "Aguardando Upload" criado
      // sob um código e a fatura real (já calculada/enviada) sob o outro. Se pararmos na
      // primeira variante com QUALQUER resultado, corremos o risco de travar no placeholder
      // e nunca enxergar a fatura real, fazendo a UC aparecer como pendente indevidamente.
      // Por isso juntamos os candidatos de todas as variantes antes de escolher.
      const candidateKeys = Array.from(new Set([itemUcClean, itemOutroUcClean, searchUc1, searchUc2].filter(Boolean)));
      const candidatasFatura = candidateKeys.flatMap(key => faturasMap.get(`${key}|${expectedMonth}`) || []);

      // Is it pending?
      // Defined as: No fatura exists OR exists but not yet calculated/validated/sent
      const nonPendingStatuses = [
        'Com boleto calculado',
        'Com boleto emitido / A enviar',
        'Cobrança enviada',
        'Pago',
        'Liquidado',
        'Cancelado',
        'Excluído'
      ];

      // Quando a mesma UC+mês tem mais de uma fatura cadastrada (dado duplicado,
      // ex.: placeholder "Aguardando Upload" + fatura real chegada depois), a fatura
      // já resolvida (calculada, com boleto, ou marcada como "não precisa calcular")
      // tem prioridade sobre uma ainda pendente — senão o item pode voltar a aparecer
      // como pendente dependendo da ordem em que as linhas vieram do banco.
      const faturaExistente = candidatasFatura.find((f: any) => nonPendingStatuses.includes(f.status_fatura))
        ?? candidatasFatura.find((f: any) => f.status_fatura !== 'Aguardando Upload')
        ?? candidatasFatura[0]
        ?? null;

      const isCalculada = faturaExistente && nonPendingStatuses.includes(faturaExistente.status_fatura);

      pendentes.push({
        ...item,
        expectedMonth,
        fatura: faturaExistente || null,
        isCalculada: !!isCalculada,
        rateioNome: rotuloRateioDaJanela(rateioNomeMap.get(item.rateio_id) || '', repetida),
        // Mês desta Janela montado com a planilha de outro mês (a dele ainda não subiu).
        planilhaRepetida: repetida ? planilhaMes : null
      });
    });

    const filterMes = filters.mes_referencia;

    const pendentesFiltrados = pendentes.filter(p => {
      // Apply filters if they exist. Pendente de cálculo ainda não tem boleto —
      // logo não tem vencimento nem emissão — e só se localiza pelo mês de
      // referência esperado. Com o filtro em vencimento/emissão, o card mostra
      // todos os pendentes (e avisa isso) em vez de zerar.
      if (selectedMeses.length > 0 && tipoMesFiltro === 'referencia') {
        if (!selectedMeses.includes(p.expectedMonth)) return false;
      }

      if (filterMes) {
        if (!p.expectedMonth.includes(filterMes)) return false;
      }

      return true;
    });

    // Deduplicate by UC + Month (in case of multiple rateios or items)
    const uniquePendentes = [];
    const seen = new Set();
    for (const p of pendentesFiltrados) {
      const key = `${p.uc}-${p.expectedMonth}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniquePendentes.push(p);
      }
    }

    return uniquePendentes;
  }, [rateioItens, rateios, mesesDaPlanilha, faturas, selectedMeses, tipoMesFiltro, filters.mes_referencia]);

  const totalPendenteCalculoCount = pendentesCalculo.filter(p => !p.isCalculada).length;

  const ucRateioBillingMonths = useMemo(() => {
    const cleanUC = (uc?: any) => {
      if (typeof uc !== 'string' && typeof uc !== 'number') return '';
      return String(uc).replace(/[.\-\s]/g, '').replace(/^0+/, '');
    };
    const set = new Set<string>();
    rateioItens.forEach(item => {
      // Mesmos meses da Janela: inclusive os em que a planilha está sendo repetida.
      for (const { mesFatura: billingMonth } of mesesDaPlanilha.get(item.rateio_id) ?? []) {
        if (item.uc) set.add(`${billingMonth}:${cleanUC(item.uc)}`);
        if (item.outro_numero_uc) set.add(`${billingMonth}:${cleanUC(item.outro_numero_uc)}`);
      }
    });
    return set;
  }, [rateioItens, mesesDaPlanilha]);

  const shouldAlertDescadastro = (fatura: any): boolean => {
    const cleanUC = (uc?: any) => {
      if (typeof uc !== 'string' && typeof uc !== 'number') return '';
      return String(uc).replace(/[.\-\s]/g, '').replace(/^0+/, '');
    };
    const mes = normalizeMesReferencia(fatura.mes_referencia);
    const uc = cleanUC(fatura.uc_codigo);
    if (!mes || !uc) return false;
    if (ucRateioBillingMonths.has(`${mes}:${uc}`)) return false;
    const hasInjecao = Number(fatura.energia_injetada_gd1_kwh) > 0 || Number(fatura.energia_injetada_gd2_kwh) > 0;
    const hasSaldo = Number(fatura.saldo_acumulado_kwh) > 0;
    return !hasInjecao && !hasSaldo;
  };

  if (minhaCarteira && cpfsMinhaCarteira) {
    filteredFaturas = filteredFaturas.filter(f => cpfsMinhaCarteira.has(normalizarDocumento(f.cliente_cpf_cnpj)));
  }

  const totalDescadastroCount = faturasParaNumeros.filter(f => shouldAlertDescadastro(f)).length;
  if (descadastroFilter) {
    filteredFaturas = filteredFaturas.filter(f => shouldAlertDescadastro(f));
  }

  // Fonte única (src/lib/statusBoleto.ts), a mesma do Painel Executivo, da
  // Central de Inadimplência e do badge desta tela. Esta cópia local não tinha
  // o guard de `valor_boleto_vigor > 0`, então fatura zerada com
  // `vencimento_boleto` órfão — o "pular cálculo" zera o valor e limpa o status,
  // mas não limpa a data — caía no card "Vencidos" e na lista filtrada sem
  // badge, sem boleto e sem opção de quitar. Eram 29 faturas fantasma que a
  // equipe de cobrança tentava cobrar (chamado da UC 65083301222, FABRIK LTDA).
  const overdueFaturas = faturasParaNumeros.filter(fatura => derivarStatusBoleto(fatura) === 'Vencido');

  const totalOverdueCount = overdueFaturas.length;
  const totalOverdueValue = overdueFaturas.reduce((acc, f) => acc + valorDevido(f), 0);

  const paidFaturas = faturasParaNumeros.filter(f => (f.status_boleto_vigor || '').trim().toLowerCase() === 'pago');
  const totalPaidCount = paidFaturas.length;
  const totalPaidValue = paidFaturas.reduce((acc, f) => acc + (Number(f.valor_pagamento ?? f.valor_boleto_vigor) || 0), 0);

  // Quanto dos pagos ficou com a parceira: o split que foi de fato para o Asaas
  // (divisaoBoletoParceira). O resto do total é da Vigor — o boleto da Gran não é
  // dinheiro da Gran, a maior parte dele é a fatia da Vigor na energia.
  const paidParceira = paidFaturas.filter(f => !!f.empresa_parceira_id);
  const totalPaidParceiraCount = paidParceira.length;
  const totalPaidParceiraValue = paidParceira.reduce((acc, f) => acc + (divisaoBoletoParceira(f)?.parceira ?? 0), 0);
  const totalPaidVigorValue = totalPaidValue - totalPaidParceiraValue;

  const gestaoFaturas = faturasParaNumeros.filter(f => isGestaoFatura(f.uc_codigo, gestaoUcKeys));
  const totalGestaoCount = gestaoFaturas.length;
  const totalGestaoValue = gestaoFaturas.reduce((acc, f) => acc + (Number(f.valor_boleto_vigor) || 0), 0);

  const parceladosFaturas = faturasParaNumeros.filter(f =>
    f.parcelamento_origem_id != null ||
    (f.status_boleto_vigor || '').trim().toLowerCase() === 'parcelado' ||
    parcelaFaturaIds.has(Number(f.id))
  );
  const totalParceladosCount = parceladosFaturas.length;
  const totalParceladosValue = parceladosFaturas.reduce((acc, f) => acc + (Number(f.valor_boleto_vigor) || 0), 0);

  const quitadosFaturas = filteredFaturas.filter(f => (f.status_boleto_vigor || '').trim().toLowerCase() === 'quitado');
  const totalQuitadosCount = quitadosFaturas.length;
  const totalQuitadosValue = quitadosFaturas.reduce((acc, f) => acc + (Number(f.valor_boleto_vigor) || 0), 0);

  const saldoRemanescenteFaturas = faturasParaNumeros.filter(f => !!f.saldo_remanescente);
  const totalSaldoRemanescenteCount = saldoRemanescenteFaturas.length;
  const totalSaldoRemanescenteValue = saldoRemanescenteFaturas.reduce((acc, f) => acc + (Number(f.valor_boleto_vigor) || 0), 0);

  // Boleto único da Vigor de fato (ver isFaturaBoletoUnico) e, à parte, as faturas de quem
  // aderiu que saíram sem a conta de luz (antes de 14/10/2026 o Sicoob registrava só a Vigor).
  const boletoUnicoFaturas = faturasParaNumeros.filter(isFaturaBoletoUnico);
  const totalBoletoUnicoSemLuzCount = faturasParaNumeros
    .filter(f => isFaturaDepoisDaAdesao(f) && boletoCobrouEquatorial(f) === false).length;

  const totalDuplicadasCount = filteredFaturas.filter(f => f.isDuplicated).length;
  const totalDuplicadasMantidasCount = filteredFaturas.filter(f => !!f.duplicata_confirmada).length;
  const totalEspelhadasCount = filteredFaturas.filter(f => f.tipo_pdf_fatura === 'espelho').length;
  const totalNaoEspelhadasCount = filteredFaturas.filter(f => f.tipo_pdf_fatura !== 'espelho').length;
  const calcCancHist = (f: any) =>
    (Array.isArray(f.historico_cancelamentos) && f.historico_cancelamentos.length > 0)
      ? f.historico_cancelamentos
      : (f.cancelado_por_nome ? [true] : []);

  const totalRecalculadaCount = filteredFaturas.filter(f => {
    const isCanceled = (f.status_boleto_vigor || '').trim().toLowerCase() === 'cancelado';
    return calcCancHist(f).length > 0 && !isCanceled;
  }).length;
  const totalSemRecalculadaCount = filteredFaturas.filter(f => {
    const isCanceled = (f.status_boleto_vigor || '').trim().toLowerCase() === 'cancelado';
    return !(calcCancHist(f).length > 0 && !isCanceled);
  }).length;
  const totalSemCancelOuRecalcCount = filteredFaturas.filter(f => {
    const isCanceled = (f.status_boleto_vigor || '').trim().toLowerCase() === 'cancelado';
    const isRecalculada = calcCancHist(f).length > 0 && !isCanceled;
    return !f.cancelado_por_nome || isRecalculada;
  }).length;
  const totalCancelamentoComCount = filteredFaturas.filter(f => !!f.cancelado_por_nome).length;
  const totalCancelamentoSemCount = filteredFaturas.filter(f => !f.cancelado_por_nome).length;
  // Contagens do card de parceiras: sobre filteredFaturas para acompanhar os demais
  // filtros ativos, mesmo padrão dos outros cards.
  const totalParceirasCount = filteredFaturas.filter(f => !!f.empresa_parceira_id).length;
  const totalVigorCount = filteredFaturas.filter(f => !f.empresa_parceira_id).length;
  // Com uma única parceira cadastrada o card mostra o nome dela ("Gran"); com mais de
  // uma, cai para o rótulo genérico.
  const nomeParceiraCard = empresasParceiras.length === 1 ? empresasParceiras[0].nome : 'Emp. Parceiras';
  const empresasParceirasMap = useMemo(
    () => new Map(empresasParceiras.map(e => [e.id, e.nome])),
    [empresasParceiras]
  );

  const totalAvulsasCount = filteredFaturas.filter(f => (f.classificacao || '').toUpperCase() === 'AVULSO' && f.status_fatura !== 'Aguardando processamento').length;
  const totalNaoAvulsasCount = filteredFaturas.filter(f => !((f.classificacao || '').toUpperCase() === 'AVULSO' && f.status_fatura !== 'Aguardando processamento')).length;

  // --- Actions ---
  const handleViewFatura = async (url: string) => {
    if (!url) return;
    try {
      if (url.startsWith('http')) {
        window.open(url, '_blank');
        return;
      }
      // Extract filename logic if path in storage
      // Reuse logic from previous implemention if consistent
      // Assuming url is storage path or full url.
      // If simple path:
      const { data, error } = await supabase.storage.from('faturas').createSignedUrl(url, 60);
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      } else {
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error('View error:', err);
      window.open(url, '_blank');
    }
  };

  const handleEdit = (fatura: any) => {
    setEditingFatura(fatura);
    setShowEditModal(true);
    setDropdownState(null);
  };

  const handleDeleteConfirm = async () => {
    if (!faturaToDelete || !user) return;
    if (!motivoExclusao.trim()) {
      addToast('Por favor, informe o motivo da exclusão.', 'warning');
      return;
    }

    try {
      const { error } = await supabase.from('faturas').update({
        status_fatura: 'Excluído',
        excluido_por_nome: profile?.full_name || user.email,
        excluido_em: new Date().toISOString(),
        motivo_exclusao: motivoExclusao.trim()
      }).eq('id', faturaToDelete.id);

      if (error) throw error;

      logUsageEvent({ userId: user.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'excluir_fatura', origem: 'acao' });
      await logAction(user.id, 'DELETE_INVOICE', {
        fatura_id: faturaToDelete.id,
        cliente: faturaToDelete.cliente_nome,
        uc: faturaToDelete.uc_codigo,
        motivo: motivoExclusao.trim()
      });

      setDeleteModalOpen(false);
      setSuccessModalOpen(true);
      setMotivoExclusao(''); // Reset motive
      fetchFaturas();

      setTimeout(() => {
        setSuccessModalOpen(false);
        setFaturaToDelete(null);
      }, 2000);

    } catch (error) {
      console.error('Error deleting fatura:', error);
      addToast('Erro ao excluir fatura. Tente novamente.', 'error');
    }
  };

  const handleAdd = () => {
    setEditingFatura(null);
    setShowEditModal(true);
  }

  const handleSave = () => {
    fetchFaturas();
  }

  const toggleDropdown = (e: React.MouseEvent, fatura: any) => {
    e.stopPropagation();

    if (dropdownState?.fatura.id === fatura.id) {
      setDropdownState(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownState({
      fatura,
      position: {
        top: rect.bottom + 5,
        // Menu tem 192px; em tela estreita não pode sair pela esquerda
        left: Math.max(8, rect.right - 192)
      }
    });
  };

  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  if (loading) return <LoadingSpinner label="Carregando faturas..." />;

  return (
    <>
      {/* No celular a tela deixa de ter altura fixa: a barra de filtros quebra em
          linhas e, presa em 100vh, esmagava cards e quadro para fora da tela.
          O fade-in também fica só no desktop — ele termina com `transform`, o que
          faz o `fixed` dos painéis (bottom sheet) se prender a este div e não à tela. */}
      <div className="md:animate-fade-in flex flex-col md:h-[calc(100vh-115px)] gap-3 md:gap-4 pb-24 md:pb-4 px-3 sm:px-0">

        {/* Unified Filter Bar & Header Replacement */}
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center bg-white dark:bg-gray-800 p-3 md:p-5 rounded-2xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm hover:shadow-md transition-shadow gap-2 md:gap-4 shrink-0">

          <div className="flex flex-wrap md:flex-nowrap gap-2 md:gap-3 w-full xl:w-auto items-center">
            {/* Filter Button (Replacing Title) */}
            <button
              onClick={() => setShowFilterModal(true)}
              className={`flex items-center gap-2 px-4 py-2 text-sm rounded-full font-medium transition-colors border ${activeFiltersCount > 0 ? 'bg-primary/10 border-primary text-primary' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700/60'}`}
            >
              <SlidersHorizontal size={16} />
              <span>Filtros</span>
              {activeFiltersCount > 0 && (
                <span className="bg-primary text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            {/* Cards Config Button */}
            <button
              onClick={() => setShowCardsModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-sm rounded-full font-medium transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/60"
            >
              <Layout size={16} className="text-primary" />
              <span className="hidden sm:inline">Cards</span>
            </button>

            {/* Divider */}
            <div className="hidden md:block h-6 w-px bg-gray-200 dark:bg-gray-700 mx-1"></div>

            {/* Search — no celular sobe para a primeira linha, com a largura toda */}
            <div className="relative w-full md:w-64 order-first md:order-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Buscar fatura..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-full text-sm text-gray-900 dark:text-white focus:border-primary outline-none transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500"
              />
            </div>

            {ehDoFaturamento(profile) && <button
              onClick={() => setMinhaCarteira(v => !v)}
              aria-pressed={minhaCarteira}
              title="Mostrar só os clientes da minha carteira de faturamento"
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold border whitespace-nowrap transition-colors ${minhaCarteira
                ? 'bg-primary text-white border-primary'
                : 'bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-primary/50'}`}
            >
              <Briefcase size={14} />
              <span className="hidden sm:inline">Minha carteira</span>
              {minhaCarteira && cpfsMinhaCarteira && cpfsMinhaCarteira.size === 0 && <span className="text-[10px] font-bold">(vazia)</span>}
            </button>}

            {/* Divider */}
            <div className="hidden md:block h-6 w-px bg-gray-200 dark:bg-gray-700 mx-1"></div>

            {/* View Switcher */}
            <div className="flex items-center p-1 bg-gray-100 dark:bg-gray-700/50 rounded-full border border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-full transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                title="Lista"
              >
                <List size={16} />
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`p-1.5 rounded-full transition-colors ${viewMode === 'kanban' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                title="Kanban"
              >
                <KanbanIcon size={16} />
              </button>
            </div>

            {/* Global Pagination Size Dropdown (only shows in kanban) */}
            {viewMode === 'kanban' && (
              <>
                <div className="h-6 w-px bg-gray-200 dark:bg-gray-700 mx-1 hidden xl:block"></div>
                <div className="relative">
                  <button
                    onClick={() => setShowPageSizeDropdown(!showPageSizeDropdown)}
                    className={`flex items-center gap-2 pl-3 pr-2 py-1.5 bg-white dark:bg-gray-800 border rounded-full shadow-sm hover:shadow-md transition-all duration-200 group ${showPageSizeDropdown
                      ? 'border-primary ring-2 ring-primary/20 dark:ring-primary/30'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="text-left min-w-0">
                        <p className="text-[9px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest leading-none mb-0.5">Exibir</p>
                        <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                          {globalKanbanPageSize === 'all' ? 'Todas' : `${globalKanbanPageSize} / coluna`}
                        </p>
                      </div>
                    </div>
                    <ChevronDown
                      size={14}
                      className={`text-gray-400 dark:text-gray-500 transition-transform duration-300 flex-shrink-0 ml-1 ${showPageSizeDropdown ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {/* Dropdown Panel */}
                  {showPageSizeDropdown && (
                    <>
                      <div className={SHEET_BACKDROP} onClick={() => setShowPageSizeDropdown(false)} />
                      <div className={`${SHEET_PANEL} md:w-36 bg-white dark:bg-gray-800 shadow-xl border border-gray-100 dark:border-gray-700 p-1.5 animate-fade-in`}>
                        <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest px-3 py-1.5">
                          Quantidade
                        </p>
                        {([25, 50, 100, 'all'] as const).map((val) => {
                          const label = val === 'all' ? 'Todas' : `${val} / coluna`;
                          return (
                            <button
                              key={val}
                              onClick={() => handleGlobalPageSizeChange(val)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all duration-150 flex items-center justify-between group/item mb-0.5 last:mb-0 ${globalKanbanPageSize === val
                                ? 'bg-primary/10 dark:bg-primary/15 text-primary font-bold'
                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 font-medium'
                                }`}
                            >
                              <span className="truncate">{label}</span>
                              {globalKanbanPageSize === val && (
                                <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full xl:w-auto justify-start md:justify-end">
            {/* Date Filter */}
            <div className="relative" ref={filterRef}>
              <div className="flex items-center gap-2 bg-white dark:bg-gray-800 py-1 px-2 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                <div
                  className="flex items-center gap-1.5 cursor-pointer select-none"
                  onClick={() => setIsMesFilterOpen(!isMesFilterOpen)}
                >
                  <span className={`text-[9px] font-bold uppercase tracking-tight ${tipoMesFiltro === 'referencia' ? 'text-gray-400' : 'text-primary'}`}>
                    {TIPOS_MES_FILTRO.find(t => t.key === tipoMesFiltro)?.curto}:
                  </span>
                  <div className="text-[11px] px-1.5 py-0.5 bg-gray-50 dark:bg-gray-700/50 rounded border border-gray-200 dark:border-gray-600 flex items-center gap-1 min-w-[70px] justify-between text-gray-900 dark:text-white">
                    <span className="truncate max-w-[80px]">
                      {(() => {
                        const validos = selectedMeses.filter(m => mesesDisponiveis.some(g => g.meses.some((item: any) => item.label === m)));
                        // Com um mês só, dizer QUAL: "1 mês(es)" não distingue o mês
                        // corrente de um mês de anos atrás deixado fixado aqui, e o
                        // quadro fica vazio sem a tela explicar por quê.
                        if (validos.length === 1) return validos[0];
                        return validos.length > 0 ? `${validos.length} meses` : 'Todos';
                      })()}
                    </span>
                    <ChevronDown size={12} className="text-gray-400 shrink-0" />
                  </div>
                </div>
                <div className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-0.5"></div>
                <button
                  onClick={handleCurrentMonth}
                  className="flex items-center gap-1 px-2 py-0.5 bg-primary/10 dark:bg-primary/20 text-primary text-[11px] font-bold rounded-md hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors whitespace-nowrap"
                >
                  <Calendar size={12} />
                  Atual
                </button>
              </div>

              {isMesFilterOpen && (
                <>
                {/* Fica dentro do filterRef: o clique-fora não o enxerga, então fecha pelo onClick */}
                <div className={`${SHEET_BACKDROP} md:hidden`} onClick={() => setIsMesFilterOpen(false)} />
                <div className={`${SHEET_PANEL} md:!z-[60] md:w-72 max-h-[85vh] md:max-h-none bg-white dark:bg-gray-800 shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden flex flex-col`}>
                  <div className="px-3 pt-3">
                    <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1.5">Filtrar pelo mês de</p>
                    <div className="grid grid-cols-3 gap-1 p-1 bg-gray-100 dark:bg-gray-700/60 rounded-lg">
                      {TIPOS_MES_FILTRO.map(t => (
                        <button
                          key={t.key}
                          onClick={() => setTipoMesFiltro(t.key)}
                          title={t.key === 'referencia'
                            ? 'Mês de referência da fatura da distribuidora'
                            : t.key === 'vencimento'
                              ? 'Mês de vencimento do boleto Vigor (faturas sem boleto ficam de fora)'
                              : 'Mês em que o boleto foi emitido (faturas sem boleto ficam de fora)'}
                          className={`py-1 rounded-md text-xs font-semibold transition-all ${tipoMesFiltro === t.key
                            ? 'bg-white dark:bg-gray-800 text-primary shadow-sm'
                            : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
                            }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="p-3 border-b border-gray-100 dark:border-gray-700 flex justify-between gap-2">
                    <button
                      onClick={handleMarcarTodos}
                      className="flex-1 px-2 py-1.5 text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      Todos
                    </button>
                    <button
                      onClick={handleLimparMeses}
                      className="flex-1 px-2 py-1.5 text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      Limpar
                    </button>
                  </div>
                  <div className="flex-1 min-h-0 md:flex-none md:max-h-64 overflow-y-auto p-2">
                    {mesesDisponiveis.map(agrupamento => {
                      const expanded = isYearExpanded(agrupamento.ano, agrupamento.meses);
                      return (
                        <div key={agrupamento.ano} className="mb-1.5 last:mb-0">
                          <button
                            onClick={() => toggleYearExpand(agrupamento.ano)}
                            className="w-full flex items-center justify-between text-xs font-bold text-gray-600 dark:text-gray-300 px-2 py-2.5 md:py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded transition-all"
                          >
                            <span>{agrupamento.ano}</span>
                            <span className="text-gray-400">
                              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </span>
                          </button>
                          {expanded && (
                            <div className="flex flex-col gap-0.5 mt-0.5 pl-2 border-l border-gray-100 dark:border-gray-700 ml-2">
                              {agrupamento.meses.map((m: any) => (
                                <label
                                  key={m.label}
                                  className="flex items-center gap-2 px-3 py-2.5 md:py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded cursor-pointer transition-colors"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedMeses.includes(m.label)}
                                    onChange={() => toggleMes(m.label)}
                                    className="rounded border-gray-300 text-primary focus:ring-primary/20"
                                  />
                                  <span className="text-sm text-gray-700 dark:text-gray-200">{m.label}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {mesesDisponiveis.length === 0 && (
                      <div className="p-4 text-center text-sm text-gray-500">Nenhum mês disponível</div>
                    )}
                  </div>
                  <div className="p-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <button
                      onClick={() => setIsMesFilterOpen(false)}
                      className="w-full py-2 bg-primary text-white text-sm font-bold rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      Aplicar Filtro
                    </button>
                  </div>
                </div>
                </>
              )}
            </div>

            {/* Date Filter Dropdown */}
            {(() => {
              const DATE_TYPES = [
                { key: 'created_at' as const, label: 'Importação', startKey: 'created_at_start', endKey: 'created_at_end' },
                { key: 'emitido_em' as const, label: 'Emissão', startKey: 'emitido_em_start', endKey: 'emitido_em_end' },
                { key: 'enviado_em' as const, label: 'Envio', startKey: 'enviado_em_start', endKey: 'enviado_em_end' },
                { key: 'vencimento_boleto' as const, label: 'Venc. Boleto', startKey: 'vencimento_boleto_start', endKey: 'vencimento_boleto_end' },
                { key: 'data_vencimento' as const, label: 'Venc. Fatura', startKey: 'data_vencimento_start', endKey: 'data_vencimento_end' },
                { key: 'data_pgto_boleto' as const, label: 'Pagamento', startKey: 'data_pgto_boleto_start', endKey: 'data_pgto_boleto_end' },
              ];
              const isTypeActive = (dt: typeof DATE_TYPES[number]) =>
                !!(filters[dt.startKey as keyof typeof filters] || filters[dt.endKey as keyof typeof filters]);
              const selected = DATE_TYPES.find(d => d.key === dateFilterType)!;
              const activeTypes = DATE_TYPES.filter(isTypeActive);
              // Ranges de tipos não selecionados continuam filtrando; o botão precisa refletir todos
              const hasActive = activeTypes.length > 0;
              // Com filtro ativo o botão mostrava só o nome do tipo ("Importação") —
              // idêntico ao estado neutro, que também mostra o tipo selecionado. A
              // única pista era um ponto de 4px, e um período esquecido aqui esvazia
              // o quadro inteiro sem explicação (caso do filtro de importação preso
              // em jul/2026, 11/09/2026). Agora o intervalo aparece no próprio botão.
              const ddmm = (iso: string) => {
                const [a, m, d] = (iso || '').split('-');
                return a && m && d ? `${d}/${m}` : '';
              };
              const periodoDoTipo = (t: typeof selected) => {
                const de = ddmm(filters[t.startKey as keyof typeof filters] as string);
                const ate = ddmm(filters[t.endKey as keyof typeof filters] as string);
                if (de && ate) return `${de} a ${ate}`;
                if (de) return `de ${de}`;
                if (ate) return `até ${ate}`;
                return '';
              };
              const buttonLabel = hasActive
                ? activeTypes.map(t => `${t.label}: ${periodoDoTipo(t)}`).join(' · ')
                : selected.label;
              return (
                <div className="relative" ref={dateDropdownRef}>
                  <button
                    onClick={() => setShowDateDropdown(!showDateDropdown)}
                    className={`flex items-center gap-2 pl-3 pr-2 py-1.5 bg-white dark:bg-gray-800 border rounded-full shadow-sm hover:shadow-md transition-all duration-200 group ${showDateDropdown
                      ? 'border-primary ring-2 ring-primary/20 dark:ring-primary/30'
                      : hasActive
                        ? 'border-primary/60 dark:border-primary/40'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="text-left min-w-0">
                        <p className="text-[9px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest leading-none mb-0.5">Data</p>
                        <p className={`text-xs font-semibold truncate ${hasActive ? 'max-w-[230px] text-primary' : 'max-w-[140px] text-gray-800 dark:text-gray-200'}`} title={buttonLabel}>
                          {buttonLabel}
                          {hasActive && <span className="ml-1 text-primary">•</span>}
                        </p>
                      </div>
                    </div>
                    <ChevronDown
                      size={14}
                      className={`text-gray-400 dark:text-gray-500 transition-transform duration-300 flex-shrink-0 ml-1 ${showDateDropdown ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {showDateDropdown && (
                    <>
                      <div className={SHEET_BACKDROP} onClick={() => setShowDateDropdown(false)} />
                      <div className={`${SHEET_PANEL} md:w-72 bg-white dark:bg-gray-800 shadow-xl border border-gray-100 dark:border-gray-700 p-4 md:p-3 animate-fade-in`}>
                        <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">Filtrar por</p>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {DATE_TYPES.map(dt => (
                            <button
                              key={dt.key}
                              onClick={() => setDateFilterType(dt.key)}
                              className={`relative px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${dateFilterType === dt.key
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                              title={isTypeActive(dt) ? `${dt.label}: filtro de data ativo` : dt.label}
                            >
                              {dt.label}
                              {isTypeActive(dt) && (
                                <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-white dark:ring-gray-800 ${dateFilterType === dt.key ? 'bg-amber-400' : 'bg-primary'}`}></span>
                              )}
                            </button>
                          ))}
                        </div>
                        <div className="space-y-2">
                          <div>
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">De</label>
                            <input
                              type="date"
                              value={filters[selected.startKey as keyof typeof filters]}
                              onChange={e => setFilters(prev => ({ ...prev, [selected.startKey]: e.target.value }))}
                              className="w-full px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white outline-none focus:border-primary"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Até</label>
                            <input
                              type="date"
                              value={filters[selected.endKey as keyof typeof filters]}
                              onChange={e => setFilters(prev => ({ ...prev, [selected.endKey]: e.target.value }))}
                              className="w-full px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white outline-none focus:border-primary"
                            />
                          </div>
                        </div>
                        {isTypeActive(selected) && (
                          <button
                            onClick={() => setFilters(prev => ({ ...prev, [selected.startKey]: '', [selected.endKey]: '' }))}
                            className="mt-2 w-full py-1.5 text-xs font-semibold text-gray-500 hover:text-red-500 transition-colors"
                          >
                            Limpar data
                          </button>
                        )}
                        {activeTypes.some(t => t.key !== selected.key) && (
                          <button
                            onClick={() => setFilters(prev => {
                              const next = { ...prev };
                              DATE_TYPES.forEach(dt => {
                                (next as any)[dt.startKey] = '';
                                (next as any)[dt.endKey] = '';
                              });
                              return next;
                            })}
                            className="mt-1 w-full py-1.5 text-xs font-semibold text-gray-500 hover:text-red-500 transition-colors"
                          >
                            Limpar todas as datas ({activeTypes.length})
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Clear Filters */}
            <button
              onClick={clearFilters}
              className="flex items-center px-2 py-2 text-xs font-semibold text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors group"
              title="Limpar todos os filtros"
            >
              <RotateCcw size={14} className="group-hover:rotate-[-180deg] transition-transform duration-500" />
              <span className="hidden lg:inline"></span>
            </button>

            {/* Export Dropdown */}
            <div className="relative" ref={exportDropdownRef}>
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                className={`flex items-center gap-2 pl-3 pr-2 py-1.5 bg-white dark:bg-gray-800 border rounded-full shadow-sm hover:shadow-md transition-all duration-200 ${showExportDropdown
                  ? 'border-primary ring-2 ring-primary/20 dark:ring-primary/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
              >
                <div className="text-left">
                  <p className="text-[9px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest leading-none mb-0.5">Ações</p>
                  <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">Exportar</p>
                </div>
                <ChevronDown
                  size={14}
                  className={`text-gray-400 dark:text-gray-500 transition-transform duration-300 ml-1 ${showExportDropdown ? 'rotate-180' : ''}`}
                />
              </button>

              {showExportDropdown && (
                <>
                  <div className={SHEET_BACKDROP} onClick={() => setShowExportDropdown(false)} />
                  <div className={`${SHEET_PANEL} md:w-44 bg-white dark:bg-gray-800 shadow-xl border border-gray-100 dark:border-gray-700 p-2 md:p-1.5 animate-fade-in`}>
                    <button
                      onClick={() => { setShowKanbanExportModal(true); setShowExportDropdown(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <Download size={14} className="text-primary" /> Excel
                    </button>
                    <button
                      onClick={() => { setShowKPIDashboard(true); setShowExportDropdown(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <BarChart3 size={14} className="text-primary" /> Dashboard
                    </button>
                    <button
                      onClick={() => { setShowZipModal(true); setShowExportDropdown(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <Archive size={14} className="text-primary" /> ZIP
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Atualizar lista — recarrega faturas do banco sem F5, preservando
                filtros, busca e ordenação. O horário no topo do botão diz o quanto
                o que está na tela está defasado. */}
            <button
              onClick={handleRefreshLista}
              disabled={refreshing}
              title={refreshing ? 'Atualizando...' : 'Atualizar a lista com o que está no banco agora (mantém os filtros)'}
              aria-label="Atualizar lista de faturas"
              aria-busy={refreshing}
              className="flex items-center gap-2 pl-3 pr-3.5 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 disabled:opacity-60 disabled:cursor-wait"
            >
              <div className="text-left">
                <p className="text-[9px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest leading-none mb-0.5">
                  {refreshing
                    ? 'Buscando'
                    : `Às ${ultimaAtualizacao.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}
                </p>
                <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">Atualizar</p>
              </div>
              <RefreshCw
                size={14}
                className={`text-gray-400 dark:text-gray-500 ml-1 ${refreshing ? 'animate-spin text-primary' : ''}`}
              />
            </button>

          </div>
        </div>

        {sandboxFilter === 'somente' && (
          <div className="mb-3 flex items-center justify-between gap-3 bg-amber-100 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800 rounded-xl px-4 py-2.5 shrink-0">
            <p className="text-sm text-amber-800 dark:text-amber-300 flex items-center gap-2">
              <FlaskConical size={16} className="shrink-0" />
              <span>
                Mostrando <strong>somente faturas de teste</strong>. Fora desta visão elas ficam escondidas,
                nunca entram em somatórios, inadimplência ou exportação, e só adm as enxerga.
              </span>
            </p>
            <button
              onClick={() => setSandboxFilter('ocultar')}
              className="shrink-0 px-3 py-1.5 text-xs font-bold rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors"
            >
              Voltar às faturas reais
            </button>
          </div>
        )}

        {/* Summary Cards */}
        {canViewSummary && (
          <div className="flex flex-row overflow-x-auto overflow-y-hidden pb-4 pt-1 shrink-0 gap-4 custom-scrollbar snap-x snap-mandatory px-1">
            {/* Sandbox — só adm. Faturas de teste não aparecem na lista normal;
                este card é a única porta de entrada para elas. */}
            {profile?.role === 'adm' && visibleCards.includes('sandbox') && (
              <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-all flex items-center justify-between min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${sandboxFilter === 'somente'
                ? 'bg-amber-100 border-amber-300 dark:bg-amber-950/30 dark:border-amber-800 shadow-md ring-1 ring-amber-200 dark:ring-amber-900/50'
                : 'bg-amber-50 border-amber-100 dark:bg-amber-900/10 dark:border-amber-900/30'
                }`}>
                <div>
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                    {sandboxFilter === 'somente' ? 'Só teste' : 'Faturas de teste'}
                  </p>
                  <p className="text-lg font-montserrat font-bold mt-1 text-amber-700 dark:text-amber-500">
                    {sandboxCount} fatura{sandboxCount !== 1 ? 's' : ''}
                  </p>
                  <p className="text-[11px] text-amber-600/80 dark:text-amber-400/70 mt-0.5">
                    {sandboxFilter === 'somente' ? 'fora dos somatórios' : 'escondidas da lista'}
                  </p>
                </div>
                <div
                  onClick={() => setSandboxFilter(f => f === 'somente' ? 'ocultar' : 'somente')}
                  className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 ${sandboxFilter === 'somente'
                    ? 'bg-amber-600 text-white shadow-lg shadow-amber-200 dark:shadow-amber-900/40'
                    : 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/60'
                    }`}
                  title={sandboxFilter === 'somente' ? 'Voltar às faturas reais' : 'Mostrar as faturas de teste (ficam escondidas da lista normal)'}
                >
                  <FlaskConical size={18} className={sandboxFilter === 'somente' ? 'animate-pulse' : ''} />
                </div>
              </div>
            )}

            {/* Total Faturas */}
            {visibleCards.includes('total_faturas') && (
              <div className="bg-white dark:bg-gray-800 p-4 md:p-5 rounded-2xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Total de Faturas</p>
                  <p className="text-lg font-montserrat font-bold mt-1 text-gray-900 dark:text-white">{totalFaturasCount}</p>
                </div>
                <div className="p-2.5 bg-blue-50 dark:bg-blue-900/20 text-blue-500 rounded-xl">
                  <FileText size={18} />
                </div>
              </div>
            )}

            {/* Saldo Remanescente (avulsas marcadas no modal de cobrança avulsa). Fica no
                começo da fileira: no fim do carrossel ninguém achava. */}
            {visibleCards.includes('saldo_remanescente') && (
              <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between transition-all min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${showOnlySaldoRemanescente
                ? 'bg-sky-100 border-sky-300 dark:bg-sky-950/30 dark:border-sky-800 shadow-md ring-1 ring-sky-200 dark:ring-sky-900/50'
                : 'bg-sky-50 border-sky-100 dark:bg-sky-900/10 dark:border-sky-900/30'
              }`}>
                <div>
                  <p className="text-sky-600 dark:text-sky-400 text-sm font-medium">Saldo Remanescente ({totalSaldoRemanescenteCount})</p>
                  <p className="text-lg font-montserrat font-bold mt-1 text-sky-700 dark:text-sky-500">R$ {totalSaldoRemanescenteValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div
                  onClick={() => setShowOnlySaldoRemanescente(!showOnlySaldoRemanescente)}
                  className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 ${showOnlySaldoRemanescente
                    ? 'bg-sky-600 text-white shadow-lg shadow-sky-200 dark:shadow-sky-900/40'
                    : 'bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-400 hover:bg-sky-200 dark:hover:bg-sky-900/60'
                  }`}
                  title={showOnlySaldoRemanescente ? 'Mostrar Todos' : 'Filtrar cobranças de saldo remanescente'}
                >
                  <BatteryMedium size={18} className={showOnlySaldoRemanescente ? 'animate-pulse' : ''} />
                  {!showOnlySaldoRemanescente && totalSaldoRemanescenteCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-sky-500 rounded-full animate-ping opacity-75"></div>
                  )}
                </div>
              </div>
            )}

            {/* Boleto Único: quem aderiu (UCs com unidades_consumidoras.boleto_unico) e quantos
                boletos já levam a conta de luz. O ícone filtra a lista para essas faturas. */}
            {visibleCards.includes('boleto_unico') && (
              <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between transition-all min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${showOnlyBoletoUnico
                ? 'bg-primary/10 border-primary/40 dark:bg-primary/20 dark:border-primary/50 shadow-md ring-1 ring-primary/20'
                : 'bg-primary/5 border-primary/15 dark:bg-primary/10 dark:border-primary/25'
              }`}>
                <div className="min-w-0">
                  <p className="text-primary dark:text-primary-light text-sm font-medium">Boleto Único ({boletoUnicoFaturas.length})</p>
                  <p className="text-lg font-montserrat font-bold mt-1 text-primary-dark dark:text-white">
                    {ucsBoletoUnicoCount} UC{ucsBoletoUnicoCount !== 1 ? 's' : ''} aderida{ucsBoletoUnicoCount !== 1 ? 's' : ''}
                  </p>
                  {totalBoletoUnicoSemLuzCount > 0 ? (
                    <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-0.5" title="Clientes que já tinham aderido, mas o boleto saiu cobrando só a parte da Vigor (emitidos antes da correção de 14/10/2026)">
                      {totalBoletoUnicoSemLuzCount} de quem aderiu saíram sem a conta de luz
                    </p>
                  ) : (
                    <p className="text-[11px] text-primary/80 dark:text-primary-light/80 mt-0.5">todos com a conta de luz no boleto</p>
                  )}
                </div>
                <div
                  onClick={() => setShowOnlyBoletoUnico(!showOnlyBoletoUnico)}
                  className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 shrink-0 ${showOnlyBoletoUnico
                    ? 'bg-primary text-white shadow-lg shadow-primary/30'
                    : 'bg-primary/10 dark:bg-primary/30 text-primary dark:text-primary-light hover:bg-primary/20'
                  }`}
                  title={showOnlyBoletoUnico ? 'Mostrar todas' : 'Mostrar só as faturas do boleto único da Vigor (emitidas depois da adesão)'}
                >
                  <Layers size={18} />
                </div>
              </div>
            )}

            {/* Pendente de Cálculo - NEW */}
            {visibleCards.includes('pendente_calculo') && (
              <div
                onClick={() => setShowPendentesModal(true)}
                className="bg-white dark:bg-gray-800 p-4 md:p-5 rounded-2xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full cursor-pointer hover:border-primary/50 transition-all active:scale-[0.98] group"
              >
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Pendente de cálculo</p>
                  <p className="text-lg font-montserrat font-bold mt-1 text-gray-900 dark:text-white">{totalPendenteCalculoCount || 0}</p>
                  {selectedMeses.length > 0 && tipoMesFiltro !== 'referencia' && (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5" title="Pendentes ainda não têm boleto, então não têm mês de vencimento nem de emissão">
                      todos os meses (sem boleto)
                    </p>
                  )}
                </div>
                <div className="p-2.5 bg-slate-50 dark:bg-slate-900/20 text-slate-500 rounded-xl group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                  <Calculator size={18} />
                </div>
              </div>
            )}

            {/* Boletos Emitidos */}
            {visibleCards.includes('boletos_emitidos') && (
              <div className="bg-white dark:bg-gray-800 p-4 md:p-5 rounded-2xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Boletos Emitidos</p>
                  <p className="text-lg font-montserrat font-bold mt-1 text-gray-900 dark:text-white">{totalBoletosEmitidosCount}</p>
                </div>
                <div className="p-2.5 bg-green-50 dark:bg-green-900/20 text-green-500 rounded-xl">
                  <Receipt size={18} />
                </div>
              </div>
            )}

            {/* Total Emitido */}
            {visibleCards.includes('total_emitido') && profile?.role !== 'analista' && (
              <div className="bg-white dark:bg-gray-800 p-4 md:p-5 rounded-2xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Total Emitido</p>
                  <p className="text-base font-montserrat font-bold mt-1 text-gray-900 dark:text-white">R$ {totalEmitidoValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div className="p-2.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 rounded-xl">
                  <DollarSign size={18} />
                </div>
              </div>
            )}

            {/* Total a Receber */}
            {visibleCards.includes('total_areceber') && profile?.role !== 'analista' && (
              <div className="bg-white dark:bg-gray-800 p-4 md:p-5 rounded-2xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Total a Receber</p>
                  <p className="text-base font-montserrat font-bold mt-1 text-gray-900 dark:text-white">R$ {totalAReceberValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div className="p-2.5 bg-teal-50 dark:bg-teal-900/20 text-teal-600 rounded-xl">
                  <Banknote size={18} />
                </div>
              </div>
            )}

            {/* Total Injetado */}
            {visibleCards.includes('total_injetado') && profile?.role !== 'analista' && (
              <div className="bg-white dark:bg-gray-800 p-4 md:p-5 rounded-2xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Total Injetado (kWh)</p>
                  <p className="text-lg font-montserrat font-bold mt-1 text-gray-900 dark:text-white">
                    {Number(totalInjetado).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} <span className="text-sm font-normal text-gray-400">kWh</span>
                  </p>
                </div>
                <div className="p-2.5 bg-orange-50 dark:bg-orange-900/20 text-orange-500 rounded-xl">
                  <Bolt size={18} />
                </div>
              </div>
            )}

            {/* Boletos Vencidos */}
            {visibleCards.includes('vencidos') && (
              <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between transition-all min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${showOnlyOverdue
                ? 'bg-red-100 border-red-300 dark:bg-red-950/30 dark:border-red-800 shadow-md ring-1 ring-red-200 dark:ring-red-900/50'
                : 'bg-red-50 border-red-100 dark:bg-red-900/10 dark:border-red-900/30'
                }`}>
                <div>
                  <p className="text-red-500 dark:text-red-400 text-sm font-medium">Vencidos ({totalOverdueCount})</p>
                  <p className="text-lg font-montserrat font-bold mt-1 text-red-700 dark:text-red-500">R$ {totalOverdueValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div
                  onClick={() => {
                    setShowOnlyOverdue(!showOnlyOverdue);
                  }}
                  className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 group ${showOnlyOverdue
                    ? 'bg-red-600 text-white shadow-lg shadow-red-200 dark:shadow-red-900/40'
                    : 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/60'
                    }`}
                  title={showOnlyOverdue ? 'Mostrar Todos' : 'Filtrar Vencidos'}
                >
                  <AlertTriangle size={18} className={showOnlyOverdue ? 'animate-pulse' : ''} />
                  {!showOnlyOverdue && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-75"></div>
                  )}
                </div>
              </div>
            )}

            {/* Vigor Gestão */}
            {visibleCards.includes('gestao') && (
              <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between transition-all min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${showOnlyGestao
                ? 'bg-indigo-100 border-indigo-300 dark:bg-indigo-950/30 dark:border-indigo-800 shadow-md ring-1 ring-indigo-200 dark:ring-indigo-900/50'
                : 'bg-indigo-50 border-indigo-100 dark:bg-indigo-900/10 dark:border-indigo-900/30'
                }`}>
                <div>
                  <p className="text-indigo-500 dark:text-indigo-400 text-sm font-medium">Vigor Gestão ({totalGestaoCount})</p>
                  <p className="text-lg font-montserrat font-bold mt-1 text-indigo-700 dark:text-indigo-500">R$ {totalGestaoValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div
                  onClick={() => setShowOnlyGestao(!showOnlyGestao)}
                  className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 ${showOnlyGestao
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200 dark:shadow-indigo-900/40'
                    : 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-900/60'
                    }`}
                  title={showOnlyGestao ? 'Mostrar Todos' : 'Filtrar Vigor Gestão'}
                >
                  <Building2 size={18} className={showOnlyGestao ? 'animate-pulse' : ''} />
                </div>
              </div>
            )}

            {/* Boletos Pagos */}
            {visibleCards.includes('pagos') && (
              <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between transition-all min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${showOnlyPaid
                ? 'bg-emerald-100 border-emerald-300 dark:bg-emerald-950/30 dark:border-emerald-800 shadow-md ring-1 ring-emerald-200 dark:ring-emerald-900/50'
                : 'bg-emerald-50 border-emerald-100 dark:bg-emerald-900/10 dark:border-emerald-900/30'
                }`}>
                <div>
                  <p className="text-emerald-500 dark:text-emerald-400 text-sm font-medium">Pagos ({totalPaidCount})</p>
                  <p className="text-lg font-montserrat font-bold mt-1 text-emerald-700 dark:text-emerald-500">R$ {totalPaidValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  {totalPaidParceiraCount > 0 && (
                    <div className="flex flex-col gap-0 mt-0.5">
                      <p className="text-[11px] text-emerald-600/80 dark:text-emerald-400/70" title={`Boletos da Vigor + a parte da Vigor nos ${totalPaidParceiraCount} boletos pagos da ${nomeParceiraCard}`}>
                        Ficou com a Vigor: R$ {totalPaidVigorValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-[11px] text-amber-600/80 dark:text-amber-400/70" title={`Parte da ${nomeParceiraCard} nos ${totalPaidParceiraCount} boletos pagos dela, antes da taxa do Asaas quando foi ela que emitiu`}>
                        Ficou com a {nomeParceiraCard}: R$ {totalPaidParceiraValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  )}
                  {totalQuitadosCount > 0 && (
                    <p
                      className="text-[11px] text-emerald-500/70 dark:text-emerald-400/60 mt-0.5"
                      title="Boletos quitados via cobrança avulsa/unificada — dinheiro recebido, mas contabilizado à parte no card Quitados."
                    >
                      + {totalQuitadosCount} quitado{totalQuitadosCount === 1 ? '' : 's'} (R$ {totalQuitadosValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                    </p>
                  )}
                </div>
                <div
                  onClick={() => {
                    setShowOnlyPaid(!showOnlyPaid);
                  }}
                  className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 group ${showOnlyPaid
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200 dark:shadow-emerald-900/40'
                    : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/60'
                    }`}
                  title={showOnlyPaid ? 'Mostrar Todos' : 'Filtrar Pagos'}
                >
                  <CheckCircle2 size={18} className={showOnlyPaid ? 'animate-pulse' : ''} />
                  {!showOnlyPaid && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-500 rounded-full animate-ping opacity-75"></div>
                  )}
                </div>
              </div>
            )}

            {/* Parcelados */}
            {visibleCards.includes('parcelados') && (
              <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between transition-all min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${showOnlyParcelados
                ? 'bg-indigo-100 border-indigo-300 dark:bg-indigo-950/30 dark:border-indigo-800 shadow-md ring-1 ring-indigo-200 dark:ring-indigo-900/50'
                : 'bg-indigo-50 border-indigo-100 dark:bg-indigo-900/10 dark:border-indigo-900/30'
              }`}>
                <div>
                  <p className="text-indigo-500 dark:text-indigo-400 text-sm font-medium">Parcelamentos ({totalParceladosCount})</p>
                  <p className="text-lg font-montserrat font-bold mt-1 text-indigo-700 dark:text-indigo-500">R$ {totalParceladosValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div
                  onClick={() => setShowOnlyParcelados(!showOnlyParcelados)}
                  className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 ${showOnlyParcelados
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200 dark:shadow-indigo-900/40'
                    : 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-900/60'
                  }`}
                  title={showOnlyParcelados ? 'Mostrar Todos' : 'Filtrar Parcelados'}
                >
                  <Split size={18} className={showOnlyParcelados ? 'animate-pulse' : ''} />
                  {!showOnlyParcelados && totalParceladosCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-500 rounded-full animate-ping opacity-75"></div>
                  )}
                </div>
              </div>
            )}

            {/* Quitados (pago via avulsa) */}
            {visibleCards.includes('quitados') && (
              <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between transition-all min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${showOnlyQuitados
                ? 'bg-teal-100 border-teal-300 dark:bg-teal-950/30 dark:border-teal-800 shadow-md ring-1 ring-teal-200 dark:ring-teal-900/50'
                : 'bg-teal-50 border-teal-100 dark:bg-teal-900/10 dark:border-teal-900/30'
              }`}>
                <div>
                  <p className="text-teal-500 dark:text-teal-400 text-sm font-medium">Quitados ({totalQuitadosCount})</p>
                  <p className="text-lg font-montserrat font-bold mt-1 text-teal-700 dark:text-teal-500">R$ {totalQuitadosValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div
                  onClick={() => setShowOnlyQuitados(!showOnlyQuitados)}
                  className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 ${showOnlyQuitados
                    ? 'bg-teal-600 text-white shadow-lg shadow-teal-200 dark:shadow-teal-900/40'
                    : 'bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400 hover:bg-teal-200 dark:hover:bg-teal-900/60'
                  }`}
                  title={showOnlyQuitados ? 'Mostrar Todos' : 'Filtrar Quitados'}
                >
                  <BadgeCheck size={18} className={showOnlyQuitados ? 'animate-pulse' : ''} />
                  {!showOnlyQuitados && totalQuitadosCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-teal-500 rounded-full animate-ping opacity-75"></div>
                  )}
                </div>
              </div>
            )}

            {/* Descadastrar E-mail */}
            {visibleCards.includes('descadastro_email') && (
              <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between transition-all min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${descadastroFilter
                ? 'bg-orange-100 border-orange-300 dark:bg-orange-950/30 dark:border-orange-800 shadow-md ring-1 ring-orange-200 dark:ring-orange-900/50'
                : 'bg-orange-50 border-orange-100 dark:bg-orange-900/10 dark:border-orange-900/30'
              }`}>
                <div>
                  <p className="text-orange-600 dark:text-orange-400 text-sm font-medium">Descadastrar E-mail</p>
                  <p className="text-lg font-montserrat font-bold mt-1 text-orange-700 dark:text-orange-500">
                    {totalDescadastroCount} fatura{totalDescadastroCount !== 1 ? 's' : ''}
                  </p>
                </div>
                <div
                  onClick={() => setDescadastroFilter(f => !f)}
                  className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 ${descadastroFilter
                    ? 'bg-orange-600 text-white shadow-lg shadow-orange-200 dark:shadow-orange-900/40'
                    : 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/60'
                  }`}
                  title={descadastroFilter ? 'Remover filtro' : 'Filtrar: precisam de descadastro'}
                >
                  <MailX size={18} className={descadastroFilter ? 'animate-pulse' : ''} />
                  {!descadastroFilter && totalDescadastroCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full animate-ping opacity-75"></div>
                  )}
                </div>
              </div>
            )}

            {/* Faturas Espelhadas */}
            {visibleCards.includes('espelhadas') && (
              <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between transition-all min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${espelhadasFilter === 'espelhadas'
                ? 'bg-purple-100 border-purple-300 dark:bg-purple-950/30 dark:border-purple-800 shadow-md ring-1 ring-purple-200 dark:ring-purple-900/50'
                : espelhadasFilter === 'nao_espelhadas'
                  ? 'bg-slate-100 border-slate-300 dark:bg-slate-800/60 dark:border-slate-600 shadow-md ring-1 ring-slate-200 dark:ring-slate-700/50'
                  : 'bg-purple-50 border-purple-100 dark:bg-purple-900/10 dark:border-purple-900/30'
                }`}>
                <div>
                  <p className={`text-sm font-medium ${espelhadasFilter === 'nao_espelhadas' ? 'text-slate-600 dark:text-slate-400' : 'text-purple-500 dark:text-purple-400'}`}>
                    {espelhadasFilter === 'nao_espelhadas' ? 'Não Espelhadas' : 'Espelhadas'}
                  </p>
                  <p className={`text-lg font-montserrat font-bold mt-1 ${espelhadasFilter === 'nao_espelhadas' ? 'text-slate-700 dark:text-slate-300' : 'text-purple-700 dark:text-purple-500'}`}>
                    {espelhadasFilter === 'nao_espelhadas' ? totalNaoEspelhadasCount : totalEspelhadasCount} fatura{(espelhadasFilter === 'nao_espelhadas' ? totalNaoEspelhadasCount : totalEspelhadasCount) !== 1 ? 's' : ''}
                  </p>
                </div>
                <div
                  onClick={() => setEspelhadasFilter(f => f === 'none' ? 'espelhadas' : f === 'espelhadas' ? 'nao_espelhadas' : 'none')}
                  className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 ${espelhadasFilter === 'espelhadas'
                    ? 'bg-purple-600 text-white shadow-lg shadow-purple-200 dark:shadow-purple-900/40'
                    : espelhadasFilter === 'nao_espelhadas'
                      ? 'bg-slate-600 text-white shadow-lg shadow-slate-200 dark:shadow-slate-900/40'
                      : 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/60'
                    }`}
                  title={espelhadasFilter === 'none' ? 'Filtrar: Espelhadas' : espelhadasFilter === 'espelhadas' ? 'Filtrar: Não espelhadas' : 'Remover filtro'}
                >
                  <Copy size={18} className={espelhadasFilter !== 'none' ? 'animate-pulse' : ''} />
                </div>
              </div>
            )}

            {/* Faturas Duplicadas */}
            {visibleCards.includes('duplicadas') && (
              <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between transition-all min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${duplicadasFilter === 'duplicadas'
                ? 'bg-rose-100 border-rose-300 dark:bg-rose-950/30 dark:border-rose-800 shadow-md ring-1 ring-rose-200 dark:ring-rose-900/50'
                : duplicadasFilter === 'mantidas'
                  ? 'bg-amber-100 border-amber-300 dark:bg-amber-950/30 dark:border-amber-800 shadow-md ring-1 ring-amber-200 dark:ring-amber-900/50'
                  : 'bg-rose-50 border-rose-100 dark:bg-rose-900/10 dark:border-rose-900/30'
                }`}>
                <div>
                  <p className={`text-sm font-medium ${duplicadasFilter === 'mantidas' ? 'text-amber-600 dark:text-amber-400' : 'text-rose-500 dark:text-rose-400'}`}>
                    {duplicadasFilter === 'mantidas' ? 'Duplicadas Mantidas' : 'Duplicadas'}
                  </p>
                  <p className={`text-lg font-montserrat font-bold mt-1 ${duplicadasFilter === 'mantidas' ? 'text-amber-700 dark:text-amber-500' : 'text-rose-700 dark:text-rose-500'}`}>
                    {(duplicadasFilter === 'mantidas' ? totalDuplicadasMantidasCount : totalDuplicadasCount)} fatura{(duplicadasFilter === 'mantidas' ? totalDuplicadasMantidasCount : totalDuplicadasCount) !== 1 ? 's' : ''}
                  </p>
                  {duplicadasFilter !== 'mantidas' && totalDuplicadasMantidasCount > 0 && (
                    <p
                      className="text-[11px] text-amber-600/80 dark:text-amber-400/70 mt-0.5"
                      title='Faturas confirmadas via "Manter mesmo assim" no modal de duplicatas.'
                    >
                      + {totalDuplicadasMantidasCount} mantida{totalDuplicadasMantidasCount === 1 ? '' : 's'}
                    </p>
                  )}
                </div>
                <div
                  onClick={() => setDuplicadasFilter(f => f === 'none' ? 'duplicadas' : f === 'duplicadas' ? 'mantidas' : 'none')}
                  className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 ${duplicadasFilter === 'duplicadas'
                    ? 'bg-rose-600 text-white shadow-lg shadow-rose-200 dark:shadow-rose-900/40'
                    : duplicadasFilter === 'mantidas'
                      ? 'bg-amber-600 text-white shadow-lg shadow-amber-200 dark:shadow-amber-900/40'
                      : 'bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400 hover:bg-rose-200 dark:hover:bg-rose-900/60'
                    }`}
                  title={duplicadasFilter === 'none' ? 'Filtrar: Duplicadas' : duplicadasFilter === 'duplicadas' ? 'Filtrar: Mantidas (manter mesmo assim)' : 'Remover filtro'}
                >
                  <Files size={18} className={duplicadasFilter !== 'none' ? 'animate-pulse' : ''} />
                  {duplicadasFilter === 'none' && totalDuplicadasCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-rose-500 rounded-full animate-ping opacity-75"></div>
                  )}
                </div>
              </div>
            )}

            {/* Cancelamento Solicitado */}
            {visibleCards.includes('cancelamento_solicitado') && (
              <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between transition-all min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${cancelamentoFilter === 'com'
                ? 'bg-orange-100 border-orange-300 dark:bg-orange-950/30 dark:border-orange-800 shadow-md ring-1 ring-orange-200 dark:ring-orange-900/50'
                : cancelamentoFilter === 'sem'
                  ? 'bg-green-100 border-green-300 dark:bg-green-950/30 dark:border-green-800 shadow-md ring-1 ring-green-200 dark:ring-green-900/50'
                  : 'bg-orange-50 border-orange-100 dark:bg-orange-900/10 dark:border-orange-900/30'
                }`}>
                <div>
                  <p className={`text-sm font-medium ${cancelamentoFilter === 'sem' ? 'text-green-600 dark:text-green-400' : 'text-orange-500 dark:text-orange-400'}`}>
                    {cancelamentoFilter === 'sem' ? 'Sem Cancel. Solicitado' : 'Cancel. Solicitado'}
                  </p>
                  <p className={`text-lg font-montserrat font-bold mt-1 ${cancelamentoFilter === 'sem' ? 'text-green-700 dark:text-green-500' : 'text-orange-700 dark:text-orange-500'}`}>
                    {cancelamentoFilter === 'sem' ? totalCancelamentoSemCount : totalCancelamentoComCount} fatura{(cancelamentoFilter === 'sem' ? totalCancelamentoSemCount : totalCancelamentoComCount) !== 1 ? 's' : ''}
                  </p>
                </div>
                <div
                  onClick={() => setCancelamentoFilter(f => f === 'none' ? 'com' : f === 'com' ? 'sem' : 'none')}
                  className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 ${cancelamentoFilter === 'com'
                    ? 'bg-orange-600 text-white shadow-lg shadow-orange-200 dark:shadow-orange-900/40'
                    : cancelamentoFilter === 'sem'
                      ? 'bg-green-600 text-white shadow-lg shadow-green-200 dark:shadow-green-900/40'
                      : 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/60'
                    }`}
                  title={cancelamentoFilter === 'none' ? 'Filtrar: Com cancelamento' : cancelamentoFilter === 'com' ? 'Filtrar: Sem cancelamento' : 'Remover filtro'}
                >
                  <XCircle size={18} className={cancelamentoFilter !== 'none' ? 'animate-pulse' : ''} />
                  {cancelamentoFilter === 'none' && totalCancelamentoComCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full animate-ping opacity-75"></div>
                  )}
                </div>
              </div>
            )}

            {/* Cobrança Recalculada */}
            {visibleCards.includes('recalculada') && (() => {
              const cardLabel =
                recalculadaFilter === 'com' ? 'Cobr. Recalculada' :
                  recalculadaFilter === 'sem_cancel_ou_recalc' ? 'Sem Cancel. + Recalc.' :
                    recalculadaFilter === 'sem' ? 'Sem Recálculo' :
                      'Cobr. Recalculada';
              const cardCount =
                recalculadaFilter === 'sem' ? totalSemRecalculadaCount :
                  recalculadaFilter === 'sem_cancel_ou_recalc' ? totalSemCancelOuRecalcCount :
                    totalRecalculadaCount;
              const isActive = recalculadaFilter !== 'none';
              const bgActive =
                recalculadaFilter === 'com' ? 'bg-teal-100 border-teal-300 dark:bg-teal-950/30 dark:border-teal-800 shadow-md ring-1 ring-teal-200 dark:ring-teal-900/50' :
                  recalculadaFilter === 'sem_cancel_ou_recalc' ? 'bg-emerald-100 border-emerald-300 dark:bg-emerald-950/30 dark:border-emerald-800 shadow-md ring-1 ring-emerald-200 dark:ring-emerald-900/50' :
                    recalculadaFilter === 'sem' ? 'bg-slate-100 border-slate-300 dark:bg-slate-800/60 dark:border-slate-600 shadow-md ring-1 ring-slate-200 dark:ring-slate-700/50' :
                      'bg-teal-50 border-teal-100 dark:bg-teal-900/10 dark:border-teal-900/30';
              const textColor =
                recalculadaFilter === 'sem' ? 'text-slate-600 dark:text-slate-400' :
                  recalculadaFilter === 'sem_cancel_ou_recalc' ? 'text-emerald-600 dark:text-emerald-400' :
                    'text-teal-600 dark:text-teal-400';
              const valueColor =
                recalculadaFilter === 'sem' ? 'text-slate-700 dark:text-slate-300' :
                  recalculadaFilter === 'sem_cancel_ou_recalc' ? 'text-emerald-700 dark:text-emerald-500' :
                    'text-teal-700 dark:text-teal-500';
              const btnColor =
                recalculadaFilter === 'com' ? 'bg-teal-600 text-white shadow-lg shadow-teal-200 dark:shadow-teal-900/40' :
                  recalculadaFilter === 'sem_cancel_ou_recalc' ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200 dark:shadow-emerald-900/40' :
                    recalculadaFilter === 'sem' ? 'bg-slate-600 text-white shadow-lg shadow-slate-200 dark:shadow-slate-900/40' :
                      'bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400 hover:bg-teal-200 dark:hover:bg-teal-900/60';
              const titleHint =
                recalculadaFilter === 'none' ? 'Clique: só recalculadas → clique: sem cancel. + recalc. → clique: sem recálculo → clique: limpar' :
                  recalculadaFilter === 'com' ? 'Filtrar: Sem cancel. + Recalculadas' :
                    recalculadaFilter === 'sem_cancel_ou_recalc' ? 'Filtrar: Sem recálculo' :
                      'Remover filtro';
              return (
                <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between transition-all min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${bgActive}`}>
                  <div>
                    <p className={`text-sm font-medium ${textColor}`}>{cardLabel}</p>
                    <p className={`text-lg font-montserrat font-bold mt-1 ${valueColor}`}>
                      {cardCount} fatura{cardCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div
                    onClick={() => setRecalculadaFilter(f =>
                      f === 'none' ? 'com' :
                        f === 'com' ? 'sem_cancel_ou_recalc' :
                          f === 'sem_cancel_ou_recalc' ? 'sem' :
                            'none'
                    )}
                    className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 ${btnColor}`}
                    title={titleHint}
                  >
                    <RefreshCw size={18} className={isActive ? 'animate-spin-slow' : ''} />
                    {!isActive && totalRecalculadaCount > 0 && (
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-teal-500 rounded-full animate-ping opacity-75"></div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Cobranças Avulsas */}
            {visibleCards.includes('avulsas') && (
              <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between transition-all min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${avulsasFilter === 'com'
                ? 'bg-amber-100 border-amber-300 dark:bg-amber-950/30 dark:border-amber-800 shadow-md ring-1 ring-amber-200 dark:ring-amber-900/50'
                : avulsasFilter === 'sem'
                  ? 'bg-cyan-100 border-cyan-300 dark:bg-cyan-950/30 dark:border-cyan-800 shadow-md ring-1 ring-cyan-200 dark:ring-cyan-900/50'
                  : 'bg-amber-50 border-amber-100 dark:bg-amber-900/10 dark:border-amber-900/30'
                }`}>
                <div>
                  <p className={`text-sm font-medium ${avulsasFilter === 'sem' ? 'text-cyan-600 dark:text-cyan-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    {avulsasFilter === 'sem' ? 'Sem Cobr. Avulsa' : 'Cobranças Avulsas'}
                  </p>
                  <p className={`text-lg font-montserrat font-bold mt-1 ${avulsasFilter === 'sem' ? 'text-cyan-700 dark:text-cyan-500' : 'text-amber-700 dark:text-amber-500'}`}>
                    {avulsasFilter === 'sem' ? totalNaoAvulsasCount : totalAvulsasCount} fatura{(avulsasFilter === 'sem' ? totalNaoAvulsasCount : totalAvulsasCount) !== 1 ? 's' : ''}
                  </p>
                </div>
                <div
                  onClick={() => setAvulsasFilter(f => f === 'none' ? 'com' : f === 'com' ? 'sem' : 'none')}
                  className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 ${avulsasFilter === 'com'
                    ? 'bg-amber-600 text-white shadow-lg shadow-amber-200 dark:shadow-amber-900/40'
                    : avulsasFilter === 'sem'
                      ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-200 dark:shadow-cyan-900/40'
                      : 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/60'
                    }`}
                  title={avulsasFilter === 'none' ? 'Filtrar: Com cobrança avulsa' : avulsasFilter === 'com' ? 'Filtrar: Sem cobrança avulsa' : 'Remover filtro'}
                >
                  <Zap size={18} className={avulsasFilter !== 'none' ? 'animate-pulse' : ''} />
                  {avulsasFilter === 'none' && totalAvulsasCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full animate-ping opacity-75"></div>
                  )}
                </div>
              </div>
            )}

            {/* Empresas Parceiras (Gran) */}
            {visibleCards.includes('parceiras') && (
              <div className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between transition-all min-w-[200px] w-[200px] md:min-w-[220px] md:w-[220px] shrink-0 snap-start h-full ${parceiraFilter === 'parceira'
                ? 'bg-orange-100 border-orange-300 dark:bg-orange-950/30 dark:border-orange-800 shadow-md ring-1 ring-orange-200 dark:ring-orange-900/50'
                : parceiraFilter === 'vigor'
                  ? 'bg-emerald-100 border-emerald-300 dark:bg-emerald-950/30 dark:border-emerald-800 shadow-md ring-1 ring-emerald-200 dark:ring-emerald-900/50'
                  : 'bg-orange-50 border-orange-100 dark:bg-orange-900/10 dark:border-orange-900/30'
                }`}>
                <div>
                  <p className={`text-sm font-medium ${parceiraFilter === 'vigor' ? 'text-emerald-600 dark:text-emerald-400' : 'text-orange-600 dark:text-orange-400'}`}>
                    {parceiraFilter === 'vigor' ? 'Só Vigor' : nomeParceiraCard}
                  </p>
                  <p className={`text-lg font-montserrat font-bold mt-1 ${parceiraFilter === 'vigor' ? 'text-emerald-700 dark:text-emerald-500' : 'text-orange-700 dark:text-orange-500'}`}>
                    {parceiraFilter === 'vigor' ? totalVigorCount : totalParceirasCount} fatura{(parceiraFilter === 'vigor' ? totalVigorCount : totalParceirasCount) !== 1 ? 's' : ''}
                  </p>
                </div>
                <div
                  onClick={() => (parceiraFilter === 'none' ? setShowEscolhaGran(true) : setParceiraFilter('none'))}
                  className={`p-2.5 rounded-xl relative cursor-pointer transition-all hover:scale-110 active:scale-95 ${parceiraFilter === 'parceira'
                    ? 'bg-orange-600 text-white shadow-lg shadow-orange-200 dark:shadow-orange-900/40'
                    : parceiraFilter === 'vigor'
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200 dark:shadow-emerald-900/40'
                      : 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/60'
                    }`}
                  title={parceiraFilter === 'none' ? `Faturas da ${nomeParceiraCard}: quadro filtrado ou planilha` : 'Remover filtro'}
                >
                  <Handshake size={18} className={parceiraFilter !== 'none' ? 'animate-pulse' : ''} />
                  {parceiraFilter === 'none' && totalParceirasCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full animate-ping opacity-75"></div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        {viewMode === 'list' ? (
          <div className="bg-white dark:bg-gray-800/40 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden flex flex-col shrink-0 h-[75svh] md:h-auto md:shrink md:flex-1 md:min-h-0">
            {/* Lista compacta do celular — a tabela tem 10 colunas e min-w de 1000px,
                o que no telefone vira rolagem lateral sem fim. Mesmos dados e ações. */}
            <div className="md:hidden flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/50">
              {sortedFaturas.map(fatura => (
                <div
                  key={fatura.id}
                  onClick={() => handleCardClick(fatura)}
                  className={`flex items-start gap-2 px-3 py-3 active:bg-gray-50 dark:active:bg-gray-700/50 cursor-pointer ${fatura.isDuplicated ? 'bg-red-50/50 dark:bg-red-900/10' : destaqueEtiqueta(fatura, 'fundo')}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-bold text-primary bg-primary/5 px-1.5 rounded truncate">
                        {getDisplayUC(fatura.uc_codigo)}
                      </span>
                      <span className="text-[11px] text-gray-500 dark:text-gray-400 shrink-0">{fatura.mes_referencia}</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900 dark:text-white truncate mt-0.5">{fatura.cliente_nome}</p>
                    {renderNegociacao(fatura)}
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      {renderEtiquetas(fatura)}
                      {renderUsina(fatura)}
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${getStatusColor(fatura.status_fatura || 'Fatura disponível')}`}>
                        {fatura.status_fatura || 'Fatura disponível'}
                      </span>
                      {getBoletoBadge(fatura)}
                      {fatura.ambiente === 'sandbox' && <EtiquetaSandbox onRemover={() => alternarSandbox(fatura)} />}
                      {fatura.empresa_parceira_id && (
                        <span className="flex items-center gap-1 text-[10px] font-bold text-orange-700 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400 border border-orange-300 dark:border-orange-700 px-1.5 py-0.5 rounded">
                          <Handshake size={11} />
                          {empresasParceirasMap.get(fatura.empresa_parceira_id) || 'Parceira'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-end justify-between gap-2 mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                      <span>
                        Venc.: {formatDate(isBoletoCalculado(fatura.status_fatura) && fatura.vencimento_boleto ? fatura.vencimento_boleto : fatura.data_vencimento)}
                      </span>
                      <span className="text-right">
                        <span className="block text-gray-900 dark:text-white font-bold text-xs">
                          R$ {Number(fatura.valor_fatura_distribuidora || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                        {isBoletoCalculado(fatura.status_fatura) && (
                          <span className="block text-primary font-bold">
                            Bol: R$ {Number(fatura.valor_boleto_vigor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => toggleDropdown(e, fatura)}
                    aria-label="Ações da fatura"
                    className={`p-2 -mr-1 rounded-full shrink-0 ${dropdownState?.fatura.id === fatura.id ? 'text-primary bg-gray-50 dark:bg-gray-700' : 'text-gray-400'}`}
                  >
                    <MoreVertical size={16} />
                  </button>
                </div>
              ))}
              {sortedFaturas.length === 0 && (
                <div className="p-12 text-center text-gray-400 dark:text-gray-500">
                  <p>Nenhuma fatura encontrada.</p>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="hidden md:block flex-1 overflow-auto">
              <table className="w-full text-left border-collapse min-w-[1000px]">
                <thead className="sticky top-0 z-10 shadow-sm">
                  <tr className="bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-400 text-[10px] font-bold uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-4 w-80 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('cliente_nome')}>
                      <div className="flex items-center gap-1">
                        UC / Cliente
                        {sortConfig.key === 'cliente_nome' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </div>
                    </th>
                    <th className="px-4 py-4 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('status_fatura')}>
                      <div className="flex items-center gap-1">
                        Status
                        {sortConfig.key === 'status_fatura' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </div>
                    </th>
                    <th className="px-4 py-4 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('created_at')}>
                      <div className="flex items-center gap-1">
                        Disponível em
                        {sortConfig.key === 'created_at' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </div>
                    </th>
                    <th className="px-4 py-4 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('data_vencimento')}>
                      <div className="flex items-center gap-1">
                        Vencimento
                        {sortConfig.key === 'data_vencimento' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </div>
                    </th>
                    <th className="px-4 py-4 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('valor_fatura_distribuidora')}>
                      <div className="flex items-center gap-1">
                        Valor da fatura
                        {sortConfig.key === 'valor_fatura_distribuidora' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </div>
                    </th>
                    <th className="px-4 py-4 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('mes_referencia')}>
                      <div className="flex items-center gap-1">
                        Mês Referência
                        {sortConfig.key === 'mes_referencia' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </div>
                    </th>
                    <th className="px-4 py-4 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('consumo_ativo_kwh')}>
                      <div className="flex items-center gap-1">
                        Consumo (kWh)
                        {sortConfig.key === 'consumo_ativo_kwh' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </div>
                    </th>
                    <th className="px-4 py-4 w-56 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('classificacao')}>
                      <div className="flex items-center gap-1">
                        Classificação
                        {sortConfig.key === 'classificacao' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </div>
                    </th>
                    <th className="px-4 py-4">Fatura PDF</th>
                    <th className="px-4 py-4 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {sortedFaturas.map(fatura => (
                    <tr
                      key={fatura.id}
                      onClick={() => handleCardClick(fatura)}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer ${fatura.isDuplicated ? 'bg-red-50/50 dark:bg-red-900/10' : destaqueEtiqueta(fatura, 'fundo')}`}
                    >
                      {/* UC / Cliente */}
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-[10px] font-bold text-primary bg-primary/5 px-1.5 rounded w-fit mb-0.5">
                            {getDisplayUC(fatura.uc_codigo)}
                          </span>
                          <div className="mb-1 empty:hidden">{renderEtiquetas(fatura)}</div>
                          <span className="text-sm font-bold text-gray-900 dark:text-white">
                            {fatura.cliente_nome}
                          </span>
                          <div className="mt-1 empty:hidden">{renderUsina(fatura)}</div>
                        </div>
                      </td>

                      {/* Status Fatura */}
                      <td className="px-2 py-4">
                        <div className="flex flex-col items-start gap-1">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusColor(fatura.status_fatura || 'Fatura disponível')}`}>
                            {fatura.status_fatura || 'Fatura disponível'}
                          </span>
                          {getBoletoBadge(fatura)}
                          {fatura.ambiente === 'sandbox' && <EtiquetaSandbox onRemover={() => alternarSandbox(fatura)} />}
                          {fatura.empresa_parceira_id && (
                            <span className="flex items-center gap-1 text-[10px] font-bold text-orange-700 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400 border border-orange-300 dark:border-orange-700 px-1.5 py-0.5 rounded">
                              <Handshake size={11} />
                              {empresasParceirasMap.get(fatura.empresa_parceira_id) || 'Parceira'}
                            </span>
                          )}
                          {shouldAlertDescadastro(fatura) && (
                            <span className="flex items-center gap-1 text-[10px] font-bold text-orange-700 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400 px-1.5 py-0.5 rounded">
                              <AlertTriangle size={11} /> Descadastrar e-mail
                            </span>
                          )}
                          {/* Segunda fatura do mês aceita de propósito: o motivo fica à mão de quem calcula. */}
                          {fatura.duplicata_confirmada && (
                            <span
                              title={fatura.duplicata_motivo
                                ? `${fatura.duplicata_motivo}${fatura.duplicata_confirmada_por_nome ? ` — ${fatura.duplicata_confirmada_por_nome}` : ''}`
                                : 'Duplicata confirmada como legítima'}
                              className="flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded"
                            >
                              <Files size={11} /> Duplicata mantida
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Disponível em */}
                      <td className="px-4 py-4 text-gray-500 dark:text-gray-400 text-sm">
                        {formatDate(fatura.created_at)}
                      </td>

                      {/* Vencimento */}
                      <td className="px-4 py-4 text-gray-900 dark:text-white text-sm font-medium">
                        {formatDate(fatura.data_vencimento)}
                      </td>

                      {/* Valor */}
                      <td className="px-4 py-4 text-sm font-bold text-gray-900 dark:text-white">
                        <div className="flex flex-col">
                          <span>R$ {Number(fatura.valor_fatura_distribuidora || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                          {isBoletoCalculado(fatura.status_fatura) && (() => {
                            // O que o boleto cobra de fato: no boleto único, Vigor + Equatorial (#6302).
                            const cobrado = valorCobradoBoleto(fatura);
                            const comJuros = Number(fatura.valor_pagamento) > cobrado + 0.009;
                            return (fatura.status_boleto_vigor || '').trim().toLowerCase() === 'pago' && fatura.valor_pagamento != null ? (
                              <span className="flex flex-col gap-0.5 mt-0.5">
                                {comJuros && (
                                  <span className="text-[10px] text-gray-400 font-normal line-through">
                                    Bol: R$ {cobrado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                  </span>
                                )}
                                <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                                  R$ {Number(fatura.valor_pagamento).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                  {comJuros && (
                                    <span className="text-[9px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-1 py-0.5 rounded font-semibold">c/ juros</span>
                                  )}
                                </span>
                              </span>
                            ) : (
                              <span className="text-[11px] text-primary mt-0.5 font-bold">
                                Bol: R$ {cobrado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </span>
                            );
                          })()}
                        </div>
                      </td>

                      {/* Mês Referência */}
                      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                        {fatura.mes_referencia}
                        {renderNegociacao(fatura)}
                      </td>

                      {/* Consumo */}
                      <td className="px-4 py-4 text-sm font-medium text-gray-700 dark:text-gray-200">
                        {fatura.consumo_ativo_kwh || '-'}
                      </td>

                      {/* Classificação */}
                      <td className="px-4 py-4 text-[11px] max-w-[6rem] text-gray-900 dark:text-gray-300" title={(fatura.classificacao || '').split(/\s*[\u002D\u2013\u2014]\s*/)[0]}>
                        {(fatura.classificacao || '-').split(/\s*[\u002D\u2013\u2014]\s*/)[0]}
                      </td>

                      {/* Fatura PDF */}
                      <td className="px-3 py-4">
                        {fatura.pdf_fatura ? (
                          <button
                            onClick={() => handleViewFatura(fatura.pdf_fatura)}
                            className="text-primary hover:text-primary/80 flex items-center gap-1 text-xs font-bold bg-transparent border-none p-0 cursor-pointer"
                          >
                            <Eye size={14} /> Visualizar
                          </button>
                        ) : <span className="text-gray-300 dark:text-gray-600 text-xs">-</span>}
                      </td>

                      {/* Ações */}
                      <td className="px-4 py-4 text-right relative">
                        <button
                          onClick={(e) => toggleDropdown(e, fatura)}
                          className={`p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full ${dropdownState?.fatura.id === fatura.id ? 'text-primary bg-gray-50 dark:bg-gray-700' : 'text-gray-400 hover:text-primary'}`}
                        >
                          <MoreVertical size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sortedFaturas.length === 0 && (
                <div className="p-12 text-center text-gray-400 dark:text-gray-500">
                  <p>Nenhuma fatura encontrada.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          // No celular: altura própria (a tela não tem mais altura fixa) e uma coluna
          // por vez, com encaixe na rolagem lateral.
          <div className="flex gap-3 md:gap-4 overflow-x-auto pb-4 shrink-0 h-[75svh] md:h-auto md:shrink md:flex-1 md:min-h-0 snap-x snap-mandatory md:snap-none">
            {/* Kanban Columns */}
            {['Fatura disponível', 'Fatura validada', 'Com boleto calculado', 'Com boleto emitido / A enviar', 'Cobrança enviada'].map(status => {
              const isEmissaoFilterActive = !!(filters.emitido_em_start || filters.emitido_em_end);
              const columnFaturas = filteredFaturas
                .filter(f => {
                  // Fatura de agrupamento (Prefeitura) sai de "calculado" e "a enviar"; com o malote
                  // entregue, vira o card da Secretaria abaixo (lib/colunaDoQuadro.ts).
                  const coluna = colunaDoQuadro(f, malotesEntregues);
                  if (coluna === null) return false;
                  // When filtering by emission date, keep cancelled boletos in the emitido column
                  // so the historical count reflects how many were actually emitted that day
                  if (isEmissaoFilterActive && f.cancelado_em && f.emitido_em) {
                    return status === 'Com boleto emitido / A enviar';
                  }
                  return coluna === status;
                })
                .sort((a, b) => {
                  if (!a.data_vencimento) return 1;
                  if (!b.data_vencimento) return -1;
                  return a.data_vencimento < b.data_vencimento ? -1 : (a.data_vencimento > b.data_vencimento ? 1 : 0);
                });

              const pageSize = globalKanbanPageSize;
              const totalItems = columnFaturas.length;
              // A contagem da coluna é operacional: quem lê o quadro quer saber quantas
              // faturas de verdade tem ali. As de teste aparecem em chip separado.
              const testesNaColuna = columnFaturas.filter(f => f.ambiente === 'sandbox').length;
              // Um card por Secretaria com malote entregue, no lugar dos cards das UCs dela.
              const malotesNaColuna = status === COLUNA_DOS_MALOTES ? malotesDaLista(filteredFaturas, malotesEntregues) : [];
              const itensReais = totalItems - testesNaColuna + malotesNaColuna.length;
              const displayedFaturas = pageSize === 'all'
                ? columnFaturas
                : columnFaturas.slice(0, pageSize as number);

              return (
                <div key={status} className="min-w-[85vw] w-[85vw] sm:min-w-[280px] sm:w-[280px] snap-center md:snap-align-none flex flex-col gap-3 h-full">
                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg border-b-2 ${getStatusBorderColor(status)} bg-gray-50 dark:bg-gray-800/50 shrink-0`}>
                    <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider text-[11px]">{status}</h3>
                    <div className="flex items-center gap-1 shrink-0">
                      {testesNaColuna > 0 && (
                        <span
                          className="flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700"
                          title={`${testesNaColuna} fatura(s) de teste nesta coluna — não contam no total.`}
                        >
                          <FlaskConical size={9} /> {testesNaColuna}
                        </span>
                      )}
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${getStatusColor(status)}`}>
                        {itensReais}
                      </span>
                    </div>
                  </div>

                  <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-xl p-2 border border-gray-100 dark:border-gray-700/50 flex flex-col gap-2 flex-1 overflow-y-auto relative">
                    {malotesNaColuna.map(renderCardMalote)}
                    {displayedFaturas.map(fatura => {
                      const lockInfo = activeLocks[Number(fatura.id)];
                      const isLockedByOther = lockInfo && lockInfo.locked_by_user_id !== user?.id;
                      const isAvulso = (fatura.classificacao || '').toUpperCase() === 'AVULSO';
                      const isSandbox = fatura.ambiente === 'sandbox';
                      return (
                        <div
                          key={fatura.id}
                          onClick={() => handleCardClick(fatura)}
                          // Teste vem antes de tudo: muda o significado do card inteiro
                          // (não é dinheiro real), então precisa ser reconhecível de longe.
                          // Borda tracejada é a única do quadro — não se confunde com a
                          // vermelha de duplicata nem com a âmbar de card travado.
                          className={`p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer group relative ${isSandbox
                            ? 'bg-amber-50/70 dark:bg-amber-900/10 border-2 border-dashed border-amber-400 dark:border-amber-600'
                            : fatura.isDuplicated
                              ? 'bg-red-50/50 dark:bg-red-900/10 border-2 border-red-400 dark:border-red-600'
                              : isLockedByOther
                                ? 'bg-amber-50/60 dark:bg-amber-900/10 border-2 border-amber-400 dark:border-amber-600'
                                : isAvulso
                                  ? 'bg-blue-50/50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-700/50'
                                  : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                            } ${destaqueEtiqueta(fatura, 'contorno')}`}
                        >
                          {/* Card Content */}
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex flex-col items-start gap-1">
                              <span className="text-[10px] font-bold text-primary bg-primary/5 px-0.5 rounded">
                                {getDisplayUC(fatura.uc_codigo)}
                              </span>
                              {renderEtiquetas(fatura)}
                              {renderUsina(fatura)}
                            </div>
                            <div className="flex items-center gap-1">
                              {/* Carimbar sem sair do quadro: o quadro não tem menu "...",
                                  e abrir a fatura só para marcá-la como teste é caro. */}
                              {profile?.role === 'adm' && !isSandbox && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); alternarSandbox(fatura); }}
                                  title="Marcar como fatura de teste (sai da lista e dos somatórios; fica só no card de teste, visível só para adm)"
                                  className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded-full text-gray-400 hover:text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                                >
                                  <FlaskConical size={12} />
                                </button>
                              )}
                              {isSandbox && <EtiquetaSandbox onRemover={() => alternarSandbox(fatura)} />}
                              {fatura.empresa_parceira_id && (
                                <span className="flex items-center gap-1 text-[9px] font-bold text-orange-700 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700 px-1.5 py-0.5 rounded-full">
                                  <Handshake size={10} />
                                  {empresasParceirasMap.get(fatura.empresa_parceira_id) || 'Parceira'}
                                </span>
                              )}
                              {isAvulso && (
                                <span className="flex items-center gap-1 text-[9px] font-bold text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 px-1.5 py-0.5 rounded-full">
                                  <Zap size={10} /> Avulsa
                                </span>
                              )}
                              {isLockedByOther && (
                                <span className="flex items-center gap-1 text-[9px] font-bold text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 px-1.5 py-0.5 rounded-full animate-pulse">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block"></span>
                                  {lockInfo.locked_by_name.split(' ')[0]}
                                </span>
                              )}
                              {getBoletoBadge(fatura)}
                            </div>
                          </div>
                          <h4 className="text-[12px] font-bold text-gray-900 dark:text-white mb-1 truncate" title={fatura.cliente_nome}>Titular: {fatura.cliente_nome}</h4>
                          {(() => {
                            let assinanteNome = '';
                            if (['Com boleto emitido / A enviar', 'Cobrança enviada'].includes(status)) {
                              assinanteNome = fatura.nome_cliente_boleto || '';
                            } else if (['Fatura disponível', 'Fatura validada', 'Com boleto calculado'].includes(status)) {
                              const normUc = normalizeUc(fatura.uc_codigo);
                              const ucData = normUc ? ucsMap.get(normUc) : null;
                              assinanteNome = ucData ? ucData.nome_cliente : '';
                            }
                            return assinanteNome ? (
                              <div className="flex items-center gap-1 mb-1">
                                <span className="text-[12px] py-0.5 text-primary font-bold rounded truncate" title={`Cliente Vigor: ${assinanteNome}`}>
                                  Pagador: {assinanteNome}
                                </span>
                              </div>
                            ) : null;
                          })()}
                          {(() => {
                            // Selo do agrupamento: diz que a cobranca dessa UC sai no boleto da
                            // Secretaria e explica por que o card some do quadro depois de calculado.
                            const normUcSelo = normalizeUc(fatura.uc_codigo);
                            const nomeGrupo = normUcSelo ? agrupamentoPorUc.get(normUcSelo) : null;
                            return nomeGrupo ? (
                              <div className="mb-1">
                                <span
                                  className="inline-flex items-center gap-1 text-[9px] font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/30 border border-indigo-300 dark:border-indigo-700 px-1.5 py-0.5 rounded-full"
                                  title={`Esta UC é cobrada no boleto do agrupamento "${nomeGrupo}". Depois de calculada, a fatura sai deste quadro e é acompanhada em Agrupamentos de Faturamento. Quando o malote da Secretaria for entregue, a Secretaria inteira aparece num card só em "Cobrança enviada".`}
                                >
                                  <Layers size={9} /> Cobrada no agrupamento {nomeGrupo}
                                </span>
                              </div>
                            ) : null;
                          })()}
                          <div className="text-[11px] text-gray-500 mb-2">
                            {fatura.mes_referencia}{!isAvulso && ` • Venc. fatura: ${formatDate(fatura.data_vencimento)}`}
                            {isBoletoCalculado(fatura.status_fatura) && fatura.vencimento_boleto && (
                              isAvulso ? (
                                <span> • Venc. boleto: <span className="font-medium">{formatDate(fatura.vencimento_boleto)}</span></span>
                              ) : (
                                <div className="mt-0.5">Venc. boleto: <span className="font-medium">{formatDate(fatura.vencimento_boleto)}</span></div>
                              )
                            )}
                            {renderNegociacao(fatura)}
                          </div>
                          {/* Cobrança da Gran sem o boleto unificado (fatura 14510, 20/10/2026):
                              sem este selo só se descobria ao abrir o boleto. */}
                          {faltaPdfUnificado(fatura) && (
                            <div
                              className="mb-2 flex items-center gap-1 text-[10px] font-bold text-red-600 dark:text-red-400"
                              title={'A cobrança existe no Asaas, mas o boleto unificado não foi gerado. Abra o card e clique em "Gerar PDF unificado" antes de enviar.'}
                            >
                              <AlertTriangle size={11} /> Falta o PDF unificado
                            </div>
                          )}

                          <div className="flex justify-between items-end mt-2 pt-2 border-t border-gray-50 dark:border-gray-700">
                            {!isAvulso && (
                              <div className="text-left">
                                <p className="text-[10px] text-gray-400">Valor da fatura</p>
                                <p className="text-sm font-bold text-gray-900 dark:text-white">R$ {Number(fatura.valor_fatura_distribuidora?.toFixed(2) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                              </div>
                            )}
                            {isBoletoCalculado(fatura.status_fatura) ? (() => {
                              // O que o boleto cobra de fato: no boleto único, Vigor + Equatorial (#6302).
                              const cobrado = valorCobradoBoleto(fatura);
                              const comJuros = Number(fatura.valor_pagamento) > cobrado + 0.009;
                              return (
                              <div className={isAvulso ? "text-left" : "text-right"}>
                                {(fatura.status_boleto_vigor || '').trim().toLowerCase() === 'pago' && fatura.valor_pagamento != null ? (
                                  <>
                                    {comJuros && (
                                      <p className="text-[10px] text-gray-400 line-through">
                                        Boleto: R$ {cobrado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </p>
                                    )}
                                    <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                                      R$ {Number(fatura.valor_pagamento).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      {comJuros && (
                                        <span className="text-[9px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-1 rounded font-semibold">c/ juros</span>
                                      )}
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <p className="text-[10px] text-gray-400">Valor do boleto</p>
                                    <p className="text-sm font-bold text-primary">R$ {cobrado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                  </>
                                )}
                              </div>
                              );
                            })() : (
                              <div className={isAvulso ? "text-left" : "text-right"}>
                                <p className="text-[10px] text-gray-400">Consumo</p>
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{fatura.consumo_ativo_kwh || '-'} kWh</p>
                              </div>
                            )}
                          </div>
                          {/* Saldo Acumulado */}
                          {!isAvulso && (
                            <div className="mt-2 text-[10px] text-gray-400 text-center bg-gray-50 dark:bg-gray-700/50 rounded py-0.5">
                              Saldo: {!fatura.saldo_acumulado_kwh || ['0', '0.00', '0,00'].includes(String(fatura.saldo_acumulado_kwh).trim()) ? '0,00' : fatura.saldo_acumulado_kwh} kWh
                            </div>
                          )}

                          {/* Alerta: descadastrar e-mail do cliente */}
                          {shouldAlertDescadastro(fatura) && (
                            <div className="mt-2 flex items-center gap-1.5 px-2 py-1 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg">
                              <AlertTriangle size={12} className="text-orange-500 shrink-0" />
                              <span className="text-[9px] font-bold text-orange-600 dark:text-orange-400 leading-tight">
                                Descadastrar e-mail do cliente
                              </span>
                            </div>
                          )}

                          {/* Sender Info for "Cobrança enviada" */}
                          {status === 'Cobrança enviada' && fatura.enviado_por_nome && (
                            <div className="mt-2 pt-2 border-t border-gray-50 dark:border-gray-700/50">
                              <p className="text-[9px] text-gray-400 italic">
                                Enviado por <span className="font-medium text-gray-500 dark:text-gray-400">{fatura.enviado_por_nome}</span>
                                {fatura.enviado_em && ` em ${formatDateSafely(fatura.enviado_em)}`}
                              </p>
                            </div>
                          )}

                          {/* Cancelamento Info */}
                          {(() => {
                            const isCurrentlyCanceled = (fatura.status_boleto_vigor || '').trim().toLowerCase() === 'cancelado';
                            const cancellationHistory = (Array.isArray(fatura.historico_cancelamentos) && fatura.historico_cancelamentos.length > 0)
                              ? fatura.historico_cancelamentos
                              : (fatura.cancelado_por_nome
                                ? [{
                                  cancelado_por_nome: fatura.cancelado_por_nome,
                                  cancelado_em: fatura.cancelado_em || fatura.created_at,
                                  motivo_cancelamento_boleto: fatura.motivo_cancelamento_boleto || 'Não informado'
                                }]
                                : []
                              );

                            if (isCurrentlyCanceled && fatura.cancelado_por_nome) {
                              return (
                                <div className="mt-2 pt-2 border-t border-red-50 dark:border-red-900/30 animate-fadeIn">
                                  <p className="text-[9px] text-red-500 italic">
                                    Cancelamento solicitado por <span className="font-bold">{fatura.cancelado_por_nome}</span>
                                    {fatura.cancelado_em && ` em ${formatDateSafely(fatura.cancelado_em)}`}
                                  </p>
                                  {fatura.motivo_cancelamento_boleto && (
                                    <p className="text-[9px] text-red-400/80 italic mt-0.5 line-clamp-1" title={fatura.motivo_cancelamento_boleto}>
                                      Motivo: {fatura.motivo_cancelamento_boleto}
                                    </p>
                                  )}
                                </div>
                              );
                            } else if (!isCurrentlyCanceled && cancellationHistory.length > 0) {
                              return (
                                <div className="mt-2 pt-2 border-t border-amber-50 dark:border-amber-950/20 animate-fadeIn">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCancellationModalData(cancellationHistory);
                                    }}
                                    className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-all cursor-pointer group"
                                  >
                                    <RefreshCw className="w-3 h-3 animate-spin-slow text-amber-500 group-hover:rotate-180 transition-transform duration-500" />
                                    <span>Cobrança recalculada {cancellationHistory.length} vez(es)</span>
                                  </button>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      );
                    })}


                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Floating Action Button - Outside container */}
      <FaturaFAB onAdd={handleAdd} onRefresh={() => fetchFaturas(true)} />

      {/* Dropdown Portal */}
      {dropdownState && createPortal(
        <div
          className="fixed bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 z-[100] w-48 overflow-hidden animate-fade-in text-left"
          style={{
            top: dropdownState.position.top,
            left: dropdownState.position.left
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="w-full px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
            <Receipt size={14} /> Calcular boleto
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(dropdownState.fatura); }}
            className="w-full px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            <FileEdit size={14} /> Editar dados
          </button>
          <button className="w-full px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
            <Bolt size={14} /> Gerar tarefa
          </button>
          {['Em aberto', 'Vencido'].includes(dropdownState.fatura.status_boleto_vigor || '') && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setParcelarFatura(dropdownState.fatura);
                setShowParcelamentoModal(true);
                setDropdownState(null);
              }}
              className="w-full px-4 py-3 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/10 flex items-center gap-2"
            >
              <Split size={14} /> Parcelar cobrança
            </button>
          )}
          {['Em aberto', 'Vencido'].includes(dropdownState.fatura.status_boleto_vigor || '') && !dropdownState.fatura.parcelamento_origem_id && ['adm', 'gerente', 'analista'].includes(profile?.role || '') && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSincronizarFatura(dropdownState.fatura);
                setShowSincronizarStatusModal(true);
                setDropdownState(null);
              }}
              className="w-full px-4 py-3 text-sm text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/10 flex items-center gap-2"
            >
              <RefreshCw size={14} /> Sincronizar status (sem boleto)
            </button>
          )}
          {['Em aberto', 'Vencido'].includes(dropdownState.fatura.status_boleto_vigor || '') && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setQuitarFatura(dropdownState.fatura);
                setShowQuitarModal(true);
                setDropdownState(null);
              }}
              className="w-full px-4 py-3 text-sm text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/10 flex items-center gap-2"
            >
              <BadgeCheck size={14} /> Quitar (pago via avulsa)
            </button>
          )}
          {profile?.role === 'adm' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                alternarSandbox(dropdownState.fatura);
              }}
              className="w-full px-4 py-3 text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/10 flex items-center gap-2 border-t border-gray-50 dark:border-gray-700"
            >
              <FlaskConical size={14} />
              {dropdownState.fatura.ambiente === 'sandbox' ? 'Tirar marca de teste' : 'Marcar como teste'}
            </button>
          )}
          <button
            onClick={() => {
              setFaturaToDelete(dropdownState.fatura);
              setDeleteModalOpen(true);
              setDropdownState(null);
            }}
            className="w-full px-4 py-3 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 flex items-center gap-2 border-t border-gray-50 dark:border-gray-700"
          >
            <Trash2 size={14} /> Excluir fatura
          </button>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && faturaToDelete && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 m-4 text-center transform scale-100 transition-all">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Excluir Fatura?</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Tem certeza que deseja excluir a fatura de <strong>{faturaToDelete.cliente_nome}</strong>? O registro será arquivado no sistema.
            </p>

            <div className="mb-6 text-left">
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Motivo da Exclusão <span className="text-red-500">*</span></label>
              <textarea
                value={motivoExclusao}
                onChange={(e) => setMotivoExclusao(e.target.value)}
                placeholder="Descreva o motivo da solicitação..."
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-3 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all min-h-[100px] resize-none"
                required
              />
            </div>

            <div className="flex gap-3 justify-center">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setMotivoExclusao('');
                }}
                className="flex-1 px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={!motivoExclusao.trim()}
                className="flex-1 px-6 py-2.5 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-red-500/20"
              >
                Confirmar Exclusão
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Success Modal */}
      {successModalOpen && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 m-4 text-center transform scale-100 transition-all">
            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Sucesso!</h3>
            <p className="text-gray-500">
              A fatura foi excluída corretamente.
            </p>
          </div>
        </div>,
        document.body
      )}

      {/* Edit/Create Modal */}
      <FaturaModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingFatura(null);
        }}
        fatura={editingFatura}
        onSave={handleSave}
      />

      <FaturaValidationModal
        isOpen={showValidationModal}
        onClose={() => {
          releaseFaturaLock(validatingFaturaId);
          setShowValidationModal(false);
          setValidatingFaturaId(null);
        }}
        fatura={validatingFatura}
        onUpdate={atualizarAposFluxoDaFatura}
        onNext={() => {
          // Lock persists — same fatura moving to next pipeline step
          setShowValidationModal(false);
          if (validatingFaturaId) {
            // Só as faturas da mesma UC, não a base inteira (solicitação 5973): é tudo
            // o que a checagem de duplicata precisa, e a carga cheia fica para o fim
            // do fluxo (ver atualizarAposFluxoDaFatura).
            cargaCheiaPendenteRef.current = true;
            buscarFaturasDaMesmaUc(validatingFaturaId).then((newFaturas) => {
              if (newFaturas) {
                const currentFatura = newFaturas.find((f: any) => String(f.id) === validatingFaturaId);
                if (currentFatura) {
                  // Mesma régua da lista: avulsa e fatura inativa não são duplicata.
                  const duplicates = newFaturas.filter((f: any) =>
                    faturaEntraNaDuplicata(f) &&
                    normalizeUc(f.uc_codigo || '') === normalizeUc(currentFatura.uc_codigo || '') &&
                    normalizeMesReferencia(f.mes_referencia) === normalizeMesReferencia(currentFatura.mes_referencia) &&
                    normalizeDate(f.data_vencimento) === normalizeDate(currentFatura.data_vencimento)
                  );
                  const isDuplicated = faturaEntraNaDuplicata(currentFatura) && duplicates.length > 1 && !currentFatura.duplicata_confirmada;

                  if (isDuplicated) {
                    setDuplicatedFaturasGroup(duplicates);
                    setShowDuplicateModal(true);
                    return;
                  }
                }
              }
              setShowGenericModal(true);
            });
          }
        }}
      />

      <PendentesCalculoModal
        isOpen={showPendentesModal}
        onClose={() => setShowPendentesModal(false)}
        pendentes={pendentesCalculo}
        historicoFaturas={faturas}
        unidadesConsumidoras={unidadesConsumidoras}
        onActionComplete={aplicarAlvoAtualizacao}
        onOpenValidation={async (faturaId, keepOpen = false) => {
          if (!user?.id) return;
          const { data, error } = await supabase.rpc('acquire_fatura_lock_v2', {
            p_fatura_id: Number(faturaId),
            p_user_id: user.id
          });
          if (error) {
            console.error('[FaturaLock] RPC error (pendentes):', error);
            addToast('Erro ao verificar disponibilidade da fatura.', 'error');
            return;
          }
          const lockResult = data?.[0];
          console.log('[FaturaLock] pendentes acquire result:', lockResult);
          if (lockResult && !lockResult.success) {
            addToast(`Esta fatura está sendo editada por ${lockResult.locked_by_name || 'outro usuário'}.`, 'warning');
            return;
          }
          setValidatingFaturaId(faturaId);
          const faturaToOpen = faturas.find(f => String(f.id) === String(faturaId));
          if (!faturaToOpen || !faturaToOpen.status_fatura || faturaToOpen.status_fatura === 'Fatura disponível' || faturaToOpen.status_fatura === 'Aguardando validação') {
            setShowValidationModal(true);
          } else {
            setShowGenericModal(true);
          }
          if (!keepOpen) {
            setShowPendentesModal(false);
          }
        }}
      />

      <FaturaCalculationModal
        isOpen={showGenericModal && validatingFatura?.status_fatura === 'Fatura validada'}
        onClose={() => {
          releaseFaturaLock(validatingFaturaId);
          setShowGenericModal(false);
          setValidatingFaturaId(null);
        }}
        fatura={validatingFatura}
        onUpdate={atualizarAposFluxoDaFatura}
        onNext={() => {
          // Already in generic modal state, fetching update will refresh status to trigger the next component
          if (validatingFaturaId) {
            setShowGenericModal(true);
          }
        }}
      />

      <BoletoEmissionModal
        isOpen={showGenericModal && validatingFatura?.status_fatura === 'Com boleto calculado'}
        onClose={() => {
          releaseFaturaLock(validatingFaturaId);
          setShowGenericModal(false);
          setValidatingFaturaId(null);
        }}
        fatura={validatingFatura}
        onUpdate={atualizarAposFluxoDaFatura}
        onNext={() => {
          if (validatingFaturaId) {
            setShowGenericModal(true);
          }
        }}
      />

      <BoletoEmitidoModal
        isOpen={showGenericModal && validatingFatura?.status_fatura === 'Com boleto emitido / A enviar'}
        onClose={() => {
          releaseFaturaLock(validatingFaturaId);
          setShowGenericModal(false);
          setValidatingFaturaId(null);
        }}
        fatura={validatingFatura}
        onUpdate={atualizarAposFluxoDaFatura}
        onParcelar={(f) => {
          releaseFaturaLock(validatingFaturaId);
          setShowGenericModal(false);
          setValidatingFaturaId(null);
          setParcelarFatura(f);
          setShowParcelamentoModal(true);
        }}
        onQuitar={(f) => {
          releaseFaturaLock(validatingFaturaId);
          setShowGenericModal(false);
          setValidatingFaturaId(null);
          setQuitarFatura(f);
          setShowQuitarModal(true);
        }}
        onSincronizarStatus={['adm', 'gerente', 'analista'].includes(profile?.role || '') ? (f) => {
          releaseFaturaLock(validatingFaturaId);
          setShowGenericModal(false);
          setValidatingFaturaId(null);
          setSincronizarFatura(f);
          setShowSincronizarStatusModal(true);
        } : undefined}
      />

      <EscolhaVisaoGranModal
        isOpen={showEscolhaGran}
        nomeEmpresa={empresaGran?.nome || nomeParceiraCard}
        planilhaDisponivel={!!empresaGran}
        onClose={() => setShowEscolhaGran(false)}
        onQuadro={() => { setShowEscolhaGran(false); setParceiraFilter('parceira'); }}
        onSoVigor={() => { setShowEscolhaGran(false); setParceiraFilter('vigor'); }}
        onPlanilha={() => { setShowEscolhaGran(false); setShowPlanilhaGran(true); }}
      />

      <PlanilhaGranModal
        isOpen={showPlanilhaGran}
        onClose={() => setShowPlanilhaGran(false)}
        empresa={empresaGran}
        faturas={faturas}
        ucs={unidadesConsumidoras}
        podeGerir={podeGerirPlanilhaGran}
        fluxoAberto={!!validatingFaturaId || showDuplicateModal}
        onAbrirFatura={(id) => {
          const f = faturas.find((x: any) => Number(x.id) === id);
          if (f) handleCardClick(f);
          else addToast(`A fatura ${id} não está carregada no quadro. Atualize a lista e tente de novo.`, 'warning');
        }}
        onEmitirEmSequencia={iniciarFilaGran}
      />

      {filaGran && (
        <FilaEmissaoGranBar
          fila={filaGran}
          onContinuar={() => setFilaGran(q => q && filaGranRegra.continuar(q))}
          onPular={pularNaFilaGran}
          onEncerrar={() => {
            if (filaGran.pos < filaGran.itens.length) {
              addToast(`Emissão em sequência encerrada: ${filaGran.emitidas.length} emitida(s). As que faltaram continuam prontas para emitir.`, 'info');
            }
            setFilaGran(null);
          }}
        />
      )}

      <CobrancaEnviadaModal
        isOpen={showGenericModal && validatingFatura?.status_fatura === 'Cobrança enviada'}
        onClose={() => {
          releaseFaturaLock(validatingFaturaId);
          setShowGenericModal(false);
          setValidatingFaturaId(null);
        }}
        fatura={validatingFatura}
        onUpdate={atualizarAposFluxoDaFatura}
        onParcelar={(f) => {
          releaseFaturaLock(validatingFaturaId);
          setShowGenericModal(false);
          setValidatingFaturaId(null);
          setParcelarFatura(f);
          setShowParcelamentoModal(true);
        }}
        onQuitar={(f) => {
          releaseFaturaLock(validatingFaturaId);
          setShowGenericModal(false);
          setValidatingFaturaId(null);
          setQuitarFatura(f);
          setShowQuitarModal(true);
        }}
        onSincronizarStatus={['adm', 'gerente', 'analista'].includes(profile?.role || '') ? (f) => {
          releaseFaturaLock(validatingFaturaId);
          setShowGenericModal(false);
          setValidatingFaturaId(null);
          setSincronizarFatura(f);
          setShowSincronizarStatusModal(true);
        } : undefined}
      />

      <CriarParcelamentoModal
        isOpen={showParcelamentoModal}
        onClose={() => { setShowParcelamentoModal(false); setParcelarFatura(null); }}
        onSuccess={() => { fetchFaturas(true); fetchParcelamentosData(); }}
        preSelectedFatura={parcelarFatura}
      />

      <SincronizarStatusModal
        isOpen={showSincronizarStatusModal}
        onClose={() => { setShowSincronizarStatusModal(false); setSincronizarFatura(null); }}
        onSuccess={() => fetchFaturas(true)}
        preSelectedFatura={sincronizarFatura}
      />

      <QuitarFaturasModal
        isOpen={showQuitarModal}
        onClose={() => { setShowQuitarModal(false); setQuitarFatura(null); }}
        onSuccess={() => { fetchFaturas(true); fetchParcelamentosData(); }}
        preSelectedFatura={quitarFatura}
      />

      {/* Generic Placeholder Modal */}
      {showGenericModal &&
        validatingFatura?.status_fatura !== 'Fatura validada' &&
        validatingFatura?.status_fatura !== 'Com boleto calculado' &&
        validatingFatura?.status_fatura !== 'Com boleto emitido / A enviar' &&
        validatingFatura?.status_fatura !== 'Cobrança enviada' &&
        createPortal(
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in" style={{ zIndex: 9999 }}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Em Breve</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                Ações para faturas com status <strong>{validatingFatura?.status_fatura}</strong> serão implementadas na próxima etapa.
              </p>
              <button
                onClick={() => { releaseFaturaLock(validatingFaturaId); setShowGenericModal(false); setValidatingFaturaId(null); }}
                className="px-6 py-2 bg-primary text-white rounded-lg font-bold"
              >
                Fechar
              </button>
            </div>
          </div>,
          document.body
        )}

      {/* Janela de filtros — os filtros valem na hora; a janela só edita e fecha. */}
      {showFilterModal && (
        <FiltroFaturasModal
          onFechar={() => setShowFilterModal(false)}
          filtros={filters}
          onMudar={(campo, valor) => setFilters(prev => ({ ...prev, [campo]: valor }))}
          contagemStatus={{
            'Fatura disponível': countImportados,
            'Fatura validada': countValidadas,
            'Com boleto calculado': countCalculados,
            'Com boleto emitido / A enviar': countEmitidos,
            'Cobrança enviada': countCobrancaEnviada,
          }}
          // Etiqueta da UC — só para quem vê etiquetas e quando já existe alguma.
          opcoesEtiqueta={podeEtiquetar && etiquetasApi.etiquetas.length > 0 ? [
            { value: FILTRO_QUALQUER_ETIQUETA, label: 'Com qualquer etiqueta' },
            ...etiquetasApi.etiquetas.map(et => {
              const ucs = etiquetasApi.ucsPorEtiqueta.get(et.id) ?? 0;
              return { value: et.id, label: et.texto, meta: `${ucs} UC${ucs === 1 ? '' : 's'}`, ponto: estiloDaCor(et.cor).pilula };
            }),
          ] : undefined}
          // Usina (#6402) — a impressa na conta; sem ela, a do cadastro da UC.
          opcoesUsina={indiceUsinas.usinas.length > 0 ? [
            ...indiceUsinas.usinas.map(u => ({ value: u.id, label: u.nome })),
            { value: FILTRO_SEM_USINA, label: NOME_SEM_USINA },
          ] : undefined}
          filtrosAtivos={activeFiltersCount}
          onLimpar={clearFilters}
        />
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Exportar Faturas"
        columns={faturasExportColumns}
        data={finalExportData}
        filename="Faturas_Cobrancas"
        summary={exportSummary}
        availableDateFields={[
          { key: 'mes_referencia', label: 'Mês Referência' },
          { key: 'data_vencimento', label: 'Data de Vencimento' },
          { key: 'data_pgto_boleto', label: 'Data de Pagamento' }
        ]}
      />

      {/* Kanban Export Filter Modal */}
      {showKanbanExportModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowKanbanExportModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 z-10">
            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">Exportar Excel</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Selecione as colunas do Kanban para exportar</p>

            <div className="space-y-1 mb-5">
              <button
                onClick={() => setExportKanbanFilter(
                  exportKanbanFilter.length === KANBAN_STATUSES.length ? [] : [...KANBAN_STATUSES]
                )}
                className="w-full text-left text-xs font-semibold text-primary hover:text-primary/80 transition-colors mb-2"
              >
                {exportKanbanFilter.length === KANBAN_STATUSES.length ? 'Desmarcar todas' : 'Selecionar todas'}
              </button>
              {KANBAN_STATUSES.map(status => {
                const count = filteredFaturas.filter(f => f.status_fatura === status).length;
                const checked = exportKanbanFilter.includes(status);
                return (
                  <label key={status} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setExportKanbanFilter(prev =>
                        prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
                      )}
                      className="w-4 h-4 accent-primary flex-shrink-0"
                    />
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${KANBAN_STATUS_COLORS[status] || 'bg-gray-400'}`} />
                    <span className="text-sm text-gray-700 dark:text-gray-200 flex-1">{status}</span>
                    <span className="text-xs font-semibold text-gray-400 tabular-nums">{count}</span>
                  </label>
                );
              })}
            </div>

            <div className="border-t border-gray-100 dark:border-gray-700 pt-4 mb-4">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={ucSemFormatacao}
                  onChange={e => setUcSemFormatacao(e.target.checked)}
                  className="w-4 h-4 mt-0.5 accent-primary flex-shrink-0"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Número da UC sem formatação</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    Remove pontos e traços <span className="font-mono">3.973.811.012-26 → 397381101226</span><br />
                    Remove zeros à esquerda <span className="font-mono">000098072401262 → 98072401262</span>
                  </p>
                </div>
              </label>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowKanbanExportModal(false)}
                className="flex-1 px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => { setShowKanbanExportModal(false); setShowExportModal(true); }}
                disabled={exportKanbanFilter.length === 0}
                className="flex-1 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Exportar ({exportData.length})
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* KPI Dashboard Modal */}
      <FinanceiroKPIDashboard
        isOpen={showKPIDashboard}
        onClose={() => setShowKPIDashboard(false)}
        faturas={filteredFaturas}
        mesesDisponiveis={mesesDisponiveis}
      />

      {/* ZIP Export Modal */}
      <ExportZipModal
        isOpen={showZipModal}
        onClose={() => setShowZipModal(false)}
        faturas={faturas}
        unidades={unidadesConsumidoras}
        nomesParceiras={empresasParceirasMap}
      />

      {/* Duplicate Fatura Modal */}
      <DuplicateFaturaModal
        isOpen={showDuplicateModal}
        onClose={() => {
          setShowDuplicateModal(false);
          setDuplicatedFaturasGroup([]);
        }}
        faturas={duplicatedFaturasGroup}
        onUpdate={() => {
          fetchFaturas(true);
        }}
      />

      {/* Cards Configuration Modal */}
      {showCardsModal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Layout className="text-primary" size={24} />
                  Configurar Cards
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Escolha quais informações deseja ver no resumo.</p>
              </div>
              <button
                onClick={() => setShowCardsModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-400"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-3">
                {ALL_CARDS.map(card => {
                  const isVisible = visibleCards.includes(card.id);
                  return (
                    <label
                      key={card.id}
                      className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${isVisible
                        ? 'border-primary bg-primary/5 dark:bg-primary/10'
                        : 'border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 grayscale opacity-70'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${isVisible ? 'bg-primary text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-400'}`}>
                          {card.icon}
                        </div>
                        <span className={`font-semibold ${isVisible ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                          {card.label}
                        </span>
                      </div>
                      <input
                        type="checkbox"
                        checked={isVisible}
                        onChange={() => {
                          setVisibleCards(prev => {
                            const next = prev.includes(card.id)
                              ? prev.filter(id => id !== card.id)
                              : [...prev, card.id];
                            if (user?.id) {
                              localStorage.setItem(`vigorhub_financeiro_visible_cards_${user.id}`, JSON.stringify(next));
                            }
                            return next;
                          });
                        }}
                        className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary/20"
                      />
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="p-6 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowCardsModal(false)}
                className="px-8 py-3 bg-primary text-white rounded-xl font-bold shadow-lg shadow-primary/30 hover:shadow-primary/40 transition-all transform hover:-translate-y-0.5 active:scale-95"
              >
                Salvar Configurações
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ConfirmationModal
        isOpen={!!faturaParaDesmarcar}
        onClose={() => setFaturaParaDesmarcar(null)}
        onConfirm={() => {
          const alvo = faturaParaDesmarcar;
          setFaturaParaDesmarcar(null);
          if (alvo) void gravarAmbiente(alvo, 'producao');
        }}
        variant="warning"
        title="Tirar a marca de teste?"
        confirmText="Tirar a marca"
        description={
          <p>
            A fatura <strong>{faturaParaDesmarcar ? getDisplayUC(faturaParaDesmarcar.uc_codigo) : ''}</strong> volta
            para a operação real: entra nos somatórios, fica visível para os analistas e pode receber boleto de
            verdade. Só faça isso se ela for de um cliente real.
          </p>
        }
      />

      {/* Cancellation History Modal */}
      {
        cancellationModalData && createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-md animate-fade-in p-4">
            <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800/50 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden transform scale-100 transition-all">
              {/* Header */}
              <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-950/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center text-amber-500 shadow-sm">
                    <RefreshCw className="w-5 h-5 animate-spin-slow" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Histórico de Recalculações</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Detalhamento dos cancelamentos ocorridos para esta cobrança</p>
                  </div>
                </div>
                <button
                  onClick={() => setCancellationModalData(null)}
                  className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-all cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 max-h-[60vh] overflow-y-auto space-y-4 custom-scrollbar">
                {cancellationModalData.map((item, index) => (
                  <div
                    key={index}
                    className="p-4 rounded-xl border border-slate-100 dark:border-slate-800/40 bg-slate-50/30 dark:bg-slate-950/10 hover:border-amber-200 dark:hover:border-amber-900/30 hover:bg-amber-50/[0.1] transition-all duration-300"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                          Cancelado por <span className="text-[#5F6C37] dark:text-[#8ba24e] font-bold">{item.cancelado_por_nome || 'Sistema'}</span>
                        </p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {formatDateSafely(item.cancelado_em)}
                        </p>
                      </div>
                      <span className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-900/20">
                        Recalculado
                      </span>
                    </div>

                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800/50">
                      <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                        <span className="text-slate-400 dark:text-slate-500 font-semibold block mb-1 uppercase text-[10px] tracking-wider">Motivo do Cancelamento:</span>
                        <span className="italic">"{item.motivo_cancelamento_boleto || 'Não especificado'}"</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex justify-end bg-slate-50/50 dark:bg-slate-950/20">
                <button
                  onClick={() => setCancellationModalData(null)}
                  className="px-5 py-2 rounded-xl bg-slate-800 dark:bg-slate-700 hover:bg-slate-900 dark:hover:bg-slate-600 text-white text-sm font-semibold transition-all shadow-md cursor-pointer"
                >
                  Fechar histórico
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      <EtiquetasUcModal
        key={ucEtiquetando?.id ?? 'fechado'}
        uc={ucEtiquetando}
        api={etiquetasApi}
        onFechar={() => setUcEtiquetando(null)}
      />
    </>
  );
};

// ----------------------------------------------------------------------
// BOLETOS LIST
// ----------------------------------------------------------------------

const BoletosList = () => {
  const { user, profile } = useAuth();
  const { addToast } = useToast();
  const [boletos, setBoletos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'data_vencimento', direction: 'asc' });
  const [unidadesConsumidoras, setUnidadesConsumidoras] = useState<any[]>([]);

  const ucsMap = useMemo(() => {
    const map = new Map<string, any>();
    unidadesConsumidoras.forEach(uc => {
      if (uc.numero_uc) {
        const norm1 = uc.numero_uc.replace(/[.\-\s]/g, '').replace(/^0+/, '');
        if (norm1) map.set(norm1, uc);
      }
      if (uc.outro_numero_uc) {
        const norm2 = uc.outro_numero_uc.replace(/[.\-\s]/g, '').replace(/^0+/, '');
        if (norm2) map.set(norm2, uc);
      }
    });
    return map;
  }, [unidadesConsumidoras]);

  const getDisplayUC = (uc_codigo: string) => {

    if (!uc_codigo) return 'SEM UC';
    const normCode = uc_codigo.replace(/[.\-\s]/g, '').replace(/^0+/, '');
    if (!normCode) return uc_codigo;

    const matched = unidadesConsumidoras.find(uc => {
      const n1 = uc.numero_uc ? uc.numero_uc.replace(/[.\-\s]/g, '').replace(/^0+/, '') : '';
      const n2 = uc.outro_numero_uc ? uc.outro_numero_uc.replace(/[.\-\s]/g, '').replace(/^0+/, '') : '';
      return n1 === normCode || n2 === normCode;
    });

    if (matched && matched.numero_uc && matched.outro_numero_uc) {
      const n1 = matched.numero_uc.replace(/[.\-\s]/g, '').replace(/^0+/, '');
      const n2 = matched.outro_numero_uc.replace(/[.\-\s]/g, '').replace(/^0+/, '');
      if (n1 !== n2) {
        return `${matched.numero_uc} / ${matched.outro_numero_uc}`;
      }
    }
    return uc_codigo;
  };

  // Filters
  const [activeTab, setActiveTab] = useState<'todos' | 'pendentes' | 'pagos' | 'vencidos'>('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Dropdown & Modal State
  const [dropdownState, setDropdownState] = useState<{
    boletoId: string;
    position: { top: number; left: number };
  } | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBoleto, setEditingBoleto] = useState<any | null>(null);

  // Delete State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [boletoToDelete, setBoletoToDelete] = useState<any | null>(null);

  const canViewSummary = profile?.role === 'adm' || profile?.role === 'gerente' || profile?.role === 'analista';

  React.useEffect(() => {
    const handleClickOutside = () => setDropdownState(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  React.useEffect(() => {
    fetchBoletos();
  }, []);

  const handleCurrentMonth = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  };

  const fetchBoletos = async () => {
    setLoading(true);
    try {
      const [{ data, error }, ucsResult] = await Promise.all([
        supabase
          .from('boletos_vigor')
          .select('*, fatura:faturas(cliente_nome, pdf_fatura, uc_codigo, nome_cliente_boleto)'),
        supabase
          .from('unidades_consumidoras')
          .select('numero_uc, outro_numero_uc, nome_cliente, titular_fatura')
      ]);

      if (error) throw error;
      setBoletos(data as any[]);
      if (ucsResult.data) setUnidadesConsumidoras(ucsResult.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }


  const toggleDropdown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();

    if (dropdownState?.boletoId === id) {
      setDropdownState(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownState({
      boletoId: id,
      position: {
        top: rect.bottom + 5,
        left: rect.right - 192 // 192px = w-48
      }
    });
  };

  const handleAdd = () => {
    setEditingBoleto(null);
    setShowAddModal(true);
  };

  const handleEditBoleto = (boleto: any) => {
    setEditingBoleto(boleto);
    setShowAddModal(true);
    setDropdownState(null);
  }

  const handleDeleteBoletoConfirm = async () => {
    if (!boletoToDelete) return;

    try {
      const { error } = await supabase
        .from('boletos_vigor')
        .delete()
        .eq('id', boletoToDelete.id);

      if (error) throw error;

      // Log action
      if (user) {
        await logAction(user.id, 'DELETE_BOLETO', {
          boleto_id: boletoToDelete.id,
          cliente: boletoToDelete.fatura?.cliente_nome || boletoToDelete.cliente_nome
        });
        logUsageEvent({ userId: user.id, modulo: 'financeiro', funcionalidade: 'boletos', acao: 'excluir_boleto', origem: 'acao' });
      }

      setDeleteModalOpen(false);
      setSuccessModalOpen(true);
      fetchBoletos();

      setTimeout(() => {
        setSuccessModalOpen(false);
        setBoletoToDelete(null);
      }, 2000);

    } catch (error) {
      console.error('Error deleting boleto:', error);
      addToast('Erro ao excluir boleto.', 'error');
    }
  }

  // --- Filtering Logic ---
  const getFilteredBoletos = () => {
    return boletos.filter(boleto => {
      // 1. Tab Filter
      const status = (boleto.status || '').toLowerCase();
      if (activeTab === 'pendentes' && !['gerado', 'enviado'].includes(status)) return false;
      if (activeTab === 'pagos' && status !== 'pago') return false;
      if (activeTab === 'vencidos' && status !== 'vencido') return false;

      // 2. Search Filter
      const search = removeAccents(searchTerm);
      const searchCleaned = cleanNumericString(searchTerm);

      const normUc = boleto.fatura?.uc_codigo ? boleto.fatura.uc_codigo.replace(/[.\-\s]/g, '').replace(/^0+/, '') : '';
      const matchedUc = normUc ? ucsMap.get(normUc) : null;

      const matchesSearch =
        removeAccents(boleto.cliente_nome).includes(search) ||
        removeAccents(boleto.fatura?.cliente_nome).includes(search) ||
        (boleto.fatura?.uc_codigo || '').includes(searchTerm) ||
        cleanNumericString(boleto.fatura?.uc_codigo || '').includes(searchCleaned) ||
        removeAccents(boleto.fatura?.nome_cliente_boleto).includes(search) ||
        (matchedUc && (
          removeAccents(matchedUc.nome_cliente).includes(search) ||
          (matchedUc.numero_uc || '').includes(searchTerm) ||
          cleanNumericString(matchedUc.numero_uc || '').includes(searchCleaned) ||
          (matchedUc.outro_numero_uc || '').includes(searchTerm) ||
          cleanNumericString(matchedUc.outro_numero_uc || '').includes(searchCleaned) ||
          removeAccents(matchedUc.titular_fatura).includes(search)
        )) ||
        String(boleto.valor_total).includes(searchTerm);

      if (!matchesSearch) return false;


      // 3. Date Filter (using data_vencimento for the filter main range, or emission?)
      // Usually "Gestão de Cobranças" filters by Due Date (Vencimento) for cash flow view
      if (startDate || endDate) {
        const dateStr = boleto.data_vencimento;
        if (!dateStr) return false;
        const date = new Date(dateStr);
        const start = startDate ? new Date(startDate) : new Date('1900-01-01');
        const end = endDate ? new Date(endDate) : new Date('2100-01-01');
        end.setHours(23, 59, 59, 999);

        if (date < start || date > end) return false;
      }

      return true;
    });
  };

  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredBoletos = getFilteredBoletos();

  const sortedBoletos = [...filteredBoletos].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle nested or specific sorts
    if (sortConfig.key === 'cliente_nome') {
      aValue = a.fatura?.cliente_nome || a.cliente_nome || '';
      bValue = b.fatura?.cliente_nome || b.cliente_nome || '';
    } else if (sortConfig.key === 'uc_codigo') {
      aValue = a.fatura?.uc_codigo || '';
      bValue = b.fatura?.uc_codigo || '';
    } else if (sortConfig.key === 'valor_total' || sortConfig.key === 'economia') {
      aValue = Number(aValue || 0);
      bValue = Number(bValue || 0);
    }

    if (aValue === bValue) return 0;
    if (aValue === undefined || aValue === null) return 1;
    if (bValue === undefined || bValue === null) return -1;

    const comparison = aValue < bValue ? -1 : 1;
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  // --- Summary Calculations ---
  // Note: Summary usually applies to the CURRENT MONTH or specific context.
  // Based on mockup, the first card says "RECEBIDO NO MÊS".
  // For now, I will calculate based on the *filtered* data to make it responsive to date filters, 
  // OR if date filter is empty, default to all-time or current month?
  // User asked for specific cards. Let's compute from the FULL lists (or filtered by date if applied).
  // Strategy: Apply Date Filter to totals if set, otherwise show totals for current view context.

  const totalRecebido = filteredBoletos
    .filter(b => b.status === 'pago')
    .reduce((acc, b) => acc + (Number(b.valor_total) || 0), 0);

  const totalPendente = filteredBoletos
    .filter(b => ['gerado', 'enviado'].includes(b.status))
    .reduce((acc, b) => acc + (Number(b.valor_total) || 0), 0);

  const totalVencido = filteredBoletos
    .filter(b => b.status === 'vencido')
    .reduce((acc, b) => acc + (Number(b.valor_total) || 0), 0);

  const countEmitidos = filteredBoletos.length;

  // --- Actions ---
  const handleGenerateBoleto = async (boleto: any) => {
    // Reuse existing logic...
    // For brevity in this refactor, calling the same function (assuming it exists or I rewrite it)
    // Detailed implementation omitted for brevity, reusing previous implementation logic
    // But since I am replacing the WHOLE component, I must include the logic again.
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('generate-boleto', {
        body: { record_id: boleto.id }
      });
      if (error) throw error;
      if (data) {
        const blob = new Blob([data], { type: 'application/pdf' });
        const fileName = `boleto_${boleto.id}_${Date.now()}.pdf`;
        const filePath = `boletos_gerados/${fileName}`;
        const { error: uploadError } = await supabase.storage.from('faturas').upload(filePath, blob, { contentType: 'application/pdf' });
        if (uploadError) throw uploadError;
        await supabase.from('boletos_vigor').update({ pdf_boleto: filePath }).eq('id', boleto.id);
        addToast('Boleto gerado com sucesso!', 'success');
        logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'boletos', acao: 'gerar_boleto', origem: 'acao' });
        fetchBoletos();
      }
    } catch (err: any) {
      console.error('Erro:', err);
      addToast('Erro ao gerar boleto.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBoleto = async (path: string) => {
    if (!path) return;
    const { data } = await supabase.storage.from('faturas').createSignedUrl(path, 60);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  };

  const handleSave = () => { fetchBoletos(); setShowAddModal(false); }

  return (
    <div className="animate-fade-in space-y-6 pb-12">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-poppins font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg text-primary">
              <Receipt size={24} />
            </div>
            Gestão de Cobranças
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Controle de boletos emitidos, status de pagamento e previsibilidade de caixa.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Date Filter */}
          <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-gray-800 p-2 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Período:</span>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded px-2 py-1 outline-none focus:border-primary"
              />
              <span className="text-gray-400">-</span>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded px-2 py-1 outline-none focus:border-primary"
              />
            </div>
            <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1"></div>
            <button
              onClick={handleCurrentMonth}
              className="flex items-center gap-2 px-3 py-1 bg-primary/10 dark:bg-primary/20 text-primary text-sm font-semibold rounded-lg hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors"
            >
              <Calendar size={14} />
              Mês Atual
            </button>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm">
            <Download size={16} /> Exportar Relatório
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {canViewSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Card 1: Recebido (Dark) */}
          <div className="bg-gray-700 text-white p-6 rounded-xl shadow-lg relative overflow-hidden group">
            <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <ArrowUpRight size={80} />
            </div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">RECEBIDO NO MÊS</p>
            <h3 className="text-2xl font-montserrat font-bold">
              R$ {totalRecebido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </h3>
            <div className="mt-2 inline-flex items-center px-2 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded">
              +15% vs mês anterior
            </div>
          </div>

          {/* Card 2: A Receber */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col justify-center relative overflow-hidden">
            <div className="absolute right-4 top-4 p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-500 rounded-lg">
              <Clock size={20} />
            </div>
            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">A RECEBER (PENDENTE)</p>
            <h3 className="text-2xl font-montserrat font-bold text-gray-900 dark:text-white">
              R$ {totalPendente.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </h3>
          </div>

          {/* Card 3: Vencido */}
          {profile?.role !== 'analista' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col justify-center relative overflow-hidden">
              <div className="absolute right-4 top-4 p-2 bg-red-50 dark:bg-blue-900/20 text-red-500 rounded-lg">
                <AlertTriangle size={20} />
              </div>
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">VENCIDO (INADIMPLÊNCIA)</p>
              <h3 className="text-2xl font-montserrat font-bold text-red-600">
                R$ {totalVencido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </h3>
            </div>
          )}

          {/* Card 4: Emitidos */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col justify-center relative overflow-hidden">
            <div className="absolute right-4 top-4 p-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 rounded-lg">
              <FileText size={20} />
            </div>
            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">BOLETOS EMITIDOS</p>
            <h3 className="text-2xl font-montserrat font-bold text-gray-900 dark:text-white">
              {countEmitidos}
            </h3>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">

        {/* Tabs & Search */}
        <div className="border-b border-gray-100 dark:border-gray-700 p-4 pb-0">
          <div className="flex flex-col md:flex-row justify-between items-end gap-4">
            {/* Tabs */}
            <div className="flex gap-6 overflow-x-auto w-full md:w-auto text-sm font-medium">
              {['todos', 'pendentes', 'pagos', 'vencidos'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab as any)}
                  className={`pb-4 px-2 capitalize transition-colors relative ${activeTab === tab
                    ? 'text-primary'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                >
                  {tab}
                  {activeTab === tab && (
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-primary rounded-t-full"></div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Search Bar Row */}
        <div className="p-4 bg-gray-50/50 dark:bg-gray-900/20 border-b border-gray-100 dark:border-gray-700">
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar boleto, UC ou valor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:border-primary outline-none transition-shadow focus:shadow-sm placeholder:text-gray-400 dark:placeholder:text-gray-500"
            />
          </div>
          <div className="flex justify-end mt-2">
            <p className="text-xs text-gray-400">Mostrando {filteredBoletos.length} resultados</p>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 text-[10px] font-bold uppercase tracking-wider border-b border-gray-100 dark:border-gray-700">
                <th className="px-6 py-4 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('data_vencimento')}>
                  <div className="flex items-center gap-1">
                    Status / Vencimento
                    {sortConfig.key === 'data_vencimento' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                  </div>
                </th>
                <th className="px-6 py-4 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('cliente_nome')}>
                  <div className="flex items-center gap-1">
                    UC / Cliente
                    {sortConfig.key === 'cliente_nome' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                  </div>
                </th>
                <th className="px-6 py-4 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('data_emissao')}>
                  <div className="flex items-center gap-1">
                    Gerado em
                    {sortConfig.key === 'data_emissao' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                  </div>
                </th>
                <th className="px-6 py-4 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('mes_referencia')}>
                  <div className="flex items-center gap-1">
                    Mês Referência
                    {sortConfig.key === 'mes_referencia' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                  </div>
                </th>
                <th className="px-6 py-4 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('valor_total')}>
                  <div className="flex items-center gap-1">
                    Valor da Cobrança
                    {sortConfig.key === 'valor_total' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                  </div>
                </th>
                <th className="px-6 py-4 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('economia')}>
                  <div className="flex items-center gap-1">
                    Economia
                    {sortConfig.key === 'economia' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                  </div>
                </th>
                <th className="px-6 py-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {sortedBoletos.map(boleto => {
                let statusBadgeClass = '';
                let statusLabel = boleto.status?.toUpperCase() || 'PENDENTE';
                const status = (boleto.status || '').toLowerCase();

                if (status === 'pago') {
                  statusBadgeClass = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
                  statusLabel = 'PAGO';
                } else if (status === 'vencido') {
                  statusBadgeClass = 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
                  statusLabel = 'VENCIDO';
                } else {
                  statusBadgeClass = 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
                  statusLabel = 'ABERTO';
                }

                return (
                  <tr key={boleto.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group">
                    {/* Status / Vencimento */}
                    <td className="px-6 py-5 align-top">
                      <div className="flex flex-col items-start gap-1">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1 ${statusBadgeClass}`}>
                          {status === 'pago' && <CheckCircle2 size={10} />}
                          {status === 'vencido' && <AlertTriangle size={10} />}
                          {(status !== 'pago' && status !== 'vencido') && <Clock size={10} />}
                          {statusLabel}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                          Vence: {formatDate(boleto.data_vencimento)}
                        </span>
                      </div>
                    </td>

                    {/* UC / Cliente */}
                    <td className="px-6 py-5 align-top">
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold text-primary bg-primary/5 px-1.5 rounded w-fit mb-0.5">
                          {getDisplayUC(boleto.fatura?.uc_codigo || '')}
                        </span>
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          {boleto.fatura?.cliente_nome || boleto.cliente_nome || 'Desconhecido'}
                        </span>
                      </div>
                    </td>

                    {/* Gerado em */}
                    <td className="px-6 py-5 align-top">
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {formatDate(boleto.data_emissao)}
                      </span>
                    </td>

                    {/* Mês Referência */}
                    <td className="px-6 py-5 align-top">
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {boleto.mes_referencia || boleto.fatura?.mes_referencia || '-'}
                      </span>
                    </td>

                    {/* Valor da Cobrança */}
                    <td className="px-6 py-5 align-top">
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        R$ {Number(boleto.valor_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </td>

                    {/* Economia */}
                    <td className="px-6 py-5 align-top">
                      <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                        R$ {Number(boleto.economia || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </td>

                    {/* Ações */}
                    <td className="px-6 py-5 text-right align-top">
                      <div className="flex justify-end gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleGenerateBoleto(boleto)}
                          className="p-2 text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors border border-transparent hover:border-gray-200"
                          title="Gerar Boleto"
                        >
                          <Bolt size={16} />
                        </button>

                        {(boleto.pdf_boleto || boleto.codigo_barras_anexo) && (
                          <button
                            onClick={() => handleDownloadBoleto(boleto.pdf_boleto || boleto.codigo_barras_anexo)}
                            className="p-2 text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors border border-transparent hover:border-gray-200"
                            title="Visualizar Boleto"
                          >
                            <Eye size={16} />
                          </button>
                        )}

                        <button
                          onClick={(e) => toggleDropdown(e, boleto.id)}
                          className="p-2 text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors border border-transparent hover:border-gray-200"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {sortedBoletos.length === 0 && (
            <div className="p-12 text-center text-gray-400 dark:text-gray-500">
              <p>Nenhum boleto encontrado.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals (Dropdown, Delete, Success, Add) */}
      {/* Dropdown Portal */}
      {
        dropdownState && createPortal(
          <div
            className="fixed bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 z-50 w-48 overflow-hidden animate-fade-in text-left"
            style={{
              top: `${dropdownState.position.top}px`,
              left: `${dropdownState.position.left}px`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
              <span className="text-xs font-bold text-gray-500 uppercase">Ações Rápidas</span>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); handleEditBoleto(sortedBoletos.find(b => b.id === dropdownState.boletoId)); }}
              className="w-full px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <FileEdit size={14} /> Editar dados
            </button>
            <button className="w-full px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
              <Send size={14} /> Enviar ao cliente
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setBoletoToDelete(sortedBoletos.find(b => b.id === dropdownState.boletoId));
                setDeleteModalOpen(true);
                setDropdownState(null);
              }}
              className="w-full px-4 py-3 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 flex items-center gap-2 border-t border-gray-50 dark:border-gray-700"
            >
              <Trash2 size={14} /> Excluir boleto
            </button>
          </div>,
          document.body
        )
      }

      <BoletoFAB onAdd={handleAdd} />

      {
        showAddModal && (
          <BoletoModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            onSave={handleSave}
            boleto={editingBoleto}
          />
        )
      }

      {/* Delete Modal */}
      {
        deleteModalOpen && boletoToDelete && createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 m-4 text-center transform scale-100 transition-all">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Excluir Boleto?</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-8">
                Tem certeza que deseja excluir o boleto de <strong>{boletoToDelete.fatura?.cliente_nome || boletoToDelete.cliente_nome || 'Desconhecido'}</strong>? Esta ação não pode ser desfeita.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setDeleteModalOpen(false)}
                  className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteBoletoConfirm}
                  className="px-6 py-2.5 rounded-xl border-2 border-red-500 text-red-600 font-bold hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                >
                  Sim, excluir!
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Success Modal */}
      {
        successModalOpen && createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-8 m-4 text-center transform scale-100 transition-all">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Sucesso!</h3>
              <p className="text-gray-500 dark:text-gray-400">
                O boleto foi excluído corretamente.
              </p>
            </div>
          </div>,
          document.body
        )
      }
    </div>
  );
};

export default FinanceiroLayout;
